import Phaser from 'phaser';
import { NetworkService } from '../services/NetworkService';
import { Cell } from '../objects/Cell';
import { Logger as log } from '../utils/logger';
import { 
  PlayerMovedEvent, 
  PlayerJoinedEvent, 
  ResourceState, 
  OrganelleState,
  ResourceCounts,
  PlayerState,
  ResourceCollectedEvent,
  ToxinShotEvent,
  ToxinState,
  RoomStateEvent,
  FullSnapshotEvent,
  ServerModeEvent,
  CellDamagedEvent,
  PlayerRespawnedEvent,
  MembraneExpandedEvent,
  MembraneKeyframeEvent,
  OrganelleBuiltEvent,
  RoomInfo,
  TickUpdateEvent
} from '../types/multiplayer';
import { FpsMonitor } from '../utils/FpsMonitor';
import { GameUISystem } from '../systems/GameUISystem';
import { DOMUIManager } from '../ui/DOMUIManager';
import { NetworkMonitor } from '../utils/NetworkMonitor';
import { GameSettings } from '../utils/GameSettings';
import { Nucleus } from '../objects/Nucleus';
import { Organelle } from '../objects/Organelle';
import { Mitochondrion } from '../objects/Mitochondrion';
import { Lysosome } from '../objects/Lysosome';
import { MEMBRANE_EXPANSION_STAGES } from '../config/MembraneExpansionConfig';
import { getResourceCapacityForStage } from '../config/ResourceCapacityConfig';
import { SharedResources } from '../utils/SharedResources';
import { BootScene, URLParams } from '../scenes/BootScene';
import nipplejs from 'nipplejs';
import { InterpolationBuffer } from '../client/InterpolationBuffer';
import { InputPredictor } from '../client/InputPredictor';
import { TOXIN_ATP_COST } from '../shared/GameConstants';

// Define interface for camera controls
interface CameraControls {
  isDragging: boolean;
  lastPointerPosition: { x: number, y: number };
  minZoom: number;
  maxZoom: number;
  zoomFactor: number;
  targetZoom: number;
  currentZoom: number;
  zoomEase: number;
  zoomPoint: Phaser.Math.Vector2;
  panEase: number;
  targetPan: Phaser.Math.Vector2;
  currentPan: Phaser.Math.Vector2;
}

// Define interface for toxin projectile
interface ToxinProjectile extends Phaser.GameObjects.Sprite {
  // Add any specific properties or methods the toxin might have
  getData(key: string): any;
  setData(key: string, value: any): this;
}

export class MultiplayerGameScene extends Phaser.Scene {
  private networkService: NetworkService = NetworkService.getInstance();
  private roomId: string = '';
  
  // Configuration flags to track initialization progress
  private clientInitialized: boolean = false;
  private networkConnected: boolean = false;
  private stateReceived: boolean = false;
  private readyForInput: boolean = false;
  
  // Event queues for data that arrives before we're ready
  private pendingEvents: {
    roomState: RoomStateEvent | null;
    playerJoins: PlayerJoinedEvent[];
    playerMoves: PlayerMovedEvent[];
    resources: ResourceState[];
    toxins: ToxinShotEvent[];
    playerLeaves: string[];
    resourceCollected: ResourceCollectedEvent[];
    cellDamaged: CellDamagedEvent[];
    playerDied: string[];
    playerRespawned: PlayerRespawnedEvent[];
    organelleBuilt: OrganelleBuiltEvent[];
    membraneExpanded: MembraneExpandedEvent[];
  } = {
    roomState: null,
    playerJoins: [],
    playerMoves: [],
    resources: [],
    toxins: [],
    playerLeaves: [],
    resourceCollected: [],
    cellDamaged: [],
    playerDied: [],
    playerRespawned: [],
    organelleBuilt: [],
    membraneExpanded: []
  };
  
  // Players
  private playerCell!: Cell;
  private remoteCells: Map<string, Cell> = new Map();
  private remoteInterpolationBuffer = new InterpolationBuffer<PlayerMovedEvent>(120, 2000);
  private localInputPredictor = new InputPredictor();
  private receivedTickUpdates: boolean = false;
  private strictTickMode: boolean = false;
  private lastReceivedServerTick: number = 0;
  private lastSnapshotRequestAtMs: number = 0;
  private readonly snapshotRequestCooldownMs: number = 1500;
  private readonly tickGapRecoveryThreshold: number = 5;
  private playerControlsEnabled: boolean = true;
  private lastMembraneSyncTimeMs: number = 0;
  private readonly membraneSyncIntervalMs: number = 120;
  
  // Resources
  private resources: Map<string, Phaser.GameObjects.Sprite> = new Map();
  
  // Toxins
  private toxins: Map<string, Phaser.GameObjects.Sprite> = new Map();
  
  // Group for all game objects that should be ignored by the UI camera
  private gameObjectsGroup!: Phaser.GameObjects.Group;
  
  // Resource counts
  private lipids: number = 20;
  private glucose: number = 30;
  private atp: number = 20;
  
  // UI elements
  private gameUI!: GameUISystem;
  private domUI!: DOMUIManager;
  private organelleButtons: Phaser.GameObjects.Container[] = [];
  private fpsMonitor!: FpsMonitor;
  private networkMonitor!: NetworkMonitor;
  private settingsManager: GameSettings = GameSettings.getInstance();
  private lastFpsCheck: number | undefined;
  
  // Camera and world settings
  private cameraControls: CameraControls = {
    isDragging: false,
    lastPointerPosition: { x: 0, y: 0 },
    minZoom: 0.1,
    maxZoom: 5,
    zoomFactor: 0.1,
    targetZoom: 1,
    currentZoom: 1,
    zoomEase: 0.07,
    zoomPoint: new Phaser.Math.Vector2(0, 0),
    panEase: 0.1,
    targetPan: new Phaser.Math.Vector2(0, 0),
    currentPan: new Phaser.Math.Vector2(0, 0)
  };
  
  // World size
  private worldSize: number = 5000;
  
  // Gradient background
  private gradientBackground!: Phaser.GameObjects.Sprite;
  
  // Add properties to track debug state
  private playerCellCreated: boolean = false;
  private serverUpdatesEnabled: boolean = true;
  
  // Add a flag to track whether we're processing server updates
  private isProcessingUpdate: boolean = false;
  
  // Add these properties to the MultiplayerGameScene class
  private playerNameText!: Phaser.GameObjects.Text;
  private playerHealthBar!: Phaser.GameObjects.Graphics;
  private remoteNameTexts: Map<string, Phaser.GameObjects.Text> = new Map();
  private remoteHealthBars: Map<string, Phaser.GameObjects.Graphics> = new Map();
  
  // Add a property to store the player name
  private playerName: string = 'Player_' + Math.random().toString(36).substring(2, 5);
  
  // Add a flag to track building state
  private isBuilding: boolean = false;
  
  // Add resource production tracking properties
  private resourceProductionTimer: number = 0;
  private resourceProductionInterval: number = 1000; // Update resources every 1 second
  private resourceProductionServerUpdateInterval: number = 2000; // Send to server every 2 seconds (was 5000)
  private atpProducedSinceLastServerUpdate: number = 0;
  private glucoseConsumedSinceLastServerUpdate: number = 0;
  private lastServerResourceUpdate: number = 0;
  
  // Add a property to store last UI values
  private _lastUIValues: {
    health: number;
    lipids: number;
    glucose: number;
    atp: number;
    maxATP: number;
    membraneStage: number;
    lastUpdateTime?: number;
  } | null = null;
  
  // Add a property to track the last build request timestamp
  private _lastBuildRequestTime: number | null = null;
  
  // Add properties to track timing for debugging
  private _lastResourceProductionTime: number | null = null;
  
  // Portal properties
  private portal: {
    container: Phaser.GameObjects.Container;
    outerCircles: Phaser.GameObjects.Arc[];
    innerCircle: Phaser.GameObjects.Arc;
    position: { x: number, y: number };
    active: boolean;
    isApproaching?: boolean;
    approachEmitter?: Phaser.GameObjects.Particles.ParticleEmitter | null;
    isTransitioning?: boolean;
    inflowEmitter?: Phaser.GameObjects.Particles.ParticleEmitter | null;
    orbitCleanupFunction?: () => void;
    orbitEmitters?: Phaser.GameObjects.Particles.ParticleEmitter[];
    inflowTimerEvent?: Phaser.Time.TimerEvent;
    particlesPerBurst?: number;
    outerRadius?: number;
  } | null = null;
  private portalRadius: number = 130;
  private portalAnimationTimers: number[] = [];
  
  // Add a flag to control portal visibility
  private showPortal: boolean = false;
  
  // Add a property to store the last player position
  private _lastPlayerPosition: { x: number, y: number } | null = null;
  
  // Add a flag for transition states
  private isTransitioning: boolean = false;
  
  // Add properties for the ref portal
  private refPortal: {
    container: Phaser.GameObjects.Container;
    outerCircles: Phaser.GameObjects.Arc[];
    innerCircle: Phaser.GameObjects.Arc;
    position: { x: number, y: number };
    active: boolean;
    isApproaching?: boolean;
    approachEmitter?: Phaser.GameObjects.Particles.ParticleEmitter | null;
    isTransitioning?: boolean;
    inflowEmitter?: Phaser.GameObjects.Particles.ParticleEmitter | null;
    orbitCleanupFunction?: () => void;
    orbitEmitters?: Phaser.GameObjects.Particles.ParticleEmitter[];
    inflowTimerEvent?: Phaser.Time.TimerEvent;
    particlesPerBurst?: number;
    outerRadius?: number;
    text?: Phaser.GameObjects.Text;
    refUrl?: string;
  } | null = null;
  
  // Add a flag to control ref portal visibility
  private showRefPortal: boolean = false;
  
  // Add a property to store the ref URL
  private refUrl: string = '';
  
  // Add a property to track if welcome modal has been shown
  private welcomeModalShown: boolean = false;
  
  // Mobile Controls
  private joystickLeft: nipplejs.JoystickManager | null = null;
  private joystickRight: nipplejs.JoystickManager | null = null;
  private moveJoystickData: { angle: number, force: number, active: boolean } = { 
    angle: 0, 
    force: 0, 
    active: false 
  };
  private shootJoystickData: { angle: number, force: number, active: boolean } = { 
    angle: 0, 
    force: 0, 
    active: false 
  };
  private isMobileDevice: boolean = false;
  
  // For tracking updates
  private _currentUpdateFrame: number = 0;
  private _lastShootTime: number = 0;
  
  // New property to track vibeverse room joining
  // private joinVibeverse: boolean = false;
  private joinVibeverse: boolean = true;
  
  constructor() {
    super('MultiplayerGameScene');
    
    // Initialize camera controls
    this.cameraControls = {
      isDragging: false,
      lastPointerPosition: { x: 0, y: 0 },
      minZoom: 0.1,
      maxZoom: 5,
      zoomFactor: 0.1,
      targetZoom: 1,
      currentZoom: 1,
      zoomEase: 0.07,
      zoomPoint: new Phaser.Math.Vector2(0, 0),
      panEase: 0.1,
      targetPan: new Phaser.Math.Vector2(0, 0),
      currentPan: new Phaser.Math.Vector2(0, 0)
    };
    
    // Parse URL query parameters
    this.parseUrlParams();
  }
  
  /**
   * Parses URL query parameters to control game features
   */
  private parseUrlParams(): void {
    try {
      // Get URL parameters from BootScene
      const urlParams = BootScene.getUrlParams();
      
      // Check if portal should be shown
      // ! TEMPORARY
      // this.showPortal = urlParams.portal || false;
      this.showPortal = true;
      
      // Check if ref portal should be shown
      this.showRefPortal = urlParams.portal === true && urlParams.ref !== undefined;
      
      // Store the ref URL
      if (urlParams.ref) {
        this.refUrl = urlParams.ref;
      }
      
      // Get username if provided
      if (urlParams.username) {
        this.playerName = urlParams.username;
      }
      
      // If portal=true, we should join the vibeverse room directly
      // const portalParamPresent = urlParams.portal === true;
      const portalParamPresent = true;
      this.joinVibeverse = portalParamPresent;
      
      console.log(`URL params parsed: portal=${portalParamPresent}, refPortal=${this.showRefPortal}, ref=${this.refUrl}, username=${this.playerName}, joinVibeverse=${this.joinVibeverse}`);
    } catch (error) {
      console.error('Error parsing URL parameters:', error);
    }
  }
  
  init(data: any): void {
    log.info(() => `MultiplayerGameScene init called with data: ${JSON.stringify(data)}`);
    
    // Get the room ID from the scene data
    this.roomId = data.roomId || '';
    
    // Get the player name from the scene data or use default
    this.playerName = data.playerName || 'Player';
    
    // Re-parse URL parameters to ensure they're fresh
    this.parseUrlParams();
    
    log.info(() => `MultiplayerGameScene initialized with room ID: ${this.roomId}, player name: ${this.playerName}, joinVibeverse: ${this.joinVibeverse}`);
    
    // Reset flags
    this.playerCellCreated = false;
    this.serverUpdatesEnabled = true;
    this.isProcessingUpdate = false;
    
    // Reset collections
    this.remoteCells.clear();
    this.resources.clear();
    this.toxins.clear();
    
    // Reset resource counts - make sure all resources are explicitly initialized
    this.lipids = 20;
    this.glucose = 30;
    this.atp = 20; // Explicitly set initial ATP value
    
    // Ensure we're connected to the server
    if (!this.networkService.isConnected()) {
      log.info(() => 'Connecting to server from MultiplayerGameScene');
      this.networkService.connect();
    }
    
    // Set up network event listeners
    // Remove any existing listeners first to avoid duplicates
    this.setupNetworkListeners();
    
    log.info(() => 'MultiplayerGameScene initialization complete');
  }
  
  /**
   * Preload assets needed for the game
   * This method is called automatically by Phaser before create()
   * 
   * Note: This is kept as a safety fallback mechanism in case textures
   * weren't properly loaded in BootScene.
   */
  preload(): void {
    log.info(() => 'MultiplayerGameScene preload called');
    
    // This serves as a safety check - all textures should already be loaded by BootScene
    const sharedResources = SharedResources.getInstance();
    const glucoseLoaded = sharedResources.hasGlucoseTexture();
    
    if (!glucoseLoaded) {
      log.warn(() => 'FALLBACK: Glucose texture was not preloaded in BootScene, attempting emergency load');
      sharedResources.preloadGlucoseTexture(this);
      
      // Start loading immediately and set up the replacement mechanism for fallbacks
      this.load.start();
      this.setupGlucoseTextureListener();
    } else {
      log.info(() => '✓ All required textures are already loaded (good!)');
    }
  }
  
  create(): void {
    log.info(() => 'MultiplayerGameScene create called');

    // Final verification that textures are loaded
    const sharedResources = SharedResources.getInstance();
    if (!sharedResources.hasGlucoseTexture()) {
      console.warn('⚠️ Glucose texture still not loaded! Game will use fallback visuals');
    }
    
    console.log("Starting initialization sequence...");
    
    // Use sequential initialization with promises
    this.initializeClientEnvironment()
      .then(() => {
        // Step 2: Connect to the network
        return this.initializeNetworkConnection();
      })
      .then(() => {
        // Step 3: Register network event handlers
        this.registerNetworkEventHandlers();
        
        // Step 4: Join the room - this will trigger room state
        this.joinRoom();
        
        // Step 5: Set up input handlers (only activated after initialization)
        this.setupInputHandlers();
      })
      .catch(error => {
        console.error("Initialization failed:", error);
        // Handle the error appropriately
        this.gameUI = new GameUISystem(this, {
          containerDepth: 1000,
          buttonBgColor: 0x1a6dd7,
          progressBarColor: 0x00aaff
        });
        this.gameUI.showNotification(`Failed to initialize: ${error.message}`, 5000);
      });
  }
  
  /**
   * Create DOM UI elements
   */
  private createDOMUI(): void {
    // Create top-right container first
    this.domUI.createTopRightContainer();

    // Create navigation buttons (back and fullscreen) in the bottom left
    this.domUI.createNavigationButtons(() => {
      this.leaveGame();
    });
    
    // Create DOM-based organelle building menu in the top-right container
    this.createOrganelleButtons();
    
    // Get current membrane stage and max ATP
    const membraneStage = this.playerCell ? this.playerCell.getMembraneStage() : 1;
    const maxATP = this.playerCell ? this.playerCell.getMaxATP() : 100;
    
    // Create combined stats panel with health and resources
    this.domUI.createStatsPanel(
      this.playerCell.getHealth(),
      {
        lipids: this.lipids,
        glucose: this.glucose,
        atp: this.atp,
        maxATP: maxATP
      },
      membraneStage
    );
  }
  
  /**
   * Create the organelle buttons directly in the DOM
   */
  private createOrganelleButtons(): void {
    // Define the organelle options
    const organelles = [
      {
        type: 'mitochondrion',
        name: 'Mitochondria',
        description: 'Produces energy (ATP) for the cell',
        lipidCost: 20,
        glucoseCost: 30,
        icon: '🔋' // Energy symbol to represent mitochondria
      },
      {
        type: 'lysosome',
        name: 'Lysosome',
        description: 'Breaks down materials and defends the cell',
        lipidCost: 30,
        glucoseCost: 20,
        icon: '💥' // Explosion symbol to represent breakdown function
      }
    ];
    
    // Get current membrane stage
    const currentStage = this.playerCell ? this.playerCell.getMembraneStage() : 1;
    
    // Get next stage config if not at max stage
    let nextStageConfig;
    if (currentStage < 4) {
      nextStageConfig = MEMBRANE_EXPANSION_STAGES.find(s => s.stage === currentStage + 1);
    }
    
    // Log current resource values for debugging
    log.info(() => `Creating organelle menu with resources: ATP=${this.atp}, Glucose=${this.glucose}, Lipids=${this.lipids}`);
    
    // Create the menu in the top right container
    this.domUI.createOrganelleMenu(
      organelles,
      { lipids: this.lipids, glucose: this.glucose, atp: this.atp },
      (type: string) => {
        if (type === 'expand_membrane') {
          this.requestMembraneExpansion();
        } else {
          this.buildOrganelle(type);
        }
      },
      nextStageConfig ? {
        currentStage,
        nextStage: {
          stage: nextStageConfig.stage,
          lipidCost: nextStageConfig.lipidCost || 0,
          atpCost: nextStageConfig.atpCost || 0,
          requirements: nextStageConfig.requirements.map(req => {
            // Use the requirement's built-in description property
            return {
              id: req.id,
              description: req.description
            };
          })
        }
      } : { currentStage }
    );
  }
  
  /**
   * Update the DOM UI elements
   */
  private updateDOMUI(): void {
    if (!this.domUI || !this.playerCell) return;
    
    // Get the correct maximum ATP capacity from the player cell
    const maxATP = this.playerCell.getMaxATP();
    const health = this.playerCell.getHealth();
    const membraneStage = this.playerCell.getMembraneStage();
    
    // Round values for display but keep actual values for comparison
    const roundedATP = Math.round(this.atp);
    const roundedGlucose = Math.round(this.glucose);
    
    // Store last UI values to compare and prevent unnecessary updates
    if (!this._lastUIValues) {
      this._lastUIValues = {
        health: -1,
        lipids: -1,
        glucose: -1,
        atp: -1,
        maxATP: -1,
        membraneStage: -1
      };
    }
    
    // Check if any values have changed - use a small threshold for floating point values
    const THRESHOLD = 0.001;
    const hasChanged = 
      Math.abs(health - this._lastUIValues.health) > THRESHOLD ||
      Math.abs(this.lipids - this._lastUIValues.lipids) > THRESHOLD ||
      Math.abs(this.glucose - this._lastUIValues.glucose) > THRESHOLD ||
      Math.abs(this.atp - this._lastUIValues.atp) > THRESHOLD ||
      Math.abs(maxATP - this._lastUIValues.maxATP) > THRESHOLD ||
      membraneStage !== this._lastUIValues.membraneStage;
    
    // Only update the UI if values have changed
    if (hasChanged) {
      this.domUI.updateStatsPanel(
        health,
        {
          lipids: this.lipids,
          glucose: roundedGlucose,
          atp: roundedATP,
          maxATP: maxATP
        },
        membraneStage
      );
      
      // Update resource values in organelle menu
      this.domUI.updateResources({
        lipids: this.lipids,
        glucose: roundedGlucose,
        atp: roundedATP
      });
      
      // Store current values for next comparison
      this._lastUIValues.health = health;
      this._lastUIValues.lipids = this.lipids;
      this._lastUIValues.glucose = this.glucose; // Store actual value, not rounded
      this._lastUIValues.atp = this.atp; // Store actual value, not rounded
      this._lastUIValues.maxATP = maxATP;
      this._lastUIValues.membraneStage = membraneStage;
    }
  }
  
  /**
   * Updates the local resource counts and UI
   */
  private updateResourceCounts(resources: ResourceCounts, forceUpdate: boolean = false): void {
    // Update only if there's an actual change or force update is requested
    // Use a very small threshold to catch tiny regeneration values
    const smallChangeThreshold = 0.001; // Even smaller threshold to catch regeneration
    
    // Store whether we need to update based on our time interval
    const timeBasedUpdate = this._lastUIValues && 
      (this.time.now - (this._lastUIValues.lastUpdateTime || 0) > 200); // Force update every 200ms
    
    // Get current health and membrane stage
    const currentHealth = this.playerCell ? this.playerCell.getHealth() : 100;
    const currentMembraneStage = this.playerCell ? this.playerCell.getMembraneStage() : 1;
    
    if (forceUpdate || !this._lastUIValues || timeBasedUpdate ||
        Math.abs((this._lastUIValues.health || 0) - currentHealth) > smallChangeThreshold ||
        Math.abs((this._lastUIValues.lipids || 0) - (resources.lipids || this.lipids)) > smallChangeThreshold ||
        Math.abs((this._lastUIValues.glucose || 0) - (resources.glucose || this.glucose)) > smallChangeThreshold ||
        Math.abs((this._lastUIValues.atp || 0) - (resources.atp || this.atp)) > smallChangeThreshold ||
        Math.abs((this._lastUIValues.maxATP || 0) - (resources.maxATP || this.playerCell.getMaxATP())) > smallChangeThreshold ||
        (this._lastUIValues.membraneStage || 1) !== currentMembraneStage
    ) {
      // Always update these values when we detect a UI update is needed
      // Store exact values with full precision for calculations
      this._lastUIValues = {
        health: currentHealth,
        lipids: resources.lipids || this.lipids,
        glucose: resources.glucose || this.glucose,
        atp: resources.atp || this.atp,
        maxATP: resources.maxATP || this.playerCell.getMaxATP(),
        membraneStage: currentMembraneStage,
        lastUpdateTime: this.time.now // Track when we last updated
      };
      
      // Log the update if there's any change
      const atpChange = (resources.atp || this.atp) - (this._lastUIValues.atp || 0);
      if (Math.abs(atpChange) > 0.001) {
        log.info(() => `UI update: ATP changed by ${atpChange > 0 ? '+' : ''}${atpChange.toFixed(4)}, now ${(resources.atp || this.atp).toFixed(4)}/${resources.maxATP || this.playerCell.getMaxATP()}`);
      }
      
      // Update the DOM UI with resource values
      // Round only for display purposes, not for internal calculations
      this.domUI.updateResources({
        lipids: Math.round((resources.lipids || this.lipids) * 100) / 100,
        glucose: Math.round((resources.glucose || this.glucose) * 100) / 100,
        atp: Math.round((resources.atp || this.atp) * 100) / 100
      });
    }
  }
  
  update(time: number, delta: number): void {
    // Debug check for freeze
    this._currentUpdateFrame = (this._currentUpdateFrame || 0) + 1;
    
    // Check for pause first
    if (!this.serverUpdatesEnabled || this.isTransitioning) {
      return;
    }
    
    // Skip updates if not fully initialized
    if (!this.readyForInput) {
      // Only minimal updating while not fully initialized
      if (this.clientInitialized) {
        // We can still update camera and UI
        this.updateCamera(delta);
        
        // Update portals
        if (this.portal) this.updatePortal(delta);
        if (this.refPortal) this.updateRefPortal(delta);
        
        // Keep legacy UI hidden at all times
        this.keepLegacyUIHidden();
      }
      return;
    }
    
    // Only process if player cell exists
    if (!this.playerCell) {
      return;
    }

    // Process joystick controls for mobile
    this.handleJoystickMovement(delta);
    const correctedLocalPosition = this.localInputPredictor.apply({
      x: this.playerCell.x,
      y: this.playerCell.y
    });
    if (
      correctedLocalPosition.x !== this.playerCell.x ||
      correctedLocalPosition.y !== this.playerCell.y
    ) {
      this.playerCell.setPosition(correctedLocalPosition.x, correctedLocalPosition.y);
    }
    
    // Right joystick toxin shooting with proper throttling
    // Lower the threshold significantly to make it easier to shoot
    if (this.shootJoystickData.active && this.shootJoystickData.force > 0.15) {
      // Add a cooldown to prevent shooting too frequently
      const currentTime = time;
      if (currentTime - this._lastShootTime > 250) { // Slightly shorter cooldown (250ms)
        console.log(`Attempting to shoot toxin with joystick. Force: ${this.shootJoystickData.force.toFixed(2)}, Angle: ${this.shootJoystickData.angle.toFixed(2)}`);
        this.shootToxinWithJoystick();
        this._lastShootTime = currentTime;
      }
    }
    
    // Update resource production
    this.updateResourceProduction(delta, time);
    
    // Update and display FPS if enabled
    if (this.settingsManager.getSettings().showFps) {
      if (time - (this.lastFpsCheck || 0) > 1000) {
        this.lastFpsCheck = time;
        this.fpsMonitor.update(time);
      }
    }
    
    // Skip updates if paused
    if (this.physics.world.isPaused) {
      return;
    }
    
    // Update portals
    this.updatePortal(delta);
    if (this.refPortal) {
      this.updateRefPortal(delta);
    }
    
    // Check for player collision with portal
    if (this.portal && this.playerCell && this.playerCellCreated && this.showPortal) {
      const portalPos = this.portal.position;
      const distance = Phaser.Math.Distance.Between(
        this.playerCell.x, this.playerCell.y,
        portalPos.x, portalPos.y
      );
      
      // Start showing particles when approaching the portal - use a much bigger radius
      const approachThreshold = this.portalRadius * 3; // Increased from 1.5x to 3x
      const collisionThreshold = this.portalRadius * 0.7;
      
      if (distance < approachThreshold && this.portal.active) {
        // When approaching the portal, start minor particle effects
        if (!this.portal.hasOwnProperty('isApproaching') || !this.portal.isApproaching) {
          this.createPortalApproachEffect();
          this.portal.isApproaching = true;
        }
        
        // Update particle effects based on proximity to portal
        this.updateParticleEffects(distance, approachThreshold);
      } else if (distance >= approachThreshold && this.portal.hasOwnProperty('isApproaching') && this.portal.isApproaching) {
        // Reset approach state when moving away
        this.portal.isApproaching = false;
        // Clear any approach effects if needed
        if (this.portal.approachEmitter) {
          this.portal.approachEmitter.stop();
          this.portal.approachEmitter = null;
        }
        // Also clean up inflow emitter
        if (this.portal.inflowEmitter) {
          this.portal.inflowEmitter.stop();
          this.portal.inflowEmitter = null;
        }
        // Clean up all orbit emitters
        if (this.portal.orbitEmitters && this.portal.orbitEmitters.length > 0) {
          for (const emitter of this.portal.orbitEmitters) {
            emitter.stop();
            emitter.destroy();
          }
          this.portal.orbitEmitters = [];
        }
        // Call the orbit cleanup function if it exists
        if (this.portal.orbitCleanupFunction) {
          this.portal.orbitCleanupFunction();
          this.portal.orbitCleanupFunction = undefined;
        }
      }
      
      // If player is close enough to the portal, trigger the full portal effect
      if (distance < collisionThreshold && this.portal.active && !this.portal.hasOwnProperty('isTransitioning')) {
        this.handlePortalCollision();
      } else if (distance > approachThreshold && this.portal.hasOwnProperty('isTransitioning')) {
        // Reset portal state when player has moved far enough away after a transition
        delete this.portal.isTransitioning;
        this.portal.active = true;
        this.playerControlsEnabled = true;
      }
    }
    
    // Update portal
    this.updatePortal(delta);
    
    // Check for player collision with ref portal
    if (this.refPortal && this.playerCell && this.playerCellCreated && this.showRefPortal) {
      const portalPos = this.refPortal.position;
      const distance = Phaser.Math.Distance.Between(
        this.playerCell.x, this.playerCell.y,
        portalPos.x, portalPos.y
      );
      
      // Start showing particles when approaching the portal - use a much bigger radius
      const approachThreshold = this.portalRadius * 3; // Increased from 1.5x to 3x
      const collisionThreshold = this.portalRadius * 0.7;
      
      if (distance < approachThreshold && this.refPortal.active) {
        // When approaching the portal, start minor particle effects
        if (!this.refPortal.hasOwnProperty('isApproaching') || !this.refPortal.isApproaching) {
          this.createRefPortalApproachEffect();
          this.refPortal.isApproaching = true;
        }
        
        // Update particle effects based on proximity to portal
        this.updateRefParticleEffects(distance, approachThreshold);
      } else if (distance >= approachThreshold && this.refPortal.hasOwnProperty('isApproaching') && this.refPortal.isApproaching) {
        // Reset approach state when moving away
        this.refPortal.isApproaching = false;
        // Clean up approach effects
        if (this.refPortal.approachEmitter) {
          this.refPortal.approachEmitter.stop();
          this.refPortal.approachEmitter = null;
        }
        // Also clean up inflow emitter
        if (this.refPortal.inflowEmitter) {
          this.refPortal.inflowEmitter.stop();
          this.refPortal.inflowEmitter = null;
        }
        // Clean up all orbit emitters
        if (this.refPortal.orbitEmitters && this.refPortal.orbitEmitters.length > 0) {
          for (const emitter of this.refPortal.orbitEmitters) {
            emitter.stop();
            emitter.destroy();
          }
          this.refPortal.orbitEmitters = [];
        }
        // Call the orbit cleanup function if it exists
        if (this.refPortal.orbitCleanupFunction) {
          this.refPortal.orbitCleanupFunction();
          this.refPortal.orbitCleanupFunction = undefined;
        }
      }
      
      // If player is close enough to the ref portal, trigger the full portal effect
      if (distance < collisionThreshold && this.refPortal.active && !this.refPortal.hasOwnProperty('isTransitioning')) {
        this.handleRefPortalCollision();
      } else if (distance > approachThreshold && this.refPortal.hasOwnProperty('isTransitioning')) {
        // Reset portal state when player has moved far enough away after a transition
        delete this.refPortal.isTransitioning;
        this.refPortal.active = true;
        this.playerControlsEnabled = true;
      }
    }
    
    // Update camera position/zoom
    this.updateCamera(delta);
    
    // Skip update if player cell is not created yet
    if (!this.playerCellCreated || !this.playerCell) return;
    
    // Get the correct maximum ATP capacity from the player cell
    const maxATP = this.playerCell.getMaxATP();
    
    // Get the current cell values to see if we need updates
    const currentCellGlucose = this.playerCell.getGlucose();
    const currentCellATP = this.playerCell.getATP();
    const currentCellHealth = this.playerCell.getHealth();
    
    // Check if any values have changed significantly enough to update the UI
    // Use smaller threshold (0.05) to reduce visible jumps in the bars
    let valuesChanged = false;
    
    if (!this._lastUIValues) {
      this._lastUIValues = {
        health: currentCellHealth, 
        lipids: this.lipids,
        glucose: currentCellGlucose,
        atp: currentCellATP,
        maxATP: maxATP,
        membraneStage: this.playerCell.getMembraneStage()
      };
      valuesChanged = true;
    } else {
      // Forced update every 200ms ensures smooth animations for small continuous changes
      const forceUiUpdate = (time % 200) < 20; // Update roughly every 200ms
      
      // Only update if values changed enough to be visible or on a timer for smoother animations
      valuesChanged = 
        Math.abs(this._lastUIValues.health - currentCellHealth) >= 0.5 ||
        Math.abs(this._lastUIValues.glucose - currentCellGlucose) >= 0.05 ||
        Math.abs(this._lastUIValues.atp - currentCellATP) >= 0.05 ||
        this._lastUIValues.maxATP !== maxATP ||
        forceUiUpdate;
      
      // Update the stored values whether we update the UI or not
      // This prevents accumulation of small changes that would eventually cause a jump
      this._lastUIValues.health = currentCellHealth;
      this._lastUIValues.glucose = currentCellGlucose; 
      this._lastUIValues.atp = currentCellATP;
      this._lastUIValues.maxATP = maxATP;
    }
    
    // Track significant resource changes, especially after building
    // This helps identify patterns in consumption
    if (valuesChanged && this._lastBuildRequestTime) {
      const timeSinceBuild = Date.now() - this._lastBuildRequestTime;
      
      // Only log if within 10 seconds of a build action
      if (timeSinceBuild < 10000) {
        // Calculate rate of change
        const glucoseChangeRate = (this.glucose - currentCellGlucose) / (delta / 1000);
        const atpChangeRate = (this.atp - currentCellATP) / (delta / 1000);
        
        log.info(() => `Resource change ${timeSinceBuild}ms after build: 
          Glucose: ${this.glucose.toFixed(2)} → ${currentCellGlucose.toFixed(2)} (${glucoseChangeRate.toFixed(2)}/s)
          ATP: ${this.atp.toFixed(2)} → ${currentCellATP.toFixed(2)} (${atpChangeRate.toFixed(2)}/s)`);
      }
    }
    
    // Only update the UI if values have changed
    if (valuesChanged) {
      // Ensure our scene values are synchronized with the cell's actual values
      this.glucose = currentCellGlucose;
      this.atp = currentCellATP;
      
      // Update resource counts in UI
      this.gameUI.updateUI({
        'health': {
          value: currentCellHealth,
          text: `Health: ${currentCellHealth}%`
        },
        'atp': {
          value: (this.atp / maxATP) * 100,
          // Round only for display purposes, not for calculations
          text: `ATP: ${Math.round(this.atp)}/${maxATP}`
        },
        'glucose': `Glucose: ${Math.round(this.glucose)}`
      });
      
      // Update DOM UI to ensure it's synchronized with the game UI
      if (this.domUI) {
        this.domUI.updateStatsPanel(
          currentCellHealth,
          {
            lipids: this.lipids,
            // Round only for display, keep full precision in actual values
            glucose: Math.round(this.glucose),
            atp: Math.round(this.atp),
            maxATP: maxATP
          },
          this.playerCell.getMembraneStage()
        );
      }
    }
    
    // Make sure the player cell is updated with current resource values
    this.playerCell.update(delta, {
      lipids: this.lipids,
      glucose: this.glucose,
      atp: this.atp
    });
    
    // Update resource production
    this.updateResourceProduction(delta, time);
    
    // Only send position updates if player controls are enabled and player is moving
    if (this.playerControlsEnabled) {
      // Store position before update to detect movement in next frame
      this._lastPlayerPosition = this._lastPlayerPosition || { x: this.playerCell.x, y: this.playerCell.y };
      
      const isMoving = 
        this._lastPlayerPosition.x !== this.playerCell.x || 
        this._lastPlayerPosition.y !== this.playerCell.y;
                   
      // Update stored position for next frame
      this._lastPlayerPosition.x = this.playerCell.x;
      this._lastPlayerPosition.y = this.playerCell.y;
      
      if (isMoving) {
        this.sendPositionUpdate();
        this.sendMembraneUpdate();
      }
    }
    
    // Apply buffered server snapshots before per-cell updates for smooth movement
    this.applyRemoteInterpolation();

    // Update remote cells
    this.updateRemoteCells(delta);
    
    // Update camera (smoothing, boundary checks)
    this.updateCamera(delta);
    
    // Update toxins
    this.updateToxins(delta);
    
    // Check FPS every second for adaptive detail changes
    if (this.lastFpsCheck === undefined || time - this.lastFpsCheck > 1000) {
      this.lastFpsCheck = time;
      this.adaptDetailBasedOnPerformance();
    }
    
    // Keep legacy UI hidden at all times (it gets re-enabled sometimes)
    this.keepLegacyUIHidden();
  }
  
  /**
   * Updates resource production from mitochondria and handles ATP consumption/regeneration
   */
  private updateResourceProduction(delta: number, time: number): void {
    // Get production and consumption from the cell
    const production = this.playerCell.getAndResetResourceProduction();
    
    // Always force an update if it's been too long since the last one
    // This ensures server sync even when not moving
    const timeSinceLastUpdate = time - this.lastServerResourceUpdate;
    const forceUpdate = (timeSinceLastUpdate >= this.resourceProductionServerUpdateInterval * 3);
    
    // Log periodic status even if no changes
    if (Math.random() < 0.01) { // 1% chance to log
      log.info(() => `Resource status check - ATP: ${this.atp.toFixed(1)}/${this.playerCell.getMaxATP()}, production.needsUpdate: ${production.needsUpdate}, timeSinceLastUpdate: ${timeSinceLastUpdate.toFixed(0)}ms`);
    }
    
    if (production.needsUpdate || forceUpdate) {
      // Calculate new values - note production.atp can be negative due to movement
      const atpChange = production.atp;
      const glucoseChange = -production.glucose; // Negative because glucose is consumed
      
      const newATP = Math.min(Math.max(0, this.atp + atpChange), this.playerCell.getMaxATP());
      const newGlucose = Math.max(0, this.glucose + glucoseChange);
      
      // Check if there are actual changes or if we're forcing an update
      if (Math.abs(newATP - this.atp) > 0.01 || Math.abs(newGlucose - this.glucose) > 0.01 || forceUpdate) {
        // Track the last production time for debugging
        this._lastResourceProductionTime = Date.now();
        
        // Keep track of post-build production
        if (this._lastBuildRequestTime) {
          const timeSinceBuild = Date.now() - this._lastBuildRequestTime;
          if (timeSinceBuild < 5000) { // Within 5 seconds of building
            log.info(() => `Resource production ${timeSinceBuild}ms after build: ${atpChange > 0 ? '+' : ''}${atpChange.toFixed(2)} ATP, ${glucoseChange > 0 ? '+' : ''}${glucoseChange.toFixed(2)} glucose`);
          }
        }
        
        // Update local resource counts
        this.atp = newATP;
        this.glucose = newGlucose;
        
        // Ensure the cell has the updated values
        this.playerCell.setATP(this.atp);
        this.playerCell.setGlucose(this.glucose);
        
        // Accumulate for server update - note that atpChange can be negative due to consumption
        this.atpProducedSinceLastServerUpdate += atpChange;
        this.glucoseConsumedSinceLastServerUpdate += -glucoseChange; // Negative because we're tracking consumption
        
        // Log resource changes for debugging
        log.info(() => `Resource update: ATP ${atpChange > 0 ? '+' : ''}${atpChange.toFixed(2)} (now ${this.atp.toFixed(2)}), Glucose ${glucoseChange > 0 ? '+' : ''}${glucoseChange.toFixed(2)} (now ${this.glucose.toFixed(2)})`);
        
        // If forcing an update, send immediately to server
        if (forceUpdate) {
          console.log('Forcing resource update to server after inactivity');
          this.sendResourceProductionUpdate();
          this.lastServerResourceUpdate = time;
        }
      }
    }
    
    // Send server updates at regular intervals regardless of whether there were changes
    // This ensures natural regeneration works even when not moving
    if (time - this.lastServerResourceUpdate >= this.resourceProductionServerUpdateInterval) {
      // Always send resource updates on a timer, even if accumulated values are 0
      // This ensures the server keeps regenerating ATP even when the player is inactive
      this.sendResourceProductionUpdate(true);
      this.lastServerResourceUpdate = time;
    }
  }
  
  /**
   * Sends resource production updates to the server using the dedicated updatePlayerResources event
   */
  private sendResourceProductionUpdate(forceUpdate: boolean = false): void {
    // Get detailed production info with source breakdowns
    const productionDetails = this.playerCell.getResourceProductionDetails();
    
    // Send if we have changes to report or if forced
    if (productionDetails.needsUpdate || forceUpdate) {
      if (productionDetails.total.atp !== 0 || productionDetails.total.glucose !== 0) {
        log.info(() => `Sending resource update to server: ATP ${productionDetails.total.atp > 0 ? '+' : ''}${productionDetails.total.atp.toFixed(4)} (natural: ${productionDetails.sources.natural.atp.toFixed(4)}, mitochondria: ${productionDetails.sources.mitochondria.atp.toFixed(4)}), Glucose: ${productionDetails.total.glucose > 0 ? '+' : ''}${productionDetails.total.glucose.toFixed(4)}`);
      } else if (forceUpdate) {
        log.info(() => `Sending regular heartbeat update to server (current ATP: ${this.atp.toFixed(4)}/${this.playerCell.getMaxATP()})`);
      }
      
      // Send precise resource data using the new dedicated event
      this.networkService.updatePlayerResources(
        {
          atp: this.atp,
          glucose: this.glucose,
          lipids: this.lipids
        },
        {
          atpProduced: productionDetails.total.atp,
          glucoseConsumed: productionDetails.total.glucose,
          sources: productionDetails.sources
        }
      );
      
      // Reset accumulators by calling the original method
      this.playerCell.getAndResetResourceProduction();
      
      // Also reset the scene-level accumulators
      this.atpProducedSinceLastServerUpdate = 0;
      this.glucoseConsumedSinceLastServerUpdate = 0;
    }
  }
  
  /**
   * Ensure legacy GameUISystem elements stay hidden
   * This is needed because sometimes they can reappear after updates
   */
  private keepLegacyUIHidden(): void {
    // Hide progress bars from GameUISystem
    const healthBar = this.gameUI.getProgressBar('health');
    if (healthBar && healthBar.container) {
      healthBar.container.visible = false;
    }
    
    // Hide resource text elements
    const lipidText = this.gameUI.getContainer().getByName('lipids');
    if (lipidText) {
      (lipidText as Phaser.GameObjects.Text).visible = false;
    }
    
    const glucoseText = this.gameUI.getContainer().getByName('glucose');
    if (glucoseText) {
      (glucoseText as Phaser.GameObjects.Text).visible = false;
    }
    
    // Check for any other progress bars in the UI container and hide them
    this.gameUI.getContainer().list.forEach((item: Phaser.GameObjects.GameObject) => {
      if (item instanceof Phaser.GameObjects.Container) {
        if (item.list.some(child => child instanceof Phaser.GameObjects.Rectangle)) {
          item.visible = false;
        }
      }
    });
  }
  
  private setupNetworkListeners(): void {
    // Room state (initial state when joining)
    this.networkService.onRoomState((data: RoomStateEvent) => {
      console.log('Received room state:', data);
      console.log('Players in room state:', data.players.map(p => `${p.name} (${p.id})`));
      
      if (!this.serverUpdatesEnabled) {
        console.log('Server updates disabled, ignoring room state event');
        return;
      }
      
      // Set processing flag to prevent concurrent updates
      if (this.isProcessingUpdate) {
        console.log('Already processing an update, deferring room state');
        return;
      }
      
      this.isProcessingUpdate = true;
      
      try {
        // Clear existing resources
        this.resources.forEach(resource => resource.destroy());
        this.resources.clear();
        
        // If we don't have a player cell yet, we need to create one
        if (!this.playerCellCreated) {
          console.log('Player cell not created yet, looking for our player data in room state');
          
          // Find our player data in the room state
          const ourPlayer = data.players.find(p => p.id === this.networkService.getSocketId());
          
          if (ourPlayer) {
            console.log('Found our player data, creating player cell');
            // Create the player cell if it doesn't exist
            this.createPlayerCell(ourPlayer.x, ourPlayer.y);
            
            // Update resources
            this.updateResourceCounts(ourPlayer.resources);
          } else {
            console.warn('Could not find our player data in room state');
          }
        }
        
        // Clear existing remote cells but don't recreate them for our player
        // This is important because we don't want duplicate cells
        const filteredPlayers: PlayerState[] = [];
        
        data.players.forEach(playerState => {
          // Keep track of which player states we'll process
          if (playerState.id !== this.networkService.getSocketId()) {
            filteredPlayers.push(playerState);
          }
          
          // If we find our local cell, we should destroy the remote version
          if (playerState.id === this.networkService.getSocketId()) {
            console.log(`Found local player in room state, updating resources only`);
            // We only update resources for our local player
            this.updateResourceCounts(playerState.resources);
          }
        });
        
        // Now destroy all existing remote cells
        this.remoteCells.forEach(cell => cell.destroy());
        this.remoteCells.clear();
        
        // Log the number of players for debugging
        log.debug(() => `Processing ${filteredPlayers.length} remote players in room state (filtered out local player)`);
        
        // Process all REMOTE players only
        filteredPlayers.forEach(playerState => {
          log.debug(() => `Creating remote cell for player ${playerState.id} from room state`);
          this.addRemoteCell(playerState);
        });
        
        // Add all resources
        data.resources.forEach(resourceState => {
          this.addResource(resourceState);
        });
        
        // Create debug visuals if needed
        if (this.settingsManager.getSettings().showFps) {
          this.createDebugVisuals();
        }
        
        // Show welcome modal after processing the initial room state
        // But make sure the DOM UI is initialized first
        this.time.delayedCall(1000, () => {
          if (this.domUI) {
            console.log('🔍 DOM UI is initialized, showing welcome modal...');
            this.showWelcomeModal();
          } else {
            console.warn('⚠️ DOM UI not initialized yet, cannot show welcome modal');
            
            // Try again after a slightly longer delay
            this.time.delayedCall(1000, () => {
              if (this.domUI) {
                console.log('🔍 DOM UI is now initialized, showing welcome modal (2nd attempt)...');
                this.showWelcomeModal();
              } else {
                console.error('❌ DOM UI still not initialized after 2 seconds, giving up on welcome modal');
              }
            });
          }
        });
      } finally {
        // Always clear the processing flag
        this.isProcessingUpdate = false;
      }
    });

    this.networkService.onFullSnapshot((data: FullSnapshotEvent) => {
      if (!this.serverUpdatesEnabled) {
        return;
      }
      this.processFullSnapshot(data);
    });

    this.networkService.onServerMode((data: ServerModeEvent) => {
      this.strictTickMode = data.strictTickMode;
    });
    
    // Player joined
    this.networkService.onPlayerJoined((data: PlayerJoinedEvent) => {
      console.log('Player joined:', data);
      
      if (!this.serverUpdatesEnabled) {
        console.log('Server updates disabled, ignoring player joined event');
        return;
      }
      
      // Set processing flag to prevent concurrent updates
      if (this.isProcessingUpdate) {
        console.log('Already processing an update, deferring player joined');
        // Schedule this to run after current processing is complete
        this.time.delayedCall(100, () => {
          this.handlePlayerJoined(data);
        });
        return;
      }
      
      this.handlePlayerJoined(data);
    });
    
    // Player moved
    this.networkService.onPlayerMoved((data: PlayerMovedEvent) => {
      if (!this.serverUpdatesEnabled) {
        return;
      }

      // If we already get tick updates, use them as the single source for movement.
      if (this.hasTickMovementEnabled()) {
        return;
      }

      this.queueRemoteMovement(data);
    });

    this.networkService.onTickUpdate((data: TickUpdateEvent) => {
      if (!this.serverUpdatesEnabled) {
        return;
      }
      this.handleTickUpdate(data);
    });
    
    // Player left
    // this.networkService.onPlayerLeft((playerId: string) => {
    //   log.info(() => `Player ${playerId} left the game`);
      
    //   // Get the remote cell
    //   const remoteCell = this.remoteCells.get(playerId);
      
    //   if (remoteCell) {
    //     // Destroy the cell
    //     remoteCell.destroy();
        
    //     // Remove from our map
    //     this.remoteCells.delete(playerId);
        
    //     log.debug(() => `Removed remote cell for player ${playerId}, remaining cells: ${this.remoteCells.size}`);
    //   } else {
    //     log.warn(() => `Received player left event for player ${playerId} but no remote cell found`);
    //   }
    // });
    
    // Resource spawned
    this.networkService.onResourceSpawned((data: ResourceState) => {
      log.info(() => `${data.ejected ? 'Ejected' : 'Normal'} resource spawned`);
      this.addResource(data);
    });

    
    // Resource collected
    this.networkService.onResourceCollected((data: ResourceCollectedEvent) => {
      const resource = this.resources.get(data.id);
      if (resource) {
        // Clean up any attached effects
        const effects = resource.getData('effects') as Phaser.GameObjects.GameObject[];
        if (effects && Array.isArray(effects)) {
          effects.forEach(effect => {
            if (effect && effect.active) {
              // Fade out all effects quickly
              this.tweens.add({
                targets: effect,
                alpha: 0,
                duration: 150,
                onComplete: () => {
                  if (effect.active) {
                    effect.destroy();
                  }
                }
              });
            }
          });
        }
        
        // Play collection animation
        this.tweens.add({
          targets: resource,
          alpha: 0,
          scale: 0.1,
          duration: 200,
          onComplete: () => {
            resource.destroy();
            this.resources.delete(data.id);
          }
        });
        
        // If we collected it, update our resource counts
        if (data.playerId === this.networkService.getSocketId()) {
          const resourceType = resource.getData('type') as 'lipid' | 'glucose';
          const value = resource.getData('value') as number;
          
         // Get capacity based on membrane stage
         const membraneStage = this.playerCell ? this.playerCell.getMembraneStage() : 1;
         const capacity = getResourceCapacityForStage(membraneStage);

          // One-time update of resources based on collected type
          if (resourceType === 'lipid') {
            this.lipids = Math.min(this.lipids + value, capacity.maxLipids);
          } else {
            // Just add glucose to the fixed amount - mitochondria will handle ATP production
            this.glucose = Math.min(this.glucose + value, capacity.maxGlucose);
            // Ensure we have a clean float value
            this.glucose = Math.round(this.glucose * 100) / 100;
            // Directly sync with the player cell to ensure consistent values
            this.playerCell.setGlucose(this.glucose);
          }
          
          // Get the correct maximum ATP capacity from the player cell
          const maxATP = this.playerCell ? this.playerCell.getMaxATP() : capacity.maxATP;

          // Update all UI components with new values
          this.gameUI.updateUI({
            'lipids': `Lipids: ${this.lipids}`,
            'glucose': `Glucose: ${Math.round(this.glucose)}`,
            'atp': `ATP: ${Math.round(this.atp)}/${maxATP}`
          });
          
          // Update DOM UI
          if (this.domUI) {
            this.domUI.updateStatsPanel(
              this.playerCell.getHealth(),
              { 
                lipids: this.lipids, 
                glucose: Math.round(this.glucose), 
                atp: Math.round(this.atp),
                maxATP: maxATP
              },
              membraneStage
            );
            
            // Also update organelle menu resources
            this.domUI.updateResources({
              lipids: this.lipids,
              glucose: Math.round(this.glucose),
              atp: Math.round(this.atp)
            });
          }
        }
      }
    });
    
    // Toxin shot
    this.networkService.onToxinShot((data: ToxinShotEvent) => {
      this.createToxin(data);
    });
    
    // Cell damaged
    this.networkService.onCellDamaged((data: CellDamagedEvent) => {
      if (data.playerId === this.networkService.getSocketId()) {
        // Local player damaged
        this.playerCell.setHealth(data.health);
        this.showDamageEffect(this.playerCell, data.position.angle, data.amount);
        
        // Update health UI using component-based approach
        this.gameUI.updateUI({
          'health': {
            value: data.health,
            text: `Health: ${data.health}%`
          }
        });
        
        // Update DOM UI health bar in the top-left stats panel
        if (this.domUI) {
          // Get current resource values from _lastUIValues or use current values
          const resources = this._lastUIValues ? {
            lipids: this._lastUIValues.lipids,
            glucose: Math.round(this._lastUIValues.glucose),
            atp: Math.round(this._lastUIValues.atp),
            maxATP: this._lastUIValues.maxATP
          } : {
            lipids: this.lipids,
            glucose: Math.round(this.glucose),
            atp: Math.round(this.atp),
            maxATP: this.playerCell.getMaxATP()
          };
          
          // Update DOM UI with new health value
          this.domUI.updateStatsPanel(
            data.health,
            resources,
            this._lastUIValues?.membraneStage || 1
          );
          
          // Update stored health value for future comparisons
          if (this._lastUIValues) {
            this._lastUIValues.health = data.health;
          }
        }
      } else {
        // Remote player damaged
        const cell = this.remoteCells.get(data.playerId);
        if (cell) {
          (cell as any).health = data.health;
          this.showDamageEffect(cell, data.position.angle, data.amount);
        }
      }
      
      // Update the health bar
      if (data.playerId === this.networkService.getSocketId()) {
        // Update player's own health bar
        if (this.playerHealthBar) {
          this.updateHealthBar(this.playerHealthBar, data.health);
        }
      } else {
        // Update remote player's health bar
        const healthBar = this.remoteHealthBars.get(data.playerId);
        if (healthBar) {
          this.updateHealthBar(healthBar, data.health);
        }
      }
    });
    
    // Player died
    this.networkService.onPlayerDied((playerId: string) => {
      console.log(`Player ${playerId} died`);
      
      if (playerId === this.networkService.getSocketId()) {
        // Local player died
        console.log('You died! Waiting for respawn...');
        
        // Create death effect immediately when player dies
        this.createDeathEffect(this.playerCell);
        
        // Hide player cell (but don't destroy it)
        this.playerCell.setVisible(false);
        
        // Disable player controls
        this.playerControlsEnabled = false;
        
        // Show death message in UI
        this.gameUI.showNotification('You died! Respawning in 8 seconds...', 8000);
      } else {
        // Remote player died
        const remoteCell = this.remoteCells.get(playerId);
        if (remoteCell) {
          // Create death effect immediately when remote player dies
          this.createDeathEffect(remoteCell);
          
          // Hide remote cell (but don't destroy it)
          remoteCell.setVisible(false);
          
          // Hide name and health bar
          const nameText = this.remoteNameTexts.get(playerId);
          const healthBar = this.remoteHealthBars.get(playerId);
          
          if (nameText) nameText.setVisible(false);
          if (healthBar) healthBar.setVisible(false);
        }
      }
    });
    
    // Player respawned
    this.networkService.onPlayerRespawned((data: PlayerRespawnedEvent) => {
      console.log(`Player ${data.playerId} respawned at (${data.x}, ${data.y})`);
      
      if (data.playerId === this.networkService.getSocketId()) {
        // Local player respawned
        console.log('You have respawned!');
        
        // Update position
        this.playerCell.setPosition(data.x, data.y);
        this.playerCell.setVisible(true);
        this.playerCell.setHealth(data.health);
        
        // Re-enable player controls
        this.playerControlsEnabled = true;
        
        // Update camera
        this.cameras.main.centerOn(data.x, data.y);
        
        // Create spawn effect at new position
        this.createPlayerSpawnEffect(data.x, data.y);
        
        // Show respawn message
        this.gameUI.showNotification('You have respawned', 2000);
        
        // Update DOM UI with health and resources
        if (this.domUI) {
          this.domUI.updateStatsPanel(
            data.health,
            { 
              lipids: this.lipids, 
              glucose: Math.round(this.glucose), 
              atp: Math.round(this.atp),
              maxATP: this.playerCell ? this.playerCell.getMaxATP() : 100
            },
            this.playerCell.getMembraneStage()
          );
        }

        // Update health bar
        if (this.playerHealthBar) {
          this.updateHealthBar(this.playerHealthBar, data.health);
          this.playerHealthBar.setVisible(true);
        }
        
      } else {
        // Remote player respawned
        const remoteCell = this.remoteCells.get(data.playerId);
        if (remoteCell) {
          remoteCell.setPosition(data.x, data.y);
          remoteCell.setVisible(true);
          remoteCell.setHealth(data.health);
          
          // Create spawn effect at new position
          this.createPlayerSpawnEffect(data.x, data.y);
          
          // Show name and health bar
          const nameText = this.remoteNameTexts.get(data.playerId);
          const healthBar = this.remoteHealthBars.get(data.playerId);
          
          if (nameText) nameText.setVisible(true);
          if (healthBar) {
            this.updateHealthBar(healthBar, data.health);
            healthBar.setVisible(true);
          }
        }
      }
    });
    
    // Organelle built
    this.networkService.onOrganelleBuilt((data) => {
      log.info(() => `Received organelleBuilt event: ${data}`);
      log.info(() => `Organelle data includes IDs: ${data.organelles.map(o => ({ type: o.type, id: o.id }))}`);
      log.info(() => `Resources received from server: ${data.resources}`);
      log.info(() => `Current resources before update: lipids: ${this.lipids}, glucose: ${this.glucose}, atp: ${this.atp}`);
      
      if (data.playerId === this.networkService.getSocketId()) {
        // This is for our player
        console.log('Organelle built for our player. Updating resources and organelles.');
        
        // Get any pending accumulated production from the cell before processing server values
        const pendingProduction = this.playerCell.getAndResetResourceProduction();
        
        // If there's pending production, apply it to our local values first
        if (pendingProduction.needsUpdate) {
          log.info(() => `Applying pending production before server update: ATP +${pendingProduction.atp.toFixed(2)}, Glucose -${pendingProduction.glucose.toFixed(2)}`);
          
          // Apply to local values
          this.atp = Math.min(this.atp + pendingProduction.atp, this.playerCell.getMaxATP());
          this.glucose = Math.max(0, this.glucose - pendingProduction.glucose);
          
          // Update cell values to match
          this.playerCell.setATP(this.atp);
          this.playerCell.setGlucose(this.glucose);
        }
        
        // Calculate time since build request for debugging
        const now = Date.now();
        let timeSinceBuild = 0;
        if (this._lastBuildRequestTime) {
          timeSinceBuild = now - this._lastBuildRequestTime;
          log.info(() => `Server processing time for build request: ${timeSinceBuild}ms`);
        } else {
          log.info(() => 'No build request timestamp available');
        }
        
        // Log resource differences to understand reconciliation needs
        log.info(() => `Local values after applying pending production: lipids: ${this.lipids}, glucose: ${this.glucose}, atp: ${this.atp}`);
        
        log.info(() => `Server values: lipids: ${data.resources.lipids}, glucose: ${data.resources.glucose}, atp: ${data.resources.atp}`);
        
        // Reconcile local values (including applied production) with server values
        const adjustedResources = {
          // For lipids: Always use server value (server is authoritative for building costs)
          lipids: data.resources.lipids !== undefined ? data.resources.lipids : this.lipids,
          
          // For glucose:
          // If our glucose is lower than server's, use our value (preserves local consumption)
          // Otherwise use server's value (likely more accurate after build costs)
          glucose: data.resources.glucose !== undefined 
            ? Math.min(data.resources.glucose, this.glucose) 
            : this.glucose,
            
          // For ATP: 
          // If our ATP is higher than server's, use our value (preserves local production)
          // Otherwise use server's value
          atp: data.resources.atp !== undefined 
            ? Math.min(Math.max(data.resources.atp, this.atp), this.playerCell.getMaxATP()) 
            : this.atp
        };
        
        // Log final reconciled values
        console.log('Reconciled values:', adjustedResources);
        
        // Update resources with adjusted values
        this.updateResourceCounts(adjustedResources, true); // Force update
        
        // Reset build request timestamp
        this._lastBuildRequestTime = now;
        
        // After calling updateResourceCounts
        console.log('Final resources after update:', { 
          lipids: this.lipids, 
          glucose: this.glucose, 
          atp: this.atp
        });
        
        // Update the player cell's organelles
        this.updateOrganelles(data.organelles);
        
        // Ensure the cell has the updated values
        this.playerCell.setATP(this.atp);
        this.playerCell.setGlucose(this.glucose);
      } else {
        log.info(() => `Organelle built for remote player: ${data.playerId}`);
        // Update remote cell's organelles
        const cell = this.remoteCells.get(data.playerId);
        if (cell) {
          this.updateRemoteCellOrganelles(cell, data.organelles);
        } else {
          console.warn('Remote cell not found for player:', data.playerId);
        }
      }
    });
    
    // Membrane expanded event
    this.networkService.onMembraneExpanded((data: MembraneExpandedEvent) => {
      console.log('Membrane expanded event:', data);
      
      if (!this.serverUpdatesEnabled) {
        return;
      }
      
      // Is this our own player?
      if (data.playerId === this.networkService.getSocketId()) {
        // Update our local cell membrane
        this.handleLocalMembraneExpansion(data.stage);
        
        // Update resources
        this.updateResourceCounts(
          data.resources
        );
      } else {
        // Find the remote cell
        const remoteCell = this.remoteCells.get(data.playerId);
        if (remoteCell) {
          // Get the stage configuration
          const stageConfig = MEMBRANE_EXPANSION_STAGES.find((s) => s.stage === data.stage);
          if (stageConfig) {
            // Apply expansion to remote cell
            remoteCell.setMembraneStage(
              data.stage, 
              stageConfig.sizeMultiplier, 
              stageConfig.speedMultiplier
            );
          }
        }
      }
    });
    
    // Membrane expansion failed event
    this.networkService.onMembraneExpansionFailed(() => {
      console.log('Membrane expansion failed');
      
      this.domUI.showNotification('Develop your cell to meet the requirements.', 3000);
    });
    
    // Resource updates (ATP consumption from movement)
    this.networkService.onResourcesUpdated((data) => {
      // Update local resources with server values
      this.atp = data.atp;
      this.glucose = data.glucose;
      this.lipids = data.lipids;
      
      // Update the player cell with new values
      if (this.playerCell) {
        this.playerCell.setATP(this.atp);
        this.playerCell.setGlucose(this.glucose);
        
        // Update UI
        this.updateResourceCounts({
          lipids: this.lipids,
          glucose: this.glucose,
          atp: this.atp,
          maxATP: this.playerCell.getMaxATP()
        }, true); // Force update
      }
    });
  }
  
  /**
   * Handles local player's membrane expansion
   */
  private handleLocalMembraneExpansion(stage: number): void {
    // Find the stage configuration
    const stageConfig = MEMBRANE_EXPANSION_STAGES.find((s) => s.stage === stage);
    if (!stageConfig) return;
    
    // Apply expansion to player cell
    this.playerCell.setMembraneStage(
      stage, 
      stageConfig.sizeMultiplier, 
      stageConfig.speedMultiplier
    );
    
    // Show success notification
    this.domUI.showNotification(`Your cell membrane has expanded to stage ${stage}!`, 3000);
    
    // Create expansion visual effect
    this.createMembraneExpansionEffect();
    
    // Update the organelle menu to show the next stage requirements or max stage message
    // Get next stage config if not at max stage
    let nextStageConfig;
    if (stage < 4) {
      nextStageConfig = MEMBRANE_EXPANSION_STAGES.find(s => s.stage === stage + 1);
    }
    
    // Update the membrane info in the organelle menu
    if (this.domUI) {
      this.domUI.updateMembraneInfo(
        nextStageConfig ? {
          currentStage: stage,
          nextStage: {
            stage: nextStageConfig.stage,
            lipidCost: nextStageConfig.lipidCost || 0,
            atpCost: nextStageConfig.atpCost || 0,
            requirements: nextStageConfig.requirements.map(req => {
              // Use the requirement's built-in description property
              return {
                id: req.id,
                description: req.description
              };
            })
          }
        } : { currentStage: stage }
      );
    }
  }
  
  /**
   * Creates a visual effect for membrane expansion
   */
  private createMembraneExpansionEffect(): void {
    const x = this.playerCell.x;
    const y = this.playerCell.y;
    
    // Create a circle that expands outward
    const circle = this.add.circle(x, y, 100, 0xFFFFFF, 0.5);
    circle.setDepth(500);
    
    // Create a tween that expands the circle and fades it out
    this.tweens.add({
      targets: circle,
      radius: 200,
      alpha: 0,
      duration: 1000,
      ease: 'Sine.easeOut',
      onComplete: () => {
        circle.destroy();
      }
    });
  }
  
  /**
   * Request membrane expansion from the server
   */
  public requestMembraneExpansion(): void {
    if (!this.playerCell) return;
    
    console.log('Requesting membrane expansion');
    this.networkService.expandMembrane();
    
    // Show a loading state during the request
    this.domUI.showNotification('Requesting membrane expansion...', 3000);
  }

  private createUI(): void {
    // Initialize the GameUISystem with visibility set to false for elements we're replacing with DOM UI
    this.gameUI = new GameUISystem(this, {
      containerDepth: 1000,
      buttonBgColor: 0x1a6dd7,
      progressBarColor: 0x00aaff
    });
    
    // Add health progress bar (invisible - we're using DOM UI instead)
    const healthProgressBar = this.gameUI.createProgressBar(
      'health',
      20,
      20,
      'Health',
      100
    );
    // Hide the old health bar since we're using DOM UI
    healthProgressBar.visible = false;
    
    // Add ATP progress bar (invisible - we're using DOM UI instead)
    const atpProgressBar = this.gameUI.createProgressBar(
      'atp',
      20,
      50,
      'ATP',
      this.atp
    );
    // Set ATP bar color to yellow
    const atpBarData = this.gameUI.getProgressBar('atp');
    if (atpBarData && atpBarData.fill) {
      atpBarData.fill.setFillStyle(0xFFDD00);
    }
    // Hide the old ATP bar since we're using DOM UI
    atpProgressBar.visible = false;
    
    // Register health bar color updater component
    this.gameUI.registerComponent({
      id: 'healthBarColorUpdater',
      update: (data: any) => {
        if (typeof data !== 'object' || !('value' in data)) return;
        
        const health = data.value as number;
        const healthProgressData = this.gameUI.getProgressBar('health');
        
        if (healthProgressData && healthProgressData.fill) {
          if (health < 25) {
            healthProgressData.fill.setFillStyle(0xFF5252);  // Red when low health
          } else if (health < 50) {
            healthProgressData.fill.setFillStyle(0xFFB74D);  // Orange when medium health
          } else {
            healthProgressData.fill.setFillStyle(0x4CAF50);  // Green when good health
          }
        }
      }
    });
    
    // Add resource counters using GameUISystem's text elements (invisible - we're using DOM UI instead)
    const lipidText = this.gameUI.createText(
      'lipids',
      20, 
      80, 
      `Lipids: ${this.lipids}`, 
      {
        fontFamily: 'Arial',
        fontSize: '16px',
        color: '#ffffff'
      }
    );
    // Hide the old lipid text since we're using DOM UI
    lipidText.visible = false;
    
    const glucoseText = this.gameUI.createText(
      'glucose',
      20, 
      110, 
      `Glucose: ${this.glucose}`, 
      {
        fontFamily: 'Arial',
        fontSize: '16px',
        color: '#ffffff'
      }
    );
    // Hide the old glucose text since we're using DOM UI
    glucoseText.visible = false;
    
    const atpText = this.gameUI.createText(
      'atp',
      20, 
      140, 
      `ATP: ${this.atp}`, 
      {
        fontFamily: 'Arial',
        fontSize: '16px',
        color: '#ffffff'
      }
    );
    // Hide the old ATP text since we're using DOM UI
    atpText.visible = false;
    
    // Don't add back button here since we're using DOM version
    // this.gameUI.createBackButton(() => {
    //  this.leaveGame();
    // });
    
    // Add fullscreen button (invisible - we're using DOM UI instead)
    const fullscreenButton = this.gameUI.createFullscreenButton(20, 150);
    // Hide the old fullscreen button since we're using DOM UI
    fullscreenButton.visible = false;
    
    // Set up FPS monitor
    this.fpsMonitor = this.gameUI.setupFPSMonitor();
    
    // Create the network monitor
    this.networkMonitor = new NetworkMonitor(this);
    
    // Hide all GameUISystem progress bars since we're using DOM UI
    this.gameUI.getContainer().list.forEach((item: Phaser.GameObjects.GameObject) => {
      if (item instanceof Phaser.GameObjects.Container) {
        // Check if this container is a progress bar
        if (item.list.some(child => child instanceof Phaser.GameObjects.Rectangle)) {
          item.visible = false;
        }
      }
    });
  }
  
  private setupInput(): void {
    // Set up mobile controls if not already set up
    this.setupMobileControls();
    
    // ... existing input setup code ...
    
    // Add key for canceling movement
    this.input.keyboard?.on('keydown-ESC', () => {
      // Exit fullscreen if active
      if (this.scale.isFullscreen) {
        this.scale.stopFullscreen();
      }
    });
    
    // Add network monitor toggle shortcut (Alt+Ctrl+N)
    this.input.keyboard?.on('keydown-N', (event: KeyboardEvent) => {
      if (event.altKey && event.ctrlKey) {
        console.log('Network monitor toggle shortcut pressed');
        this.networkMonitor.toggleDisplay();
      }
    });
    
    // Add global click handler to check for resources under the pointer
    // This solves the issue of resources being blocked by the cell
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      // Only process left clicks for resource collection
      if (pointer.leftButtonDown()) {
        // Skip if over UI. 
        // ! this is weird, potential problem for the future?
        if (pointer.y < 150 || (pointer.x > this.scale.width - 200 && pointer.y < 300)) {
          return;
        }
        
        // Convert screen coordinates to world coordinates
        const worldPoint = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
        
        // Get the membrane radius from the membrane's baseRadius property
        const membraneRadius = this.playerCell.membrane ? 
          this.playerCell.membrane.baseRadius : 100;
        
        // Create a list of resources to collect
        const resourcesToCollect: string[] = [];
        
        // Check all resources to see if any are close to the click position
        this.resources.forEach((resource, id) => {
          // Calculate distance between click and resource
          const resourceDistance = Phaser.Math.Distance.Between(
            worldPoint.x, worldPoint.y,
            resource.x, resource.y
          );
          
          // Check if click is close to the resource (10 pixel radius for easier clicking)
          if (resourceDistance < 10) {
            // Calculate distance between resource and player cell
            const distanceToCell = Phaser.Math.Distance.Between(
              this.playerCell.x, this.playerCell.y,
              resource.x, resource.y
            );
            
            // Only collect if within collection range
            if (distanceToCell < membraneRadius + 15) {
              resourcesToCollect.push(id);
              
              // Check for other resources that are very close to this one
              if (distanceToCell < membraneRadius) {
                this.resources.forEach((otherResource, otherId) => {
                  // Skip if it's the one we already found
                  if (otherId === id) return;
                  
                  // Calculate distance between resources
                  const resourceToResourceDistance = Phaser.Math.Distance.Between(
                    resource.x, resource.y,
                    otherResource.x, otherResource.y
                  );
                  
                  // If the other resource is very close and under the cell, add it too
                  if (resourceToResourceDistance < 5) {
                    const otherDistanceToCell = Phaser.Math.Distance.Between(
                      this.playerCell.x, this.playerCell.y,
                      otherResource.x, otherResource.y
                    );
                    
                    if (otherDistanceToCell < membraneRadius && !resourcesToCollect.includes(otherId)) {
                      resourcesToCollect.push(otherId);
                    }
                  }
                });
              }
            }
          }
        });
        
        // Collect the resources
        if (resourcesToCollect.length > 0) {
          log.info(() => `Collecting ${resourcesToCollect.length} resources at click position`);
          
          resourcesToCollect.forEach((id, index) => {
            // Small delay between each resource collection request
            setTimeout(() => {
              this.networkService.collectResource(id);
            }, index * 20);
          });
        }
      }
    });
    
    // Setup toxin shooting with right mouse button instead of left
    // This allows the Cell's click-to-move to work with left clicks
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      // Only use right mouse button for shooting toxins
      if (pointer.rightButtonDown()) {
        // Skip if over known UI regions. This is a quick first check before more expensive hit testing.
        // Use the same check as the left-click resource collection to ensure consistency
        if (pointer.y < 150 || (pointer.x > this.scale.width - 200 && pointer.y < 300)) {
          console.log('Skipping toxin shot - pointer over UI region');
          return;
        }
        
        // Convert to world coordinates
        const worldPoint = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
        
        // Check if click is on the player's cell (to prevent shooting when breaching)
        const cellBounds = this.playerCell.getBounds();
        const isClickOnCell = cellBounds.contains(worldPoint.x, worldPoint.y);
        
        // Check if click is on a UI element using hit testing
        // This is more precise than coordinate-based checks
        let isClickOnUI = false;
        const hitObjects = this.input.hitTestPointer(pointer);
        
        // Debug UI objects detection
        console.log(`Right click at screen position (${pointer.x}, ${pointer.y})`);
        console.log(`Hit objects count: ${hitObjects.length}`);
        
        if (hitObjects.length > 0) {
          // Log each hit object for debugging
          hitObjects.forEach((obj, index) => {
            console.log(`Hit object ${index}:`, obj.type, obj.name, 
              obj.parentContainer ? `parent: ${obj.parentContainer.name || obj.parentContainer.type}` : 'no parent');
          });
          
          isClickOnUI = hitObjects.some(obj => this.isUIObject(obj));
          console.log(`isUIObject detection result: ${isClickOnUI}`);
        }
        
        // Only shoot toxin if NOT clicking on the player's cell or UI
        if (!isClickOnCell && !isClickOnUI) {
          // Debug log to help diagnose future issues
          log.info(() => `Shooting toxin at (${worldPoint.x}, ${worldPoint.y}) from cell at (${this.playerCell.x}, ${this.playerCell.y})`);
          this.shootToxin(worldPoint.x, worldPoint.y);
        } else {
          console.log(`Shooting prevented: isClickOnCell=${isClickOnCell}, isClickOnUI=${isClickOnUI}`);
        }
      }
    });
    
    // Set up input priority - when a UI element is clicked, stop event propagation
    this.input.on('gameobjectdown', (pointer: Phaser.Input.Pointer, gameObject: any) => {
      // Check if the game object is part of the UI
      if (this.isUIObject(gameObject)) {
        // Stop event propagation to prevent cell movement
        pointer.event.stopPropagation();
      }
    }, this);
  }
  
  /**
   * Helper method to check if a game object is part of the UI
   * @param gameObject The game object to check
   * @returns True if the object is part of the UI, false otherwise
   */
  private isUIObject(gameObject: any): boolean {
    // Check if it's in the UI container
    if (this.gameUI && this.gameUI.getContainer()) {
      // Check if it's the container itself or a child of it
      const uiContainer = this.gameUI.getContainer();
      
      if (gameObject === uiContainer) {
        return true;
      }
      
      // Check if it's a child of the UI container
      let parent = gameObject.parentContainer;
      while (parent) {
        if (parent === uiContainer) {
          return true;
        }
        parent = parent.parentContainer;
      }
    }
    
    // Check if it's an organelle button
    for (const container of this.organelleButtons) {
      if (gameObject === container) {
        return true;
      }
      
      // Check if it's a child of an organelle button container
      let parent = gameObject.parentContainer;
      while (parent) {
        if (parent === container) {
          return true;
        }
        parent = parent.parentContainer;
      }
    }
    
    return false;
  }
  
  private setupCameraControls(): void {
    // Middle mouse button or touch drag to pan camera
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      // Check for middle mouse button or multi-touch (for mobile)
      if ((pointer.middleButtonDown() || (pointer as any).touchPoints > 1)) {
        // Skip if pointer is over UI elements (top area of screen or side menus)
        if (pointer.y < 150 || (pointer.x > this.scale.width - 200 && pointer.y < 300)) {
          return;
        }
        
        this.cameraControls.isDragging = true;
        this.cameraControls.lastPointerPosition = { x: pointer.x, y: pointer.y };
        this.cameras.main.stopFollow();
      }
    });
    
    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (this.cameraControls.isDragging) {
        const deltaX = pointer.x - this.cameraControls.lastPointerPosition.x;
        const deltaY = pointer.y - this.cameraControls.lastPointerPosition.y;
        
        // Fix camera panning - divide by zoom to get proper movement
        this.cameras.main.scrollX -= deltaX / this.cameras.main.zoom;
        this.cameras.main.scrollY -= deltaY / this.cameras.main.zoom;
        
        this.cameraControls.lastPointerPosition = { x: pointer.x, y: pointer.y };
      }
    });
    
    this.input.on('pointerup', () => {
      this.cameraControls.isDragging = false;
    });
    
    // Mouse wheel to zoom - fix the zoom calculation
    this.input.on('wheel', (pointer: any, gameObjects: any, deltaX: number, deltaY: number) => {
      // Fix zoom direction and sensitivity
      // Negative deltaY is scroll up (zoom in), positive is scroll down (zoom out)
      const zoomDirection = deltaY > 0 ? -1 : 1;
      const zoomFactor = 0.1; // Increased for more responsive zooming
      
      // Calculate zoom based on direction and apply immediately
      const newZoom = Phaser.Math.Clamp(
        this.cameras.main.zoom + (zoomDirection * zoomFactor * this.cameras.main.zoom),
        this.cameraControls.minZoom,
        this.cameraControls.maxZoom
      );
      
      // Apply new zoom
      this.cameras.main.zoom = newZoom;
      
      // Log zoom for debugging
      log.info(() => `Zoom: ${this.cameras.main.zoom.toFixed(2)}, deltaY: ${deltaY}`);
    });
    
    // Double tap to center on cell
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if ((pointer as any).downTime - (pointer as any).previousDownTime < 300) {
        this.cameras.main.startFollow(this.playerCell);
        this.cameras.main.zoom = 1;
      }
    });
    
    // Touch pinch to zoom - add support for mobile
    this.input.addPointer(1); // Ensure we can track at least 2 pointers for pinch
    
    let pinchDistance = 0;
    let pinchZoom = 1;
    
    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      // Check for pinch gesture (two pointers)
      if (this.input.pointer1.isDown && this.input.pointer2.isDown) {
        const currentDistance = Phaser.Math.Distance.Between(
          this.input.pointer1.x, this.input.pointer1.y,
          this.input.pointer2.x, this.input.pointer2.y
        );
        
        // Initialize pinch distance and zoom when gesture starts
        if (pinchDistance === 0) {
          pinchDistance = currentDistance;
          pinchZoom = this.cameras.main.zoom; // Start from current zoom level
        }
        
        // Ignore very small movements to prevent accidental zooming
        if (Math.abs(currentDistance - pinchDistance) > 1) {
          const distanceRatio = currentDistance / pinchDistance;
          
          // Calculate zoom change - make it more responsive on mobile
          const zoomChange = (distanceRatio - 1) * pinchZoom * 0.5;
          
          // Only apply zoom if the change is significant
          if (Math.abs(zoomChange) > 0.005) {
            // Apply new zoom immediately for better responsiveness on mobile
            const newZoom = Phaser.Math.Clamp(
              pinchZoom + zoomChange,
              this.cameraControls.minZoom,
              this.cameraControls.maxZoom
            );
            
            // Apply zoom directly for immediate feedback
            this.cameras.main.zoom = newZoom;
            
            // Update pinch zoom for next calculation
            pinchZoom = newZoom;
            
            // Log zoom for debugging
            log.info(() => `Pinch zoom: ${this.cameras.main.zoom.toFixed(2)}`);
          }
        }
        
        pinchDistance = currentDistance;
      }
    });
    
    // Reset pinch tracking on pointer up
    this.input.on('pointerup', () => {
      pinchDistance = 0;
    });
  }
  
  private createResourceTextures(): void {
    log.info(() => 'Creating resource textures');
    
    // Create lipid texture - keeping existing for now
    if (!this.textures.exists('lipid')) {
      const lipidGraphics = this.add.graphics();
      lipidGraphics.fillStyle(0x88AAFF, 1); // Blue for lipids
      lipidGraphics.fillCircle(8, 8, 8);
      lipidGraphics.lineStyle(2, 0xFFFFFF, 0.8);
      lipidGraphics.strokeCircle(8, 8, 8);
      lipidGraphics.generateTexture('lipid', 16, 16);
      lipidGraphics.destroy();
      console.log('Lipid texture created programmatically');
    }
    
    // For glucose, just verify it exists (it should have been preloaded in BootScene)
    // We don't attempt to load it here - the fallback mechanism in preload handles that
    const sharedResources = SharedResources.getInstance();
    if (!sharedResources.hasGlucoseTexture()) {
      console.error('⚠️ Glucose texture missing! Fallback visuals will be used');
    }
  }
  
  private createToxinTexture(): void {
    if (!this.textures.exists('toxin')) {
      const toxinGraphics = this.add.graphics();
      toxinGraphics.fillStyle(0xFF5555, 1); // Red for toxins
      toxinGraphics.fillCircle(6, 6, 6);
      toxinGraphics.fillStyle(0xFFAAAA, 1); 
      toxinGraphics.fillCircle(6, 6, 3);
      toxinGraphics.generateTexture('toxin', 12, 12);
      toxinGraphics.destroy();
    }
  }
  
  private addRemoteCell(playerState: PlayerState): void {
    // Ensure the player has a name
    playerState = this.ensurePlayerHasName(playerState);
    
    // If this remote cell already exists, just update it
    if (this.remoteCells.has(playerState.id)) {
      const existingCell = this.remoteCells.get(playerState.id)!;
      existingCell.setPosition(playerState.x, playerState.y);
      existingCell.setHealth(playerState.health);
      // Update organelles
      this.updateRemoteCellOrganelles(existingCell, playerState.organelles);
      return;
    }
    
    log.info(() => `Adding remote cell for player ${playerState.id} at position: ${playerState.x}, ${playerState.y}`);
    
    try {
      // Create new remote cell
      const cell = new Cell(this, playerState.x, playerState.y, playerState.id);
      
      // Set health from server state
      cell.setHealth(playerState.health);
      
      // Add organelles from server state
      for (const organelleState of playerState.organelles) {
        this.addOrganelleToCell(cell, organelleState);
      }
      
      // Add to game objects group for collision detection
      this.gameObjectsGroup.add(cell);
      
      // Store in remoteCells map
      this.remoteCells.set(playerState.id, cell);
      
      // Add to scene groups for proper rendering order
      this.ensureIgnoredByUICamera(cell);
      
      // After creating the remote cell, add name text and health bar
      this.addNameAndHealthBar(cell, playerState.id, playerState.name, playerState.health);
    } catch (error) {
      console.error(`Error adding remote cell for player ${playerState.id}:`, error);
    }
  }
  
  private addResource(resourceState: ResourceState): void {
    // Check if this resource already exists (prevents duplicates from resourceSpawned events)
    if (this.resources.has(resourceState.id)) {
      log.info(() => `Resource ${resourceState.id} already exists, not adding duplicate`);
      return;
    }
    
    // Handle case where resourceState has missing properties
    if (!resourceState.x || !resourceState.y || !resourceState.type || !resourceState.value) {
      console.error('Invalid resource state:', resourceState);
      return;
    }
    
    // Log resource being added
    log.info(() => `Adding ${resourceState.ejected ? "ejected" : "normal"} ${resourceState.type} resource at (${resourceState.x}, ${resourceState.y}) with value ${resourceState.value}`);
    
    // Create resource sprite
    let resource: Phaser.GameObjects.Sprite;
    
    // Calculate scale based on resource value (min 16px, max 22px)
    // Use a logarithmic scale to handle a wide range of values
    const calculateScaleForValue = (value: number, baseSize: number): number => {
      // Define min and max sizes
      const minSize = 16;
      const maxSize = 22;
      
      // Calculate size based on value
      // Use log scale to handle wide range (1-100+)
      let size;
      if (value <= 1) {
        size = minSize; // Minimum size for values <= 1
      } else {
        // Log scale with bias to prevent sizes growing too quickly
        const logFactor = Math.log(value + 2) / Math.log(10); // Log base 10 of (value + 2)
        size = minSize + (maxSize - minSize) * Math.min(logFactor / 1.5, 1);
      }
      
      // Convert to scale factor based on the base texture size
      return size / baseSize;
    };
    
    // Use appropriate creation method based on resource type
    if (resourceState.type === 'glucose') {
      // Use the shared resources system for glucose
      const sharedResources = SharedResources.getInstance();
      
      // First verify the texture is loaded to avoid fallbacks
      if (!sharedResources.hasGlucoseTexture()) {
        console.warn('Creating glucose resource with texture not yet loaded!');
      }
      
      resource = sharedResources.createGlucoseSprite(this, resourceState.x, resourceState.y);
      
      // Calculate scale based on value using original base size, but make ejected resources larger
      const valueScale = calculateScaleForValue(resourceState.value, 16) / 3.0; // Standard for normal resources
      
      resource.setScale(valueScale);
      resource.setAlpha(1.0); // Full visibility (was 0.9)
    } else {
      // Create other resource types normally
      resource = this.add.sprite(resourceState.x, resourceState.y, resourceState.type);
      
      // Set scale based on value (base size is 16px)
      const valueScale = calculateScaleForValue(resourceState.value, 16);
      resource.setScale(valueScale);
      resource.setAlpha(1.0); // Full visibility (was 0.9)
    }
    
    // Ensure resource has a high depth to be visible above other elements
    resource.setDepth(60); // Increased from 50 for better visibility
    
    // Set metadata
    resource.setData('id', resourceState.id);
    resource.setData('type', resourceState.type);
    resource.setData('value', resourceState.value);
    resource.setData('ejected', resourceState.ejected || false);
    
    // Add to collection
    this.resources.set(resourceState.id, resource);
    
    // Ensure it's ignored by the UI camera
    this.ensureIgnoredByUICamera(resource);
    
    // Add hover effect only - click handling is done in the global input handler
    resource.setInteractive({ useHandCursor: true });

        // Special handling for ejected resources - create visual effect
    if (resourceState.ejected) {
      // Create a simple appearance and pulsation animation for ejected resources
      this.attachAnimationToEjectedResource(resource);
    }
    
    // Use consistent hover effects for all resource types
    resource.on('pointerover', () => {
      resource.setScale(resource.scale * 1.2);
    });
    resource.on('pointerout', () => {
      resource.setScale(resource.scale / 1.2);
    });
  }
  
  /**
   * Attaches appearance and pulsation animation to ejected resources
   */
  private attachAnimationToEjectedResource(resource: Phaser.GameObjects.Sprite): void {
    log.info(() => `Adding animation effect to ejected ${resource.getData('type')} resource at (${resource.x}, ${resource.y})`);
    
    // Create a glow effect around the resource
    const resourceType = resource.getData('type');
    const color = resourceType === 'lipid' ? 0x88AAFF : 0xFFCC88;
    const glow = this.add.circle(resource.x, resource.y, 22, color, 0.6);
    glow.setDepth(resource.depth - 1); // Just below the resource
    this.ensureIgnoredByUICamera(glow);
    
    // Create a brief burst particle effect to highlight the appearance
    const burstParticles = this.add.particles(resource.x, resource.y, 'toxin', {
      speed: { min: 50, max: 150 },
      scale: { start: 0.4, end: 0 },
      alpha: { start: 0.7, end: 0 },
      lifespan: 600,
      quantity: 10,
      frequency: -1, // Emit all at once
      tint: color
    });
    burstParticles.setDepth(resource.depth - 2);
    this.ensureIgnoredByUICamera(burstParticles);
    burstParticles.explode(10, resource.x, resource.y);
    
    // Set up a timer to destroy the burst particles after they're done
    this.time.delayedCall(800, () => {
      if (burstParticles.active) {
        burstParticles.destroy();
      }
    });
    
    // Add pulsating animation to the glow
    this.tweens.add({
      targets: glow,
      scale: { from: 0.8, to: 1.2 },
      alpha: { from: 0.6, to: 0.3 },
      duration: 800,
      yoyo: true,
      repeat: 2,
      ease: 'Sine.easeInOut',
      onComplete: () => {
        // Fade out the glow
        this.tweens.add({
          targets: glow,
          alpha: 0,
          duration: 400,
          ease: 'Power2',
          onComplete: () => {
            if (glow.active) {
              glow.destroy();
            }
          }
        });
      }
    });
    
    // Add a subtle scale animation to the resource itself
    this.tweens.add({
      targets: resource,
      scale: resource.scale * 1.15, // Increase size slightly
      duration: 300,
      ease: 'Back.easeOut',
      yoyo: true,
      repeat: 1
    });
  }
  
  private sendPositionUpdate(): void {
    try {
      // Send update to server
      this.networkService.updatePosition(
        this.playerCell.x,
        this.playerCell.y,
        this.playerCell.rotation
      );
    } catch (error) {
      console.error('Error sending position update:', error);
      // Send a basic update without membrane points
      this.networkService.updatePosition(
        this.playerCell.x,
        this.playerCell.y,
        this.playerCell.rotation
      );
    }
  }

  private sendMembraneUpdate(): void {
    const now = Date.now();
    if (now - this.lastMembraneSyncTimeMs < this.membraneSyncIntervalMs) {
      return;
    }
    if (!this.playerCell?.membrane || typeof this.playerCell.membrane.getPoints !== 'function') {
      return;
    }

    const membranePoints = this.playerCell.membrane.getPoints();
    if (!membranePoints || membranePoints.length === 0) {
      return;
    }

    this.lastMembraneSyncTimeMs = now;
    this.networkService.updateMembrane(membranePoints);
  }

  private queueRemoteMovement(data: PlayerMovedEvent, serverTime?: number): void {
    if (data.id === this.networkService.getSocketId()) {
      return;
    }

    if (!this.remoteCells.has(data.id)) {
      const playerState: PlayerState = {
        id: data.id,
        name: data.name || 'Player',
        x: data.x,
        y: data.y,
        rotation: data.rotation,
        health: 100,
        organelles: [],
        resources: { lipids: 0, glucose: 0 },
        membranePoints: data.points
      };
      this.addRemoteCell(playerState);
    }

    this.remoteInterpolationBuffer.push(data.id, serverTime ?? Date.now(), data);
  }

  private applyRemoteInterpolation(): void {
    const renderNow = Date.now();
    this.remoteCells.forEach((remoteCell, playerId) => {
      const interpolated = this.remoteInterpolationBuffer.sample(playerId, renderNow);
      if (!interpolated) {
        return;
      }

      remoteCell.setPosition(interpolated.x, interpolated.y);
      remoteCell.setRotation(interpolated.rotation);

      if (
        interpolated.points &&
        interpolated.points.length > 0 &&
        remoteCell.membrane &&
        typeof remoteCell.membrane.updateMembranePath === 'function'
      ) {
        remoteCell.membrane.updateMembranePath(interpolated.points);
      }
    });
  }

  private applyRemoteMembraneKeyframe(data: MembraneKeyframeEvent): void {
    if (data.playerId === this.networkService.getSocketId()) {
      return;
    }

    const remoteCell = this.remoteCells.get(data.playerId);
    if (!remoteCell) {
      return;
    }

    if (
      data.points &&
      data.points.length > 0 &&
      remoteCell.membrane &&
      typeof remoteCell.membrane.updateMembranePath === 'function'
    ) {
      remoteCell.membrane.updateMembranePath(data.points);
    }
  }

  private hasTickMovementEnabled(): boolean {
    return this.receivedTickUpdates || this.strictTickMode;
  }

  private handleTickUpdate(data: TickUpdateEvent): void {
    if (this.lastReceivedServerTick > 0 && data.tick > this.lastReceivedServerTick + this.tickGapRecoveryThreshold) {
      const now = Date.now();
      if (now - this.lastSnapshotRequestAtMs > this.snapshotRequestCooldownMs) {
        this.lastSnapshotRequestAtMs = now;
        this.networkService.requestSnapshot();
      }
    }

    this.lastReceivedServerTick = Math.max(this.lastReceivedServerTick, data.tick);
    this.receivedTickUpdates = true;
    const ownPlayerId = this.networkService.getSocketId();
    const ownAuthoritativeMove = data.playerMoves.find((move) => move.id === ownPlayerId);
    if (ownAuthoritativeMove) {
      const reconciled = this.localInputPredictor.reconcile(
        { x: this.playerCell.x, y: this.playerCell.y },
        { x: ownAuthoritativeMove.x, y: ownAuthoritativeMove.y }
      );
      if (reconciled.x !== this.playerCell.x || reconciled.y !== this.playerCell.y) {
        this.playerCell.setPosition(reconciled.x, reconciled.y);
      }
    }

    data.playerMoves.forEach((move) => this.queueRemoteMovement(move, data.serverTime));
  }

  private processFullSnapshot(snapshot: FullSnapshotEvent): void {
    this.lastReceivedServerTick = Math.max(this.lastReceivedServerTick, snapshot.tick);
    this.processRoomState({
      players: snapshot.players,
      resources: snapshot.resources
    });

    this.toxins.forEach((toxin) => toxin.destroy());
    this.toxins.clear();
    snapshot.toxins.forEach((toxin) => {
      this.createToxin(toxin);
    });
  }
  
  private updateRemoteCells(delta: number): void {
    // Update each remote cell
    this.remoteCells.forEach((cell, playerId) => {
      cell.update(delta);
      
      // Update the position of name texts and health bars to follow the cells
      // This is needed because these elements should stay with their cells
      const nameText = this.remoteNameTexts.get(playerId);
      const healthBar = this.remoteHealthBars.get(playerId);
      
      if (nameText && !nameText.parentContainer) {
        // If the name text isn't a child of the cell container, update its position
        // Position it under the cell, just below the membrane
        const membraneRadius = cell.membrane.baseRadius;
        nameText.setPosition(cell.x, cell.y + membraneRadius + 10);
      }
      
      if (healthBar && !healthBar.parentContainer) {
        // If the health bar isn't a child of the cell container, update its position
        // Position it below the name text
        const membraneRadius = cell.membrane.baseRadius;
        healthBar.setPosition(cell.x, cell.y + membraneRadius + 35);
      }
    });
  }
  
  private updateCamera(delta: number): void {
    // If not dragging, ensure camera follows the player
    if (!this.cameraControls.isDragging) {
      this.cameras.main.centerOn(this.playerCell.x, this.playerCell.y);
    }
  }
  
  private shootToxin(targetX: number, targetY: number): void {
    console.log(`Executing shootToxin to target (${targetX}, ${targetY})`);
    
    // Calculate direction vector
    const dx = targetX - this.playerCell.x;
    const dy = targetY - this.playerCell.y;
    const angle = Math.atan2(dy, dx);
    
    if (this.atp < TOXIN_ATP_COST) {
      return;
    }

    // Send toxin info to server
    this.networkService.shootToxin(
      this.playerCell.x, 
      this.playerCell.y, 
      angle
    );
  }
  
  private createToxin(data: ToxinShotEvent | ToxinState): void {
    const spawnX = data.currentX ?? data.startX;
    const spawnY = data.currentY ?? data.startY;
    const createdAt = data.createdAt ?? Date.now();

    // Create toxin sprite
    const toxin = this.add.sprite(spawnX, spawnY, 'toxin');
    toxin.setData('id', data.id);
    toxin.setData('playerId', data.playerId);
    toxin.setData('angle', data.angle);
    toxin.setData('speed', data.speed);
    toxin.setData('createdAt', createdAt);
    
    // Rotate to match direction
    toxin.setRotation(data.angle);
    
    // Add to collection
    this.toxins.set(data.id, toxin);
    
    // Ensure it's ignored by the UI camera
    this.ensureIgnoredByUICamera(toxin);
    
    // Auto-remove based on server creation time to keep clients in sync
    const remainingLifetime = Math.max(0, 2000 - (Date.now() - createdAt));
    this.time.delayedCall(remainingLifetime, () => {
      toxin.destroy();
      this.toxins.delete(data.id);
    });
  }
  
  private updateToxins(delta: number): void {
    // Update each toxin's position
    this.toxins.forEach(toxin => {
      const angle = toxin.getData('angle');
      const speed = toxin.getData('speed');
      const deltaSeconds = delta / 1000;
      
      // Move in the direction of the angle
      toxin.x += Math.cos(angle) * speed * deltaSeconds;
      toxin.y += Math.sin(angle) * speed * deltaSeconds;
    });
  }
  
  private showDamageEffect(cell: Cell, angle: number, amount: number): void {
    // Create particle effect at the hit location
    const x = cell.x + Math.cos(angle) * 70;
    const y = cell.y + Math.sin(angle) * 70;
    
    // Create particles
    const particles = this.add.particles(x, y, 'toxin', {
      speed: 100,
      scale: { start: 0.5, end: 0 },
      blendMode: 'ADD',
      lifespan: 500,
      quantity: amount / 2,
      angle: { min: 0, max: 360 }
    });
    
    // Ensure particles are ignored by the UI camera
    this.ensureIgnoredByUICamera(particles);
    
    // Auto-destroy after animation completes
    this.time.delayedCall(500, () => {
      particles.destroy();
    });
  }
  
  private buildOrganelle(type: string): void {
    console.log(`Attempting to build ${type}`);
    
    // Prevent multiple build requests at once
    if (this.isBuilding) {
      this.domUI.showNotification('Already building an organelle!');
      return;
    }
    
    // Check if we have enough resources
    let cost = { lipids: 0, glucose: 0, atp: 0 };

    console.warn("ORGANELLE TO BUILD", type);
    
    switch (type) {
      case 'mitochondrion':
        cost = { lipids: 20, glucose: 30, atp: 0 };
        break;
      case 'lysosome':
        cost = { lipids: 30, glucose: 20, atp: 0 };
        break;
      case 'expand_membrane':
        // Membrane expansion is handled separately
        this.requestMembraneExpansion();
        return;
      default:
        console.warn(`Unknown organelle type: ${type}`);
        return;
    }
    
    // Check if we have enough resources
    if (this.lipids >= cost.lipids && this.glucose >= cost.glucose && this.atp >= cost.atp) {
      // Set building flag
      this.isBuilding = true;
      
      // Deduct resources immediately for responsive feedback
      this.lipids -= cost.lipids;
      this.glucose -= cost.glucose;
      this.atp -= cost.atp;
      
      // Update UI with new resource values
      this.updateResourceCounts({ lipids: this.lipids, glucose: this.glucose, atp: this.atp }, true);
      
      // Show building effect
      this.showBuildingEffect(type);
      
      // Set the timestamp when we send the build request
      this._lastBuildRequestTime = Date.now();
      
      // Send build request to server
      this.networkService.buildOrganelle(type);
      
      // Log when the request was sent for debugging
      log.info(() => `Build request for ${type} sent at ${new Date().toISOString()}`);
      
      // Show success message
      this.domUI.showNotification(`Building ${type}...`);
      
      // Reset building flag after a delay (protection against spam)
      this.time.delayedCall(2000, () => {
        this.isBuilding = false;
      });
    } else {
      // Calculate what resources we're missing
      const missingLipids = Math.max(0, cost.lipids - this.lipids);
      const missingGlucose = Math.max(0, cost.glucose - this.glucose);
      const missingATP = Math.max(0, cost.atp - this.atp);

      // Show detailed error message
      let errorMsg = 'Not enough resources! Need ';
      if (missingLipids > 0) {
        errorMsg += `${missingLipids} more lipids`;
      }
      if (missingGlucose > 0) {
        errorMsg += missingLipids > 0 ? ' and ' : '';
        errorMsg += `${missingGlucose} more glucose`;
      }
      if (missingATP > 0) {
        errorMsg += missingGlucose > 0 ? ' and ' : '';
        errorMsg += `${missingATP} more ATP`;
      }
      this.domUI.showNotification(errorMsg);
    }
  }

  private showBuildingEffect(type: string): void {
    // Different effects based on organelle type
    let color: number;
    switch (type) {
      case 'mitochondrion':
        color = 0x66DDCC; // Teal color for mitochondria
        break;
      case 'lysosome':
        color = 0xE88A8A; // Calm reddish color for lysosomes (association with digestive function)
        break;
      default:
        color = 0x4488FF; // Default blue color
    }
    
    const particleCount = type === 'mitochondrion' ? 15 : 10;
    
    // Create a building effect at the cell's position
    const buildParticles = this.add.particles(this.playerCell.x, this.playerCell.y, 'lipid', {
      speed: { min: 50, max: 100 },
      scale: { start: 0.5, end: 0 },
      alpha: { start: 0.6, end: 0 },
      lifespan: 1000,
      quantity: particleCount,
      blendMode: 'ADD',
      emitting: false,
      tint: color
    });
    
    // Ensure particles are ignored by UI camera
    this.ensureIgnoredByUICamera(buildParticles);
    
    // Emit particles in a burst
    buildParticles.explode(particleCount);
    
    // Add a pulsing glow effect at the cell center
    const glow = this.add.circle(this.playerCell.x, this.playerCell.y, 50, color, 0.3);
    this.ensureIgnoredByUICamera(glow);
    
    // Create a pulsing animation
    this.tweens.add({
      targets: glow,
      alpha: 0,
      scale: 1.5,
      duration: 1000,
      ease: 'Sine.Out',
      onComplete: () => {
        glow.destroy();
      }
    });
    
    // Clean up after animation
    this.time.delayedCall(1000, () => {
      buildParticles.destroy();
    });
  }
  
  private updateOrganelles(organelles: OrganelleState[]): void {
    log.info(() => `Updating player cell organelles: ${organelles}`);
    
    // Create a map of current organelles by ID
    const currentOrganelles = new Map<string, Organelle>();
    this.playerCell.getOrganelles().forEach(organelle => {
      // Skip nucleus as we don't want to remove it
      if (organelle.getType() !== 'nucleus') {
        // Store by ID if available, otherwise by type
        const id = organelle.getId() || organelle.getType();
        currentOrganelles.set(id, organelle);
        log.info(() => `Current organelle: type=${organelle.getType()}, id=${organelle.getId() || 'no-id'}`);
      }
    });
    
    log.info(() => `Found ${currentOrganelles.size} existing organelles (excluding nucleus)`);
    
    // Add or update organelles from the new state
    organelles.forEach(organelleState => {
      // Skip nucleus as it's handled separately
      if (organelleState.type !== 'nucleus') {
        // Log the organelle state we're processing
        log.info(() => `Processing organelle from state: type=${organelleState.type}, id=${organelleState.id || 'no-id'}`);
        
        // IMPORTANT: Use ID for matching when available, not type
        // This allows multiple organelles of the same type
        const lookupId = organelleState.id || organelleState.type;
        const existingOrganelle = currentOrganelles.get(lookupId);
        
        if (existingOrganelle) {
          // We found a matching organelle, update it if needed
          log.info(() => `Found existing organelle with id/type ${lookupId}`);
          
          // Remove from the map so we don't remove it later
          currentOrganelles.delete(lookupId);
          
          // Update any additional properties if needed
          if (typeof existingOrganelle.updateFromState === 'function') {
            existingOrganelle.updateFromState(organelleState);
          }
        } else {
          // No matching organelle found, add a new one
          log.info(() => `No existing organelle found with id/type ${lookupId}, adding new one`);
          this.addOrganelleToCell(this.playerCell, organelleState);
        }
      }
    });
    
    // Remove any remaining organelles that weren't in the new state
    if (currentOrganelles.size > 0) {
      log.info(() => `Removing ${currentOrganelles.size} organelles that were not in the new state`);
      currentOrganelles.forEach((organelle, id) => {
        log.info(() => `Removing organelle: type=${organelle.getType()}, id=${id}`);
        this.playerCell.removeOrganelle(organelle);
      });
    }
  }
  
  private updateRemoteCellOrganelles(cell: Cell, organelles: OrganelleState[]): void {
    log.info(() => `Updating remote cell organelles: ${organelles}`);
    
    // Create a map of current organelles by ID
    const currentOrganelles = new Map<string, Organelle>();
    cell.getOrganelles().forEach(organelle => {
      // Skip nucleus as we don't want to remove it
      if (organelle.getType() !== 'nucleus') {
        // Store by ID if available, otherwise by type
        const id = organelle.getId() || organelle.getType();
        currentOrganelles.set(id, organelle);
        log.info(() => `Current remote cell organelle: type=${organelle.getType()}, id=${organelle.getId() || 'no-id'}`);
      }
    });
    
    // Add or update organelles from the new state
    organelles.forEach(organelleState => {
      // Skip nucleus as it's handled separately
      if (organelleState.type !== 'nucleus') {
        // Log the organelle state we're processing
        log.info(() => `Processing remote cell organelle: type=${organelleState.type}, id=${organelleState.id || 'no-id'}`);
        
        // IMPORTANT: Use ID for matching when available, not type
        // This allows multiple organelles of the same type
        const lookupId = organelleState.id || organelleState.type;
        const existingOrganelle = currentOrganelles.get(lookupId);
        
        if (existingOrganelle) {
          // We found a matching organelle, update it if needed
          log.info(() => `Found existing remote cell organelle with id/type ${lookupId}`);
          
          // Remove from the map so we don't remove it later
          currentOrganelles.delete(lookupId);
          
          // Update any additional properties if needed
          if (typeof existingOrganelle.updateFromState === 'function') {
            existingOrganelle.updateFromState(organelleState);
          }
        } else {
          // No matching organelle found, add a new one
          log.info(() => `No existing remote cell organelle found with id/type ${lookupId}, adding new one`);
          this.addOrganelleToCell(cell, organelleState);
        }
      }
    });
    
    // Remove any remaining organelles that weren't in the new state
    if (currentOrganelles.size > 0) {
      log.info(() => `Removing ${currentOrganelles.size} remote cell organelles that were not in the new state`);
      currentOrganelles.forEach((organelle, id) => {
        log.info(() => `Removing remote cell organelle: type=${organelle.getType()}, id=${id}`);
        cell.removeOrganelle(organelle);
      });
    }
  }
  
  private handleResize(): void {
    // Use the GameUISystem's handleResize method which handles all UI elements
    this.gameUI.handleResize();
    
    // Handle DOM UI resize
    if (this.domUI) {
      this.domUI.handleResize();
    }
    
    // Make sure legacy UI stays hidden after resize
    this.keepLegacyUIHidden();
  }
  
  private leaveGame(): void {
    console.log('Leaving multiplayer game');
    
    // Clean up UI
    if (this.gameUI) {
      this.gameUI.destroy();
    }
    
    // Clean up all cells
    this.remoteCells.forEach(cell => cell.destroy());
    this.remoteCells.clear();
    
    if (this.playerCell) {
      this.playerCell.destroy();
    }
    
    // Clean up network connection since we're done with multiplayer
    this.networkService.destroy();
    
    // Return to main menu
    this.scene.start('MainMenuScene');
  }
  
  shutdown(): void {
    console.log('MultiplayerGameScene shutting down');
    
    // Disable server updates to prevent any new updates during shutdown
    this.serverUpdatesEnabled = false;
    
    // Clean up event listeners
    this.input.off('pointerdown');
    this.input.off('pointermove');
    this.input.off('pointerup');
    this.scale.off('resize', this.handleResize);
    this.events.off('windowResized', this.handleResize);
    
    // Destroy mobile joysticks if they exist
    if (this.joystickLeft) {
      this.joystickLeft.destroy();
      this.joystickLeft = null;
    }
    
    if (this.joystickRight) {
      this.joystickRight.destroy();
      this.joystickRight = null;
    }
    
    // Clean up UI
    if (this.gameUI) {
      this.gameUI.destroy();
    }
    
    // Clean up DOM UI elements
    if (this.domUI) {
      this.domUI.destroy();
    }
    
    // Clean up FPS monitor
    if (this.fpsMonitor) {
      this.fpsMonitor.destroy();
    }
    
    // Clean up Network monitor
    if (this.networkMonitor) {
      this.networkMonitor.destroy();
    }
    
    // Clean up game objects
    if (this.gradientBackground) {
      this.gradientBackground.destroy();
    }
    
    // Clean up all cells
    this.remoteCells.forEach(cell => cell.destroy());
    this.remoteCells.clear();
    
    if (this.playerCellCreated && this.playerCell) {
      this.playerCell.destroy();
      this.playerCellCreated = false;
    }
    
    // Clean up resources
    this.resources.forEach(resource => resource.destroy());
    this.resources.clear();
    
    // Clean up toxins
    this.toxins.forEach(toxin => toxin.destroy());
    this.toxins.clear();
    
    // Clean up player name and health bar
    if (this.playerNameText) this.playerNameText.destroy();
    if (this.playerHealthBar) this.playerHealthBar.destroy();
    
    // Clean up remote names and health bars
    this.remoteNameTexts.forEach(text => text.destroy());
    this.remoteHealthBars.forEach(bar => bar.destroy());
    this.remoteNameTexts.clear();
    this.remoteHealthBars.clear();
    
    // When returning to main menu, the leaveGame() method will be called
    // which will handle network cleanup, so we don't need to do it here
    
    console.log('MultiplayerGameScene shutdown complete');
  }
  
  // Create a new method to handle player joined events
  private handlePlayerJoined(data: PlayerJoinedEvent): void {
    console.log('Player joined:', data);
    
    // Convert the PlayerJoinedEvent to a PlayerState object with a name
    const playerState = this.ensurePlayerHasName({
      id: data.id,
      name: data.name || 'Player', // Use provided name or default
      x: data.x,
      y: data.y,
      rotation: data.rotation,
      health: data.health,
      organelles: data.organelles,
      resources: {
        lipids: 0,
        glucose: 0
      }
    });
    
    // Add remote cell
    this.addRemoteCell(playerState);
    
    // Create dramatic impulse effect for the new player
    this.createPlayerSpawnEffect(playerState.x, playerState.y);
  }
  
  // Add a debug visualization method to make it easier to see cells
  private createDebugVisuals(): void {
    // Create a background grid to help visualize the world bounds
    const grid = this.add.grid(
      0, 0,                                   // Position at world center
      this.worldSize, this.worldSize,         // Size to match world bounds
      100, 100,                               // Cell size
      0x0000ff, 0.1,                          // Main color (blue, very transparent)
      0x00ff00, 0.05                          // Alternative color (green, even more transparent)
    ).setDepth(10);                           // Set lower depth so cells appear above it
    
    // Create world boundary markers at the four corners
    const halfSize = this.worldSize / 2;
    const corners = [
      [-halfSize, -halfSize],  // Top left
      [halfSize, -halfSize],   // Top right
      [halfSize, halfSize],    // Bottom right
      [-halfSize, halfSize]    // Bottom left
    ];
    
    corners.forEach(([x, y]) => {
      this.add.circle(x, y, 50, 0xff0000, 0.5).setDepth(10);
    });
    
    // Add origin marker (0,0)
    this.add.circle(0, 0, 25, 0xffff00, 0.8).setDepth(10);
    
    console.log('Created debug visuals for world bounds visualization');
  }
  
  // Add a method to help identify all cells in scene
  private logAllCells(): void {
    console.log('=== All Cells in Scene ===');
    
    // Get UI camera for checking
    const uiCam = this.cameras.getCamera('uiCam');
    const mainCam = this.cameras.main;
    
    console.log(`Local player cell: ID ${this.networkService.getSocketId()}, position: (${this.playerCell.x}, ${this.playerCell.y}), depth: ${this.playerCell.depth}, visible: ${this.playerCell.visible}`);
    if (uiCam) {
      // Check if player cell is on UI camera (we need to check if it's NOT ignored)
      const isVisibleToUI = this.isVisibleToCamera(this.playerCell, uiCam);
      console.log(`  - Visible to UI camera: ${isVisibleToUI}`);
    }
    
    console.log(`Remote cells count: ${this.remoteCells.size}`);
    this.remoteCells.forEach((cell, id) => {
      console.log(`Remote cell ${id}: position: (${cell.x}, ${cell.y}), depth: ${cell.depth}, visible: ${cell.visible}`);
      if (uiCam) {
        // Check if the remote cell is on UI camera
        const isVisibleToUI = this.isVisibleToCamera(cell, uiCam);
        console.log(`  - Visible to UI camera: ${isVisibleToUI}`);
      }
    });
    
    // Check for any other Cell objects in the scene that might not be tracked
    let untracked = 0;
    this.children.list.forEach(obj => {
      if (obj instanceof Cell) {
        const isPlayer = obj === this.playerCell;
        const isRemote = Array.from(this.remoteCells.values()).includes(obj);
        
        if (!isPlayer && !isRemote) {
          untracked++;
          log.info(() => `UNTRACKED CELL: position: (${obj.x}, ${obj.y}), depth: ${obj.depth}, visible: ${obj.visible}`);
          // Check if it's visible to UI camera
          if (uiCam) {
            const isVisibleToUI = this.isVisibleToCamera(obj, uiCam);
            log.info(() => `  - Visible to UI camera: ${isVisibleToUI}`);
          }
          
          // Add an emergency fix - make the UI camera ignore this cell and set a clear visual indicator
          if (uiCam && this.isVisibleToCamera(obj, uiCam)) {
            log.warn(() => '  - FIXING: Making UI camera ignore this untracked cell');
            uiCam.ignore(obj);
            // Make it distinct for debugging
            try {
              // Try to set a distinct color for the membrane if possible
              if (obj.membrane) {
                // Use a workaround to set color since we don't know the exact membrane API
                (obj.membrane as any).setAlpha(0.8); // Make it partially transparent
                // We'll try to make it visually distinct without using setFillColor
                (obj.membrane as any).tint = 0xFF00FF; // Bright pink
              }
            } catch (e) {
              console.warn('Unable to modify untracked cell appearance:', e);
            }
          }
        }
      }
    });
    
    if (untracked > 0) {
      console.warn(`Found ${untracked} untracked Cell objects in the scene!`);
      
      // Emergency cleanup - remove any untracked cells
      this.cleanupUntrackedCells();
    }
    
    console.log('=== End Cell Report ===');
  }
  
  // Add helper method to check if an object is visible to a camera
  private isVisibleToCamera(gameObject: Phaser.GameObjects.GameObject, camera: Phaser.Cameras.Scene2D.Camera): boolean {
    // Objects aren't visible if they're specifically set to not be visible
    if (!(gameObject as any).visible) {
      return false;
    }
    
    // Get the object's position
    const x = (gameObject as any).x;
    const y = (gameObject as any).y;
    
    // If we can't determine position, consider it not visible
    if (x === undefined || y === undefined) {
      return false;
    }
    
    // Check if the position is within camera's view
    const bounds = camera.worldView;
    
    // Provide a buffer (e.g., 200 pixels) to consider objects just outside the view
    const buffer = 200;
    
    // Check if the object's position is within the camera's view plus buffer
    return (
      x >= bounds.x - buffer &&
      y >= bounds.y - buffer &&
      x <= bounds.x + bounds.width + buffer &&
      y <= bounds.y + bounds.height + buffer
    );
  }
  
  // Add a method to cleanup untracked cells
  private cleanupUntrackedCells(): void {
    console.log('Performing emergency cleanup of untracked cells');
    
    // Identify untracked cells
    const untrackedCells: Cell[] = [];
    this.children.list.forEach(obj => {
      if (obj instanceof Cell) {
        const isPlayer = obj === this.playerCell;
        const isRemote = Array.from(this.remoteCells.values()).includes(obj);
        
        if (!isPlayer && !isRemote) {
          untrackedCells.push(obj as Cell);
        }
      }
    });
    
    // Log cleanup
    console.log(`Cleaning up ${untrackedCells.length} untracked cells`);
    
    // Clean them up
    untrackedCells.forEach(cell => {
      try {
        // Make sure the UI camera ignores it first (prevention)
        const uiCam = this.cameras.getCamera('uiCam');
        if (uiCam) {
          uiCam.ignore(cell);
        }
        
        // Then destroy it
        cell.destroy();
        
        console.log(`Successfully destroyed untracked cell at (${cell.x}, ${cell.y})`);
      } catch (error) {
        console.error('Error destroying untracked cell:', error);
      }
    });
  }
  
  // Add the createPlayerCell method after the init method
  private createPlayerCell(x: number, y: number): void {
    console.log(`Creating player cell at position (${x}, ${y})`);
    
    // Don't create duplicates
    if (this.playerCellCreated) {
      console.warn('Attempted to create player cell when one already exists.');
      return;
    }
    
    // Create the player cell
    this.playerCell = new Cell(this, x, y, this.networkService.getSocketId());
    
    // Add nucleus to player cell
    this.addNucleusToPlayerCell();
    
    // Make sure initial resources are set correctly in the cell
    this.playerCell.setATP(this.atp);
    this.playerCell.setGlucose(this.glucose);
    
    // Mark as created
    this.playerCellCreated = true;
    
    // Player cell created event - useful for debugging
    log.info(() => 'Player cell created successfully');
    log.info(() => `Initial resources: ATP=${this.atp}, Glucose=${this.glucose}, Max ATP=${this.playerCell.getMaxATP()}`);
    
    // Center camera on player initially
    this.cameras.main.centerOn(x, y);
    
    // Use the player name from init instead of hardcoded "You"
    this.addNameAndHealthBar(this.playerCell, this.networkService.getSocketId(), this.playerName, 100);
    console.log(`Added name "${this.playerName}" to player cell`);
    
    // Create a dramatic spawn effect for the local player
    this.createPlayerSpawnEffect(x, y);
    
    // Initialize UI with starting values - force an immediate update after cell creation
    this.updateDOMUI(); // First update the DOM UI directly
    
    // Then update all resource displays
    this.updateResourceCounts({
      lipids: this.lipids,
      glucose: this.glucose,
      atp: this.atp,
      maxATP: this.playerCell.getMaxATP()
    });
  }
  
  // Add nucleus to player cell for multiplayer mode
  private addNucleusToPlayerCell(): void {
    // Check if player cell exists
    if (!this.playerCell) {
      console.error('Cannot add nucleus: Player cell does not exist');
      return;
    }
    
    // Create nucleus with a small delay to ensure the scene is fully initialized
    this.time.delayedCall(50, () => {
      try {
        // Create nucleus positioned at the center of the cell (0,0) in cell coordinates
        // This ensures the nucleus appears inside the cell and moves properly with it
        const nucleus = new Nucleus(this, 0, 0, {
          detailLevel: 'minimal', // Using minimal detail for player's cell in multiplayer
          radius: 38, // Reduced radius to match minimal style
          chromatinDensity: 0.5,
          wobbleAmount: 0.12,
          // Use the minimal style colors
          color: 0x4488FF, // Membrane blue color
          alpha: 0.5,
          outlineColor: 0x4488FF, // Explicitly match the membrane blue color
          id: `nucleus_${this.networkService.getSocketId()}`,
          movementDelay: 0.2 // Lower value ensures nucleus stays tightly bound to cell
        });
        
        // Immediately set the depth to ensure it's below the membrane
        nucleus.setDepth(80);
        
        // Add nucleus to the cell container - this makes its coordinates relative to the cell
        this.playerCell.addOrganelle(nucleus);
        
        console.log('Added nucleus to player cell in multiplayer mode (minimal style)');
      } catch (error) {
        console.error('Error adding nucleus to player cell:', error);
      }
    });
  }
  
  // Add debug command to help troubleshooting
  private addDebugCommands(): void {
    // Debug command to toggle server updates (D key)
    this.input.keyboard?.on('keydown-D', () => {
      this.serverUpdatesEnabled = !this.serverUpdatesEnabled;
      console.log(`Server updates ${this.serverUpdatesEnabled ? 'enabled' : 'disabled'}`);
      
      // Log current state
      console.log('Current state:');
      console.log(`- Player cell created: ${this.playerCellCreated}`);
      console.log(`- Remote cells count: ${this.remoteCells.size}`);
      console.log(`- Remote cells:`, Array.from(this.remoteCells.keys()));
    });
  }
  
  /**
   * Helper method to ensure a game object is ignored by the UI camera
   * @param gameObject The game object to ignore
   */
  private ensureIgnoredByUICamera(gameObject: Phaser.GameObjects.GameObject): void {
    // Add to game objects group
    this.gameObjectsGroup.add(gameObject);
    
    // Make UI camera ignore this object
    this.gameUI.setUICameraIgnore([gameObject]);
  }
  
  /**
   * Helper method to update the UI camera to ignore all current game objects
   * Can be called periodically if needed
   */
  private updateUICameraIgnoreList(): void {
    // Create a consolidated list of all game objects that should be ignored
    const gameObjectsToIgnore: Phaser.GameObjects.GameObject[] = [
      this.playerCell, 
      this.gradientBackground,
      ...Array.from(this.resources.values()),
      ...Array.from(this.toxins.values())
    ];
    
    // Add remote cells
    this.remoteCells.forEach(cell => {
      gameObjectsToIgnore.push(cell);
    });
    
    // Add network monitor stats text if it exists
    if (this.networkMonitor) {
      // The NetworkMonitor's main display is a text object that needs to be ignored
      // Since we can't directly access its statsText property (it's private),
      // we'll ensure the NetworkMonitor properly handles this internally
      this.networkMonitor.ensureProperRendering();
    }
    
    // Scan for movement markers and particle effects
    this.children.list.forEach(child => {
      // Check for movement markers
      if (child.getData && child.getData('type') === 'movementMarker') {
        gameObjectsToIgnore.push(child);
      }
      
      // Check for particle effects
      if (child instanceof Phaser.GameObjects.Particles.ParticleEmitter || 
          child.type === 'ParticleEmitterManager') {
        gameObjectsToIgnore.push(child);
      }
    });
    
    // Update UI camera ignore list
    this.gameUI.setUICameraIgnore(gameObjectsToIgnore);
    
    console.log(`Updated UI camera ignore list with ${gameObjectsToIgnore.length} objects`);
  }
  
  private createBackground(): void {
    console.log('Creating background for game scene');
    
    // Detect if this is a mobile browser
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    // Detect if WebGL is supported properly
    const hasWebGL = (this.renderer && this.renderer.type === Phaser.WEBGL);
    
    console.log(`Device info: Mobile=${isMobile}, WebGL=${hasWebGL}, UserAgent=${navigator.userAgent}`);
    
    try {
      // Try to use the shader-based approach first (fastest and best looking)
      const { ShaderGradientBackground } = require('../utils/ShaderGradientBackground');
      
      // Create the shader-based gradient
      this.gradientBackground = ShaderGradientBackground.createForScene(this, 0, 0);
      
      console.log(`Successfully created shader-based gradient background (type: ${this.gradientBackground.type})`);
      
      // Check if we're on Chrome/Android which might need special handling
      if (isMobile && /Chrome/i.test(navigator.userAgent)) {
        console.log('Chrome on Android detected - applying special rendering flags');
        
        // Force immediate render update to ensure shader initializes properly
        if (this.renderer instanceof Phaser.Renderer.WebGL.WebGLRenderer) {
          // Only WebGL renderer has this method
          (this.renderer as any).updateTexture(this.gradientBackground.texture);
        }
        
        // If we have a texture, force it to render at full resolution
        if (this.gradientBackground.texture) {
          this.gradientBackground.texture.setFilter(Phaser.Textures.LINEAR);
        }
      }
    } catch (error) {
      console.error('Error creating shader gradient background:', error);
      
      // Fallback to traditional texture-based gradient
      console.log('Falling back to traditional texture-based gradient');
      this.createTraditionalGradient();
    }
  }
  
  private createTraditionalGradient(): void {
    // Detect if this is a mobile browser
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    // Use the existing texture-based approach as fallback
    const sharedResources = SharedResources.getInstance();
    sharedResources.initialize(this);
    
    // Choose texture size based on device and available memory
    // Mobile devices need smaller textures to avoid memory issues
    const size = isMobile ? 1024 : 2048;
    
    // Create a high-quality texture-based gradient
    if (!this.textures.exists('gradientTexture')) {
      console.log(`Creating texture-based gradient (${size}x${size}) for ${isMobile ? 'mobile' : 'desktop'}`);
      
      // Create a graphics object for drawing
      const graphics = this.make.graphics({ x: 0, y: 0 });
      
      // Original rich blue gradient color stops
      const colors = [
        0x000011, // Almost black (outer edge)
        0x001133, // Deep space blue
        0x001a4d, // Deep blue
        0x002266, // Navy blue
        0x002a80, // Royal blue
        0x003399, // Medium blue
        0x0033cc, // Bright blue
        0x3377ff, // Sky blue
        0x88bbff, // Light blue
        0xaaddff, // Pale blue
        0xcceefd, // Very pale blue
        0xeeffff  // Almost white-blue center
      ];
      
      // Draw concentric circles with the colors
      for (let i = 0; i < colors.length; i++) {
        const radius = size/2 * (1 - i/(colors.length-1));
        graphics.fillStyle(colors[i]);
        graphics.fillCircle(size/2, size/2, radius);
      }
      
      // Generate the texture and clean up
      graphics.generateTexture('gradientTexture', size, size);
      graphics.destroy();
      
      console.log(`Created high-quality fallback gradient texture (${size}x${size})`);
    }
    
    // Create sprite with the texture
    if (this.textures.exists('gradientTexture')) {
      const sprite = this.add.sprite(0, 0, 'gradientTexture');
      sprite.setOrigin(0.5);
      sprite.setDepth(-100);
      sprite.name = 'textureGradientBackground';
      
      // Set texture filtering for better quality when scaled
      sprite.texture.setFilter(Phaser.Textures.LINEAR);
      
      // Scale it up enough to cover the view
      const scaleFactor = Math.max(this.scale.width, this.scale.height) / (isMobile ? 512 : 1024);
      sprite.setScale(scaleFactor * 2);
      
      console.log(`Created texture-based gradient background sprite (scale: ${sprite.scale})`);
      this.gradientBackground = sprite;
    } else {
      // Final fallback - solid color with simple gradient
      console.log('Unable to create texture - using graphics fallback');
      const graphics = this.add.graphics();
      
      // Create a simpler gradient with graphics
      graphics.fillGradientStyle(
        0x001a4d, 0x001a4d, 0x0033cc, 0x0033cc, 1
      );
      graphics.fillRect(-10000, -10000, 20000, 20000);
      graphics.setDepth(-99);
      
      // Store reference so it can be cleaned up later
      this.gradientBackground = graphics as any;
    }
  }
  
  // Add a new method to create and add a name text and health bar to a cell
  private addNameAndHealthBar(cell: Cell, playerId: string, playerName: string, health: number): void {
    // Get the membrane's radius to position elements below the membrane
    const membraneRadius = cell.membrane.baseRadius;
    
    // Create name text positioned just below the cell membrane
    const nameText = this.add.text(0, membraneRadius + 10, playerName, {
      fontFamily: 'Arial',
      fontSize: '16px',
      color: '#ffffff',
      stroke: '#000000',
      strokeThickness: 3,
      align: 'center'
    }).setOrigin(0.5, 0);
    
    // Create health bar background, positioned below the name text
    const healthBar = this.add.graphics();
    healthBar.setPosition(0, membraneRadius + 35); // 25px below the name text's position
    
    // Update the health bar
    this.updateHealthBar(healthBar, health);
    
    // Add to appropriate maps based on whether it's the player cell or remote cell
    if (playerId === this.networkService.getSocketId()) {
      if (this.playerNameText) this.playerNameText.destroy();
      if (this.playerHealthBar) this.playerHealthBar.destroy();
      
      this.playerNameText = nameText;
      this.playerHealthBar = healthBar;
    } else {
      // Store references to remote cell UI elements
      if (this.remoteNameTexts.has(playerId)) {
        this.remoteNameTexts.get(playerId)?.destroy();
      }
      if (this.remoteHealthBars.has(playerId)) {
        this.remoteHealthBars.get(playerId)?.destroy();
      }
      
      this.remoteNameTexts.set(playerId, nameText);
      this.remoteHealthBars.set(playerId, healthBar);
    }
    
    // Make sure these elements follow the cell
    cell.add([nameText, healthBar]);
    
    // Ensure they're ignored by the UI camera
    this.ensureIgnoredByUICamera(nameText);
    this.ensureIgnoredByUICamera(healthBar);
  }
  
  // Add a method to update the health bar near each player
  // this is not for the big health bar on the top left of the screen
  private updateHealthBar(healthBar: Phaser.GameObjects.Graphics, health: number): void {
    const width = 60;
    const height = 8;
    const x = -width / 2; // Center the health bar horizontally
    const y = 0; // Position is now relative to the graphics object
    
    healthBar.clear();
    
    // Background (gray)
    healthBar.fillStyle(0x333333, 1);
    healthBar.fillRect(x, y, width, height);
    
    // Calculate health width
    const healthWidth = Math.max(0, Math.min(width * (health / 100), width));
    
    // Health color based on amount
    let color = 0x00ff00; // Green
    if (health < 60) color = 0xffff00; // Yellow
    if (health < 30) color = 0xff0000; // Red
    
    // Health bar (colored)
    healthBar.fillStyle(color, 1);
    healthBar.fillRect(x, y, healthWidth, height);
    
    // Border
    healthBar.lineStyle(1, 0x000000, 1);
    healthBar.strokeRect(x, y, width, height);
  }
  
  // Add this new method to add organelles to cells
  private addOrganelleToCell(cell: Cell, organelleState: OrganelleState): void {
    log.info(() => `Adding organelle to cell: ${organelleState}`);
    
    let organelle: Organelle | null = null;
    
    switch (organelleState.type) {
      case 'nucleus':
        // Always use minimal detail for all nuclei in multiplayer
        const detailLevel = 'minimal';
        
        // Define colors - use cell membrane blue color for minimal detail
        const membraneBlue = 0x4488FF; // Same blue color as the default cell membrane
        
        // Create the nucleus at (0,0) position relative to the cell's center
        organelle = new Nucleus(this, 0, 0, {
          detailLevel: detailLevel,
          // Use minimal style colors
          color: membraneBlue, // Membrane blue for fill color
          alpha: 0.5,
          outlineColor: membraneBlue, // Membrane blue for outline to match
          // Smaller radius for the minimal style
          radius: 35,
          movementDelay: 0.2, // Lower value ensures nucleus stays tightly bound to cell
          id: organelleState.id
        });
        break;
        
      case 'mitochondrion':
        // Create mitochondrion with appropriate detail level
        const isMitoInPlayerCell = cell === this.playerCell;
        const mitoDetailLevel = 'minimal'; // Always use minimal for multiplayer
        
        // Calculate initial position - place further from nucleus to not touch it
        // Using a minimum radius of 65-85 ensures mitochondria stay well outside nucleus region
        const initialRadius = 70 + (Math.random() * 15); // Between 70-85px
        
        // Calculate a position with the appropriate angle
        // Place at random angles for visual distribution
        const initialAngle = Math.random() * Math.PI * 2;
        const mitoX = Math.cos(initialAngle) * initialRadius;
        const mitoY = Math.sin(initialAngle) * initialRadius;
        
        organelle = new Mitochondrion(this, mitoX, mitoY, {
          detailLevel: mitoDetailLevel,
          size: 20, // Fixed small size
          cristaeCount: 4, // Fewer details for minimal
          energyProductionRate: 5,
          // Use the cell blue color
          color: 0x4488FF,
          alpha: 0.8, // Slightly transparent
          outline: true,
          outlineColor: 0x3377DD,
          movementDelay: 0.9, // Slightly reduced from 0.95 to allow some subtle movement
          wobbleAmount: 0.05, // Very slight wobble for subtle movement
          maintainPosition: true, // Explicitly ensure position is maintained
          pulsate: true, // Enable pulsation for visual interest
          id: organelleState.id
        });
        break;
        
      case 'lysosome':
        // Create lysosome with proper configuration for cellular positioning
        // Position it at a random position in the intermediate zone, similar to mitochondria
        // This ensures lysosomes are distributed around the nucleus rather than all on one side
        // The intermediate zone is 52-75px from center (defined in Cell.ts)
        
        // Calculate initial position with random distance in the intermediate zone
        const initialLysoRadius = 52 + (Math.random() * 23); // Between 52-75px (intermediate zone)
        
        // Calculate a position with a random angle for proper distribution
        const initialLysoAngle = Math.random() * Math.PI * 2;
        const lysoX = Math.cos(initialLysoAngle) * initialLysoRadius;
        const lysoY = Math.sin(initialLysoAngle) * initialLysoRadius;
        
        // Check if this position might be too close to existing organelles
        // This is a simplified version of the check done in Cell.ts
        let isValidPosition = true;
        if (cell.getOrganelles) {
          const existingOrganelles = cell.getOrganelles();
          const minDistance = 20; // Minimum distance between organelles
          
          // Simple overlap check
          for (const existingOrg of existingOrganelles) {
            // Skip very small organelles like vesicles
            if (existingOrg.getType() === 'vesicle') continue;
            
            // Calculate distance between proposed position and existing organelle
            const dx = lysoX - existingOrg.x;
            const dy = lysoY - existingOrg.y;
            const distanceSquared = dx * dx + dy * dy;
            
            // If too close, mark as invalid
            if (distanceSquared < minDistance * minDistance) {
              isValidPosition = false;
              break;
            }
          }
        }
        
        // If the position is invalid, generate another one (max 5 attempts)
        let finalLysoX = lysoX;
        let finalLysoY = lysoY;
        
        if (!isValidPosition) {
          // Try up to 5 more positions
          for (let attempt = 0; attempt < 5; attempt++) {
            // Generate a new random position
            const newRadius = 52 + (Math.random() * 23);
            const newAngle = Math.random() * Math.PI * 2;
            const newX = Math.cos(newAngle) * newRadius;
            const newY = Math.sin(newAngle) * newRadius;
            
            // Check this position
            let positionOK = true;
            if (cell.getOrganelles) {
              const existingOrganelles = cell.getOrganelles();
              const minDistance = 20;
              
              for (const existingOrg of existingOrganelles) {
                if (existingOrg.getType() === 'vesicle') continue;
                
                const dx = newX - existingOrg.x;
                const dy = newY - existingOrg.y;
                const distanceSquared = dx * dx + dy * dy;
                
                if (distanceSquared < minDistance * minDistance) {
                  positionOK = false;
                  break;
                }
              }
            }
            
            // If good position found, use it
            if (positionOK) {
              finalLysoX = newX;
              finalLysoY = newY;
              isValidPosition = true;
              break;
            }
          }
        }
        
        log.info(() => `Positioning lysosome at (${finalLysoX.toFixed(2)}, ${finalLysoY.toFixed(2)}), valid position: ${isValidPosition}`);
        
        organelle = new Lysosome(this, finalLysoX, finalLysoY, {
          detailLevel: 'minimal',
          size: 15, // Small size for minimalist style
          digestiveEnzymes: 5,
          // Don't apply tint to preserve original colors
          color: 0xFFFFFF, // White/no tint
          alpha: 0.9,
          outline: false, // No outline to preserve original appearance
          movementDelay: 0.8,
          wobbleAmount: 0.1,
          maintainPosition: true,
          pulsate: true,
          id: organelleState.id
        });
        break;
    }

    if (organelle) {
      cell.addOrganelle(organelle);
    }
  }
  
  // Add this helper method to ensure player states have names
  private ensurePlayerHasName(playerState: any, defaultName: string = 'Player'): PlayerState {
    if (!playerState.name) {
      // Create a new object with all the properties from the original player state plus a name
      return {
        ...playerState,
        name: defaultName
      };
    }
    return playerState;
  }
  
  /**
   * Creates a dramatic expanding circle effect when a new player spawns
   * This gives a visual cue to all players that someone new has joined the game
   */
  private createPlayerSpawnEffect(x: number, y: number): void {
    // Create multiple expanding circles for a more dramatic effect
    const colors = [0x4488ff, 0x33aaff, 0x22ccff]; // Different blue shades
    const maxRadius = 300; // Maximum radius for the largest circle

    // Create the circles with varying properties
    colors.forEach((color, index) => {
      // Create a circle with initial small radius
      const circle = this.add.circle(x, y, 10, color, 0.8 - (index * 0.2));
      circle.setDepth(90); // Below cells (which are at depth 100) but above background
      
      // Make sure the UI camera ignores this effect
      this.ensureIgnoredByUICamera(circle);
      
      // Add expanding animation
      this.tweens.add({
        targets: circle,
        radius: maxRadius - (index * 50), // Stagger sizes
        alpha: 0,
        duration: 1200 + (index * 300), // Stagger durations
        ease: 'Sine.easeOut',
        delay: index * 150, // Stagger start times
        onComplete: () => {
          circle.destroy(); // Clean up when animation is done
        }
      });
    });
    
    // Add a quick pulse effect on the cell position
    const pulseCircle = this.add.circle(x, y, 80, 0xffffff, 0.6);
    pulseCircle.setDepth(110); // Slightly above cells for brief moment
    
    // Make sure the UI camera ignores this effect
    this.ensureIgnoredByUICamera(pulseCircle);
    
    // Add pulse animation
    this.tweens.add({
      targets: pulseCircle,
      radius: 120,
      alpha: 0,
      duration: 400,
      ease: 'Quad.easeOut',
      onComplete: () => {
        pulseCircle.destroy(); // Clean up when animation is done
      }
    });
    
    // Add subtle camera shake if the effect is happening within camera view
    const isInView = Math.abs(x - this.cameras.main.scrollX) < this.cameras.main.width &&
                     Math.abs(y - this.cameras.main.scrollY) < this.cameras.main.height;
    
    if (isInView) {
      this.cameras.main.shake(300, 0.003); // Gentle shake
    }
  }
  
  /**
   * Create a death effect at a cell's position with a sci-fi disintegration look
   * instead of a bloody appearance, including resource ejection particles
   */
  private createDeathEffect(cell: Cell): void {
    const x = cell.x;
    const y = cell.y;
    
    console.log(`Creating death effect at (${x}, ${y})`);
    
    // Create multiple expanding circles with sci-fi cyan/blue/purple colors
    const colors = [0x00ccff, 0x4477ff, 0x9966ff, 0x8844ff]; // Cyan, blue, purple colors
    
    // Initial flash effect that fades quickly
    const flash = this.add.circle(x, y, 100, 0xffffff, 0.8);
    flash.setDepth(85);
    this.ensureIgnoredByUICamera(flash);
    this.tweens.add({
      targets: flash,
      alpha: 0,
      scale: 1.5,
      duration: 300,
      ease: 'Power2',
      onComplete: () => {
        flash.destroy();
      }
    });
    
    // Create expanding circles with staggered timing
    for (let i = 0; i < 5; i++) {
      this.time.delayedCall(i * 80, () => {
        const circle = this.add.circle(x, y, 10, colors[i % colors.length], 0.7);
        circle.setDepth(90 + i);
        this.ensureIgnoredByUICamera(circle);
        
        // Expand circle with varying sizes
        this.tweens.add({
          targets: circle,
          radius: 150 + i * 30,
          alpha: 0,
          duration: 600 + i * 100,
          ease: 'Power2',
          onComplete: () => {
            circle.destroy();
          }
        });
      });
    }
    
    // Create an initial burst effect
    const burstCircle = this.add.circle(x, y, 5, 0xffffff, 0.8);
    burstCircle.setDepth(95);
    this.ensureIgnoredByUICamera(burstCircle);
    
    this.tweens.add({
      targets: burstCircle,
      radius: 100,
      alpha: 0,
      duration: 300,
      ease: 'Cubic.Out',
      onComplete: () => {
        burstCircle.destroy();
      }
    });
    
    // Add particles with sci-fi color palette
    const particles = this.add.particles(x, y, 'toxin', {
      speed: { min: 200, max: 400 },
      scale: { start: 1.0, end: 0 },
      blendMode: 'ADD',
      lifespan: 600, // Shorter lifespan to avoid lingering particles
      quantity: 40,
      tint: 0x4499ff, // Blue color
      angle: { min: 0, max: 360 }
    });
    particles.setDepth(80);
    this.ensureIgnoredByUICamera(particles);
    
    // Add second particle effect with different color
    const particles2 = this.add.particles(x, y, 'toxin', {
      speed: { min: 150, max: 350 },
      scale: { start: 0.8, end: 0 },
      blendMode: 'ADD',
      lifespan: 500, // Shorter lifespan
      quantity: 25,
      tint: 0x00ccff, // Cyan color
      angle: { min: 0, max: 360 }
    });
    particles2.setDepth(79);
    this.ensureIgnoredByUICamera(particles2);
    
    // Create main ejection particles
    this.createResourceEjectionParticles(x, y);
    
    // Auto-destroy particles after animation completes
    this.time.delayedCall(800, () => {
      particles.destroy();
      particles2.destroy();
    });
    
    // Camera shake if within view
    if (this.cameras.main.worldView.contains(x, y)) {
      this.cameras.main.shake(400, 0.015);
    }
  }

  /**
   * Creates particles simulating resources ejecting from a dying cell
   */
  private createResourceEjectionParticles(x: number, y: number): void {
    log.info(() => `Creating resource ejection particles at (${x}, ${y})`);
    
    // Create lipid-like particles (blue)
    const lipidParticles = this.add.particles(x, y, 'lipid', {
      speed: { min: 200, max: 450 },
      scale: { start: 1.0, end: 0.2 },
      alpha: { start: 0.9, end: 0 },
      lifespan: 1500, // Reduced lifespan
      quantity: 25,
      blendMode: 'ADD',
      emitting: false
    });
    lipidParticles.setDepth(70);
    this.ensureIgnoredByUICamera(lipidParticles);
    
    // Create glucose-like particles (orange) using the PNG image texture
    const glucoseParticles = this.add.particles(x, y, SharedResources.GLUCOSE_TEXTURE_KEY, {
      speed: { min: 200, max: 450 },
      scale: { start: 0.15, end: 0.03 },
      alpha: { start: 0.9, end: 0 },
      lifespan: 1500, // Reduced lifespan
      quantity: 25,
      blendMode: 'ADD',
      emitting: false
    });
    glucoseParticles.setDepth(71);
    this.ensureIgnoredByUICamera(glucoseParticles);
    
    // Emit particles in multiple bursts for an extended effect
    // First burst - largest
    lipidParticles.explode(25, x, y);
    glucoseParticles.explode(25, x, y);
    
    // Second burst after delay
    this.time.delayedCall(150, () => {
      lipidParticles.explode(15, x, y);
      glucoseParticles.explode(15, x, y);
    });
    
    // Clean up after animation completes
    this.time.delayedCall(1800, () => {
      lipidParticles.destroy();
      glucoseParticles.destroy();
    });
  }
  
  /**
   * Handles a socket connection when joining an existing room
   * @param data The room state data
   */
  private handleJoinRoom(data: RoomStateEvent): void {
    console.log('Joined room, processing initial state:', data);
    
    // Set processing flag to prevent concurrent updates
    if (this.isProcessingUpdate) {
      console.log('Already processing an update, deferring room state');
      return;
    }
    
    this.isProcessingUpdate = true;
    
    try {
      // Find our player data in the room state
      const ourPlayer = data.players.find(p => p.id === this.networkService.getSocketId());
      
      if (ourPlayer) {
        console.log('Found our player data, creating player cell');
        
        // Set membrane stage if specified
        if (ourPlayer.membraneStage && ourPlayer.membraneStage > 1) {
          // Find the stage configuration
          const stageConfig = MEMBRANE_EXPANSION_STAGES.find((s) => s.stage === ourPlayer.membraneStage);
          if (stageConfig) {
            // Apply membrane stage and update UI
            this.playerCell.setMembraneStage(
              ourPlayer.membraneStage,
              stageConfig.sizeMultiplier,
              stageConfig.speedMultiplier
            );
            
            // Update the UI to reflect the current stage
            this.createOrganelleButtons();
          }
        }
        
        // Rest of player initialization code...
      }
      
      // The welcome modal is now shown in the onRoomState handler
      // We've removed the duplicate call here to prevent showing it twice
      
      // Rest of room initialization code...
    } finally {
      this.isProcessingUpdate = false;
    }
  }

  /**
   * Shows a welcome modal with game instructions
   */
  private showWelcomeModal(): void {
    // Detailed logging
    if (!this.domUI) {
      console.log('⚠️ Cannot show welcome modal: domUI is not initialized');
      return;
    }
    
    if (this.welcomeModalShown) {
      console.log('ℹ️ Welcome modal already shown, skipping');
      return;
    }
    
    console.log('🎉 Showing welcome modal...');
    
    // Set flag to prevent showing it multiple times
    this.welcomeModalShown = true;
    
    try {
      // Show the welcome modal
      this.domUI.showWelcomeModal(() => {
        // Optional callback when modal is closed
        console.log('✅ Welcome modal closed by user');
      });
    } catch (error) {
      console.error('❌ Error showing welcome modal:', error);
      // Reset the flag if showing fails, so we can try again
      this.welcomeModalShown = false;
    }
  }
  
  /**
   * Adapts detail levels based on current performance
   */
  private adaptDetailBasedOnPerformance(): void {
    if (this.settingsManager && this.fpsMonitor) {
      // Check and fix FPS display visibility
      const settings = this.settingsManager.getSettings();
      if (settings.showFps !== this.fpsMonitor.isVisible()) {
        console.log(`Fixing FPS display visibility: settings=${settings.showFps}, current=${this.fpsMonitor.isVisible()}`);
        this.fpsMonitor.toggleDisplay(settings.showFps);
      }
      
      // Periodically log all cells (infrequently)
      if (this.serverUpdatesEnabled && Math.random() < 0.01) {
        this.logAllCells();
      }
      
      // Periodically check for and clean up untracked cells
      if (this.serverUpdatesEnabled && Math.random() < 0.005) {
        this.cleanupUntrackedCells();
      }
      
      // Update the positions of remote cell names and health bars
      this.remoteCells.forEach((cell, playerId) => {
        // Update health bar with current health value
        const healthBar = this.remoteHealthBars.get(playerId);
        if (healthBar) {
          this.updateHealthBar(healthBar, cell.getHealth());
        }
      });
    }
  }
  
  /**
   * Setup a listener to replace any fallback glucose sprites when the texture loads
   */
  private setupGlucoseTextureListener(): void {
    const sharedResources = SharedResources.getInstance();
    
    // Add a listener for the glucose texture loading
    this.load.on('filecomplete', (key: string) => {
      if (key === SharedResources.GLUCOSE_TEXTURE_KEY) {
        console.log('Glucose texture loaded, replacing fallback sprites');
        
        // Replace any existing glucose resources with proper sprites
        this.resources.forEach((resource, id) => {
          if (resource.getData('type') === 'glucose') {
            const x = resource.x;
            const y = resource.y;
            const value = resource.getData('value');
            
            // Create new sprite with the proper texture
            const newResource = sharedResources.createGlucoseSprite(this, x, y);
            newResource.setData('id', id);
            newResource.setData('type', 'glucose');
            newResource.setData('value', value);
            
            // Set value-based scale using same scale calculation as in addResource
            // First get the scale value using the regular base size (16px)
            // Then divide by 3 to get the final 1/3 scale we want
            const calcScale = (val: number, base: number) => {
              // Define min and max sizes
              const minSize = 16;
              const maxSize = 22;
              
              // Calculate size based on value using log scale
              let size;
              if (val <= 1) {
                size = minSize;
              } else {
                const logFactor = Math.log(val + 2) / Math.log(10);
                size = minSize + (maxSize - minSize) * Math.min(logFactor / 1.5, 1);
              }
              
              // Convert to scale factor based on base size
              return size / base;
            };
            
            // Calculate value-based scale and reduce to 1/3
            const valueScale = calcScale(value, 16) / 3;
            newResource.setScale(valueScale);
            
            // Set up interaction
            newResource.setInteractive({ useHandCursor: true });
            newResource.on('pointerover', () => {
              newResource.setScale(newResource.scale * 1.1);
            });
            newResource.on('pointerout', () => {
              newResource.setScale(newResource.scale / 1.1);
            });
            
            // Replace in the collection
            resource.destroy();
            this.resources.set(id, newResource);
            
            // Ensure it's ignored by the UI camera
            this.ensureIgnoredByUICamera(newResource);
            
          }
        });
      }
    });
  }

  /**
   * Creates a portal object with pulsating, animated circles to create a black hole effect
   * @param x X-coordinate for the portal
   * @param y Y-coordinate for the portal
   */
  private createPortal(x: number, y: number): void {
    try {
      // Create a container for all portal elements
      const container = this.add.container(x, y);
      container.setDepth(95); // Between background and cells
      
      // Define portal colors - dark purples and blues for black hole effect
      const outerColors = [
        0x330066, // Dark purple
        0x220077, // Deep blue-purple
        0x110088  // Deep blue
      ];
      
      // Create outer pulsating circles
      const outerCircles: Phaser.GameObjects.Arc[] = [];
      
      // Create multiple circles with different sizes and animation patterns
      for (let i = 0; i < outerColors.length; i++) {
        const initialRadius = this.portalRadius - (i * 5);
        const circle = this.add.circle(0, 0, initialRadius, outerColors[i], 0.7 - (i * 0.15));
        
        // Add to container and track
        container.add(circle);
        outerCircles.push(circle);
        
        // Add pulsating/expanding animation with different timings per circle
        this.tweens.add({
          targets: circle,
          radius: initialRadius + 15 + (i * 8), // Different expansion for each circle
          alpha: { from: 0.7 - (i * 0.15), to: 0.4 - (i * 0.1) },
          duration: 1500 + (i * 300), // Staggered timings
          ease: 'Sine.InOut',
          yoyo: true,
          repeat: -1, // Loop forever
          delay: i * 400 // Staggered start times
        });
      }
      
      // Create inner core of the portal (black hole center)
      const innerCircle = this.add.circle(0, 0, this.portalRadius * 0.6, 0x000000, 0.9);
      
      // Create slight glow at the center
      const centerGlow = this.add.circle(0, 0, this.portalRadius * 0.4, 0x6600ff, 0.4);
      
      // Add pulsating animation for the core
      this.tweens.add({
        targets: innerCircle,
        radius: { from: this.portalRadius * 0.6, to: this.portalRadius * 0.5 },
        duration: 2000,
        ease: 'Sine.InOut',
        yoyo: true, 
        repeat: -1
      });
      
      // Add pulsating animation for the glow
      this.tweens.add({
        targets: centerGlow,
        radius: { from: this.portalRadius * 0.4, to: this.portalRadius * 0.3 },
        alpha: { from: 0.4, to: 0.6 },
        duration: 1500,
        ease: 'Sine.InOut',
        yoyo: true,
        repeat: -1
      });
      
      // Add "vibeverse" text to the portal
      const textStyle = {
        fontFamily: 'Arial',
        fontSize: '20px',
        color: '#ffffff',
        align: 'center'
      };
      
      // Create the text with semi-transparency
      const portalText = this.add.text(0, 0, "vibeverse", textStyle);
      portalText.setOrigin(0.5, 0.5); // Center the text
      portalText.setAlpha(0.5); // Make it semi-transparent
      
      // Add a subtle glow effect to the text
      portalText.setShadow(0, 0, '#8844ff', 8, true, true);
      
      // Add a pulsating animation to the text
      this.tweens.add({
        targets: portalText,
        alpha: { from: 0.5, to: 0.7 },
        scale: { from: 0.95, to: 1.05 },
        duration: 2500,
        ease: 'Sine.InOut',
        yoyo: true,
        repeat: -1
      });
      
      // Add all elements to container
      container.add(innerCircle);
      container.add(centerGlow);
      container.add(portalText);
      
      // Initialize animation timers for each circle - with error safety
      this.portalAnimationTimers = Array(outerColors.length).fill(0).map(() => Math.random() * Math.PI * 2);
      
      // Store portal data
      this.portal = {
        container,
        outerCircles,
        innerCircle,
        position: { x, y },
        active: true
      };
      
      // Make sure UI camera ignores the portal
      this.ensureIgnoredByUICamera(container);
      
      // Log portal creation
      console.log(`Created portal at position (${x}, ${y})`);
      
      // Create initial spawn effect
      this.createPortalSpawnEffect(x, y);
    } catch (error) {
      console.error('Error creating portal:', error);
      // Reset portal data if creation failed
      this.portal = null;
    }
  }

  /**
   * Creates a dramatic effect when the portal first appears
   */
  private createPortalSpawnEffect(x: number, y: number): void {
    try {
      // Create expanding rings when portal spawns
      const colors = [0x6600cc, 0x4400aa, 0x330088];
      const maxRadius = this.portalRadius * 3; // Larger than the portal itself
      
      // Create multiple expanding circles
      colors.forEach((color, index) => {
        const circle = this.add.circle(x, y, 5, color, 0.8 - (index * 0.2));
        circle.setDepth(96); // Above the portal but below cells
        
        // Ensure UI camera ignores this effect
        this.ensureIgnoredByUICamera(circle);
        
        // Add expanding animation
        this.tweens.add({
          targets: circle,
          radius: maxRadius - (index * 20),
          alpha: 0,
          duration: 1800 + (index * 200),
          ease: 'Sine.easeOut',
          delay: index * 150,
          onComplete: () => {
            circle.destroy();
          }
        });
      });
      
      // Add flash effect
      const flash = this.add.circle(x, y, this.portalRadius, 0xffffff, 0.7);
      this.ensureIgnoredByUICamera(flash);
      flash.setDepth(96);
      
      this.tweens.add({
        targets: flash,
        radius: this.portalRadius * 1.5,
        alpha: 0,
        duration: 800,
        ease: 'Cubic.easeOut',
        onComplete: () => {
          flash.destroy();
        }
      });
      
      // Only add camera shake if within view and not at game start
      if (this.cameras.main.worldView.contains(x, y) && this.playerCellCreated) {
        this.cameras.main.shake(400, 0.005);
      }
    } catch (error) {
      console.error('Error creating portal spawn effect:', error);
      // Continue even if spawn effect fails
    }
  }

  /**
   * Updates the portal animation in the update loop
   */
  private updatePortal(delta: number): void {
    try {
      if (!this.portal || !this.portal.active) return;
      
      // Update animation timers
      for (let i = 0; i < this.portalAnimationTimers.length; i++) {
        // Convert delta from ms to seconds and scale for appropriate speed
        this.portalAnimationTimers[i] += (delta / 1000) * (0.5 + (i * 0.2));
      }
    } catch (error) {
      console.error('Error updating portal:', error);
      // Disable portal updates if there's an error
      if (this.portal) {
        this.portal.active = false;
      }
    }
  }
  
  /**
   * Handles player collision with the portal, creates a particle effect, and redirects to the URL
   */
  private handlePortalCollision(): void {
    // Only proceed if we haven't already triggered the portal effect
    if (this.portal && this.portal.active) {
      // Mark portal as transitioning instead of just deactivating it
      this.portal.isTransitioning = true;
      this.portal.active = false;
      
      // Disable player controls during transition
      this.playerControlsEnabled = false;
      
      // Create a nice blue color for the URL
      const blueColor = '3366FF';
      
      // Get the player name (or use 'Player' as default)
      const playerName = this.playerName || 'Player';
      
      // Generate the destination URL with the player's name and color
      const destinationUrl = `https://portal.pieter.com/?username=${encodeURIComponent(playerName)}&color=${blueColor}&speed=5&ref=cytopod.com`;
      
      console.log(`Portal activated! Redirecting to: ${destinationUrl} in 2 seconds`);
      
      // Create a particle effect of flying light particles
      this.createPortalTransitionEffect();
      
      // Redirect after 2 seconds
      this.time.delayedCall(2000, () => {
        // Open the URL in a new window/tab
        window.open(destinationUrl, '_blank');
      });
    }
  }
  
  /**
   * Creates a subtle particle effect when approaching the portal
   */
  private createPortalApproachEffect(): void {
    if (!this.portal || !this.playerCell) return;
    
    // Get portal position
    const portalPos = this.portal.position;
    
    // Create a subtle orbiting effect with multiple emitters at different distances
    const orbitEmitters: Phaser.GameObjects.Particles.ParticleEmitter[] = [];
    
    // Create 3 orbit rings at different distances and speeds
    for (let i = 0; i < 3; i++) {
      const radius = this.portalRadius * (1 + (i * 0.5));
      const orbitSpeed = 100 - (i * 20); // Outer rings move slower
      const direction = i % 2 === 0 ? 1 : -1; // Alternate directions
      
      // Use let instead of const for angle so we can modify it
      let currentAngle = Math.random() * Math.PI * 2; // Random starting angle
      
      // Create emitter for this orbit ring
      const emitter = this.add.particles(portalPos.x, portalPos.y, 'toxin', {
        lifespan: 3000,
        scale: { start: 0.1, end: 0.3 },
        quantity: 1,
        frequency: 100 + (i * 50),
        tint: [0x3366FF, 0x00CCFF, 0x66FFFF, 0xFFFFFF],
        alpha: { start: 0.4, end: 0 },
        blendMode: 'ADD',
        // Custom position function for circular movement
        x: () => {
          return portalPos.x + Math.cos(currentAngle) * radius;
        },
        y: () => {
          return portalPos.y + Math.sin(currentAngle) * radius;
        },
        // Set velocities tangent to the circle
        speedX: {
          onEmit: () => {
            // Update angle for next particle (produces orbital movement)
            currentAngle += 0.1 * direction;
            // Return tangential velocity X
            return Math.cos(currentAngle + (Math.PI / 2 * direction)) * orbitSpeed;
          }
        },
        speedY: {
          onEmit: () => {
            // Return tangential velocity Y
            return Math.sin(currentAngle + (Math.PI / 2 * direction)) * orbitSpeed;
          }
        }
      });
      
      emitter.particleBringToTop = true;
      
      // Store for cleanup
      orbitEmitters.push(emitter);
      
      // Ensure UI camera ignores the emitter
      this.ensureIgnoredByUICamera(emitter);
    }
    
    // Add some additional particles that flow toward the portal - fix type issues
    const inflowEmitter = this.add.particles(portalPos.x, portalPos.y, 'toxin', {
      x: { min: -500, max: 500 },
      y: { min: -500, max: 500 },
      lifespan: 2000,
      scale: { start: 0.05, end: 0.2 },
      quantity: 1,
      frequency: 150,
      tint: [0x3366FF, 0x00CCFF, 0x66FFFF],
      alpha: { start: 0.2, end: 0.6 },
      blendMode: 'ADD',
      // Use simpler fixed value instead of callback to avoid type issues
      speed: { min: 50, max: 80 }
    });
    
    // Create a timer to periodically create directed particles flowing toward the portal
    const createDirectedParticles = () => {
      // Safety check
      if (!this.portal || !this.portal.active) return;
      
      // Create multiple particles around the portal area that will flow inward
      for (let i = 0; i < (this.portal.particlesPerBurst || 5); i++) {
        // Random position in a wide area around the portal
        const angle = Math.random() * Math.PI * 2;
        const distance = 300 + Math.random() * 200;
        const x = portalPos.x + Math.cos(angle) * distance;
        const y = portalPos.y + Math.sin(angle) * distance;
        
        // Create a particle at this position
        const particle = inflowEmitter.emitParticle(1, x, y);
        
        // If particle was created successfully
        if (particle) {
          // Calculate angle to portal
          const angleToPortal = Math.atan2(portalPos.y - y, portalPos.x - x);
          
          // Set velocity toward the portal with increased variation based on proximity
          const distanceToPortal = Phaser.Math.Distance.Between(x, y, portalPos.x, portalPos.y);
          const speedFactor = Math.max(0.8, 1 - (distanceToPortal / 600));
          const particleSpeed = (50 + Math.random() * 30) * speedFactor;
          const angleVariation = (Math.random() - 0.5) * 0.2;
          
          particle.velocityX = Math.cos(angleToPortal + angleVariation) * particleSpeed;
          particle.velocityY = Math.sin(angleToPortal + angleVariation) * particleSpeed;
        }
      }
    };
    
    // Set up a timer to create particles
    const inflowParticleTimer = this.time.addEvent({
      delay: 200,
      callback: createDirectedParticles,
      callbackScope: this,
      loop: true
    });
    
    // Store the timer event for dynamic adjustment
    this.portal.inflowTimerEvent = inflowParticleTimer;
    
    // Set initial particles per burst value
    this.portal.particlesPerBurst = 5;
    
    // Ensure UI camera ignores the emitter
    this.ensureIgnoredByUICamera(inflowEmitter);
    
    // Store emitters for cleanup
    this.portal.approachEmitter = orbitEmitters[0]; // Store the first one as the main one
    this.portal.inflowEmitter = inflowEmitter;
    this.portal.orbitEmitters = orbitEmitters;
    
    // Update cleanup function to handle the timer
    const originalCleanupFunction = this.portal.orbitCleanupFunction;
    this.portal.orbitCleanupFunction = () => {
      // Call original cleanup if it exists
      if (originalCleanupFunction) originalCleanupFunction();
      
      // Remove the timer
      if (inflowParticleTimer) inflowParticleTimer.remove();
    };
  }
  
  /**
   * Creates a particle effect for the portal transition
   */
  private createPortalTransitionEffect(): void {
    if (!this.portal || !this.playerCell) return;
    
    // Get portal position
    const portalPos = this.portal.position;
    
    // Create particles around the player and heading toward the portal
    const emitter = this.add.particles(0, 0, 'toxin', {
      x: this.playerCell.x,
      y: this.playerCell.y,
      lifespan: 2000,
      scale: { start: 0.4, end: 0.1 },
      speed: { min: 50, max: 150 },
      quantity: 3,
      frequency: 50, // Emit particles frequently
      emitZone: {
        type: 'edge',
        source: new Phaser.Geom.Circle(0, 0, 80),
        quantity: 30
      },
      tint: [0x3366FF, 0x00CCFF, 0x66FFFF, 0xFFFFFF],
      alpha: { start: 0.8, end: 0 },
      blendMode: 'ADD'
    });
    
    // Make particles move toward the portal
    emitter.setEmitterAngle(Phaser.Math.Angle.Between(
      this.playerCell.x, this.playerCell.y,
      portalPos.x, portalPos.y
    ));
    
    // Create a second emitter for particles converging on the player
    const convergeEmitter = this.add.particles(0, 0, 'toxin', {
      x: { min: -this.worldSize/2, max: this.worldSize/2 },
      y: { min: -this.worldSize/2, max: this.worldSize/2 },
      lifespan: 1500,
      scale: { start: 0.1, end: 0.3 },
      quantity: 1,
      frequency: 20,
      tint: [0x3366FF, 0x00CCFF, 0x66FFFF, 0xFFFFFF],
      alpha: { start: 0, end: 0.8 },
      blendMode: 'ADD',
      emitting: true
    });
    
    // Make these particles move toward the player
    convergeEmitter.forEachAlive((particle) => {
      // Calculate angle from particle to player
      const angle = Phaser.Math.Angle.Between(
        particle.x, particle.y,
        this.playerCell.x, this.playerCell.y
      );
      
      // Set velocity based on angle
      const speed = 300;
      particle.velocityX = Math.cos(angle) * speed;
      particle.velocityY = Math.sin(angle) * speed;
    }, this);
    
    // Create a camera flash effect
    this.cameras.main.flash(2000, 0, 51, 255, true);
    
    // Add a slight camera zoom effect
    this.tweens.add({
      targets: this.cameraControls,
      targetZoom: this.cameraControls.targetZoom * 1.2,
      duration: 2000,
      ease: 'Sine.InOut'
    });
    
    // Stop the emitters after 2 seconds
    this.time.delayedCall(2000, () => {
      emitter.stop();
      convergeEmitter.stop();
    });
  }

  // Update particle effects based on proximity to portal
  private updateParticleEffects(distance: number, approachThreshold: number): void {
    if (!this.portal || !this.portal.active) return;
    
    // Calculate proximity factor (1 when at the portal, 0 when far away)
    const maxDistance = approachThreshold;
    const proximityFactor = Math.max(0, 1 - (distance / maxDistance));
    
    // Enhance orbit emitters based on proximity
    if (this.portal.orbitEmitters && this.portal.orbitEmitters.length > 0) {
      this.portal.orbitEmitters.forEach(emitter => {
        // Adjust frequency - emit more particles as player gets closer
        const frequency = 500 - proximityFactor * 400; // 500ms far away, 100ms when close
        emitter.setFrequency(frequency);
        
        // Increase scale of particles when closer
        const baseScale = 0.1;
        const scaleBoost = 0.2 * proximityFactor;
        emitter.scaleX = baseScale + scaleBoost;
        emitter.scaleY = baseScale + scaleBoost;
        
        // Increase alpha/opacity when closer
        const baseAlpha = 0.4;
        const alphaBoost = 0.3 * proximityFactor;
        emitter.alpha = baseAlpha + alphaBoost;
      });
    }
    
    // Adjust inflow particles frequency based on proximity
    if (this.portal.inflowTimerEvent) {
      // Adjust timer frequency - create more particles as player approaches
      const newDelay = 200 - proximityFactor * 150; // 200ms far away, 50ms when close
      
      // Only update if there's a significant change needed
      if (Math.abs(this.portal.inflowTimerEvent.delay - newDelay) > 10) {
        this.portal.inflowTimerEvent.reset({ 
          delay: newDelay,
          callback: this.portal.inflowTimerEvent.callback,
          callbackScope: this.portal.inflowTimerEvent.callbackScope,
          loop: true
        });
      }
      
      // Increase particles per burst as player approaches
      if (this.portal.particlesPerBurst !== undefined) {
        this.portal.particlesPerBurst = 5 + Math.floor(proximityFactor * 10); // 5 to 15 particles
      }
    }
    
    // Create special effects when player is very close to the portal (within 75% of approach threshold)
    if (proximityFactor > 0.75 && this.playerCell) {
      // Chance to create particle bridge between player and portal
      if (Math.random() < 0.1 * proximityFactor) {
        // Create a particle between the player and portal
        this.createSimpleBridgeParticle();
      }
    }
  }
  
  // Creates a simple bridge particle between player and portal
  private createSimpleBridgeParticle(): void {
    if (!this.portal || !this.playerCell) return;
    
    const portalPos = this.portal.position;
    
    // Create a particle that moves between player and portal
    const startAtPortal = Math.random() < 0.5;
    const startX = startAtPortal ? portalPos.x : this.playerCell.x;
    const startY = startAtPortal ? portalPos.y : this.playerCell.y;
    const endX = startAtPortal ? this.playerCell.x : portalPos.x;
    const endY = startAtPortal ? this.playerCell.y : portalPos.y;
    
    // Create a particle sprite directly (simpler than using emitter)
    const particle = this.add.sprite(startX, startY, 'toxin');
    particle.setScale(0.2);
    particle.setTint(0x66FFFF);
    particle.setBlendMode(Phaser.BlendModes.ADD);
    particle.setAlpha(0.5);
    
    // Ensure UI camera ignores this effect
    this.ensureIgnoredByUICamera(particle);
    
    // Create movement animation
    this.tweens.add({
      targets: particle,
      x: endX,
      y: endY,
      scale: 0.4,
      alpha: 0.8,
      duration: 800,
      ease: 'Quad.easeInOut',
      onComplete: () => {
        // Create a flash effect at the destination
        const flash = this.add.sprite(endX, endY, 'toxin');
        flash.setScale(0.5);
        flash.setTint(0xFFFFFF);
        flash.setBlendMode(Phaser.BlendModes.ADD);
        this.ensureIgnoredByUICamera(flash);
        
        // Make the flash fade out and grow
        this.tweens.add({
          targets: flash,
          scale: 1,
          alpha: 0,
          duration: 300,
          ease: 'Quad.easeOut',
          onComplete: () => flash.destroy()
        });
        
        // Remove the particle
        particle.destroy();
      }
    });
  }

  /**
   * Creates a portal object with pulsating, animated circles to create a black hole effect
   * @param x X-coordinate for the portal
   * @param y Y-coordinate for the portal
   * @param refUrl The URL this portal links to
   */
  private createRefPortal(x: number, y: number, refUrl: string): void {
    try {
      // Create a container for all portal elements
      const container = this.add.container(x, y);
      container.setDepth(95); // Between background and cells
      
      // Define portal colors - dark purples and blues for black hole effect
      const outerColors = [
        0x330066, // Dark purple
        0x220077, // Deep blue-purple
        0x110088  // Deep blue
      ];
      
      // Create outer pulsating circles
      const outerCircles: Phaser.GameObjects.Arc[] = [];
      
      // Create multiple circles with different sizes and animation patterns
      for (let i = 0; i < outerColors.length; i++) {
        const initialRadius = this.portalRadius - (i * 5);
        const circle = this.add.circle(0, 0, initialRadius, outerColors[i], 0.7 - (i * 0.15));
        
        // Add to container and track
        container.add(circle);
        outerCircles.push(circle);
        
        // Add pulsating/expanding animation with different timings per circle
        this.tweens.add({
          targets: circle,
          radius: initialRadius + 15 + (i * 8), // Different expansion for each circle
          alpha: { from: 0.7 - (i * 0.15), to: 0.4 - (i * 0.1) },
          duration: 1500 + (i * 300), // Staggered timings
          ease: 'Sine.InOut',
          yoyo: true,
          repeat: -1, // Loop forever
          delay: i * 400 // Staggered start times
        });
      }
      
      // Create inner core of the portal (black hole center)
      const innerCircle = this.add.circle(0, 0, this.portalRadius * 0.6, 0x000000, 0.9);
      
      // Create slight glow at the center
      const centerGlow = this.add.circle(0, 0, this.portalRadius * 0.4, 0x6600ff, 0.4);
      
      // Add pulsating animation for the core
      this.tweens.add({
        targets: innerCircle,
        radius: { from: this.portalRadius * 0.6, to: this.portalRadius * 0.5 },
        duration: 2000,
        ease: 'Sine.InOut',
        yoyo: true, 
        repeat: -1
      });
      
      // Add pulsating animation for the glow
      this.tweens.add({
        targets: centerGlow,
        radius: { from: this.portalRadius * 0.4, to: this.portalRadius * 0.3 },
        alpha: { from: 0.4, to: 0.6 },
        duration: 1500,
        ease: 'Sine.InOut',
        yoyo: true,
        repeat: -1
      });
      
      // Add "vibeverse" text to the portal
      const textStyle = {
        fontFamily: 'Arial',
        fontSize: '20px',
        color: '#ffffff',
        align: 'center'
      };
      
      // Create the text with semi-transparency
      const displayText = typeof refUrl === 'string' && refUrl.trim() !== '' ? refUrl : "External Link";
      const portalText = this.add.text(0, 0, displayText, textStyle);
      portalText.setOrigin(0.5, 0.5); // Center the text
      portalText.setAlpha(0.5); // Make it semi-transparent
      
      // Add a subtle glow effect to the text
      portalText.setShadow(0, 0, '#8844ff', 8, true, true);
      
      // Add a pulsating animation to the text
      this.tweens.add({
        targets: portalText,
        alpha: { from: 0.5, to: 0.7 },
        scale: { from: 0.95, to: 1.05 },
        duration: 2500,
        ease: 'Sine.InOut',
        yoyo: true,
        repeat: -1
      });
      
      // Add all elements to container
      container.add(innerCircle);
      container.add(centerGlow);
      container.add(portalText);
      
      // Initialize animation timers for each circle - with error safety
      this.portalAnimationTimers = Array(outerColors.length).fill(0).map(() => Math.random() * Math.PI * 2);
      
      // Store portal data
      this.refPortal = {
        container,
        outerCircles,
        innerCircle,
        position: { x, y },
        active: true,
        refUrl: refUrl
      };
      
      // Make sure UI camera ignores the portal
      this.ensureIgnoredByUICamera(container);
      
      // Log portal creation
      console.log(`Created ref portal at position (${x}, ${y}) pointing to ${refUrl}`);
      
      // Create initial spawn effect
      this.createRefPortalSpawnEffect(x, y);
    } catch (error) {
      console.error('Error creating ref portal:', error);
      // Reset portal data if creation failed
      this.refPortal = null;
    }
  }

  /**
   * Creates a dramatic effect when the ref portal first appears
   */
  private createRefPortalSpawnEffect(x: number, y: number): void {
    try {
      // Create expanding rings when ref portal spawns
      const colors = [0x6600cc, 0x4400aa, 0x330088];
      const maxRadius = this.portalRadius * 3; // Larger than the ref portal itself
      
      // Create multiple expanding circles
      colors.forEach((color, index) => {
        const circle = this.add.circle(x, y, 5, color, 0.8 - (index * 0.2));
        circle.setDepth(96); // Above the ref portal but below cells
        
        // Ensure UI camera ignores this effect
        this.ensureIgnoredByUICamera(circle);
        
        // Add expanding animation
        this.tweens.add({
          targets: circle,
          radius: maxRadius - (index * 20),
          alpha: 0,
          duration: 1800 + (index * 200),
          ease: 'Sine.easeOut',
          delay: index * 150,
          onComplete: () => {
            circle.destroy();
          }
        });
      });
      
      // Add flash effect
      const flash = this.add.circle(x, y, this.portalRadius, 0xffffff, 0.7);
      this.ensureIgnoredByUICamera(flash);
      flash.setDepth(96);
      
      this.tweens.add({
        targets: flash,
        radius: this.portalRadius * 1.5,
        alpha: 0,
        duration: 800,
        ease: 'Cubic.easeOut',
        onComplete: () => {
          flash.destroy();
        }
      });
      
      // Only add camera shake if within view and not at game start
      if (this.cameras.main.worldView.contains(x, y) && this.playerCellCreated) {
        this.cameras.main.shake(400, 0.005);
      }
    } catch (error) {
      console.error('Error creating ref portal spawn effect:', error);
      // Continue even if spawn effect fails
    }
  }

  /**
   * Updates the ref portal animation in the update loop
   */
  private updateRefPortal(delta: number): void {
    try {
      if (!this.refPortal || !this.refPortal.active) return;
      
      // Update animation timers
      for (let i = 0; i < this.portalAnimationTimers.length; i++) {
        // Convert delta from ms to seconds and scale for appropriate speed
        this.portalAnimationTimers[i] += (delta / 1000) * (0.5 + (i * 0.2));
      }
    } catch (error) {
      console.error('Error updating ref portal:', error);
      // Disable ref portal updates if there's an error
      if (this.refPortal) {
        this.refPortal.active = false;
      }
    }
  }
  
  /**
   * Handles player collision with the ref portal, creates a particle effect, and redirects to the URL
   */
  private handleRefPortalCollision(): void {
    // Only proceed if we haven't already triggered the ref portal effect
    if (this.refPortal && this.refPortal.active) {
      // Mark ref portal as transitioning instead of just deactivating it
      this.refPortal.isTransitioning = true;
      this.refPortal.active = false;
      
      // Disable player controls during transition
      this.playerControlsEnabled = false;
      
      // Get the player name (or use 'Player' as default)
      const playerName = this.playerName || 'Player';
      
      // Get the ref URL or use a default
      const refUrl = this.refPortal.refUrl || 'cytopod.com';
      
      // Generate the destination URL
      const destinationUrl = refUrl.includes('http') ? refUrl : `https://${refUrl}?portal=true&ref=cytopod.com&username=${playerName}`;
      
      console.log(`Ref portal activated! Redirecting to: ${destinationUrl} in 2 seconds`);
      
      // Create a particle effect of flying light particles (reuse the portal transition effect)
      this.createPortalTransitionEffect();
      
      // Redirect after 2 seconds
      this.time.delayedCall(2000, () => {
        // Open the URL in a new window/tab
        window.open(destinationUrl, '_blank');
      });
    }
  }
  
  /**
   * Creates a subtle particle effect when approaching the ref portal
   */
  private createRefPortalApproachEffect(): void {
    if (!this.refPortal || !this.playerCell) return;
    
    // Get ref portal position
    const portalPos = this.refPortal.position;
    
    // Create a subtle orbiting effect with multiple emitters at different distances
    const orbitEmitters: Phaser.GameObjects.Particles.ParticleEmitter[] = [];
    
    // Create 3 orbit rings at different distances and speeds
    for (let i = 0; i < 3; i++) {
      const radius = this.portalRadius * (1 + (i * 0.5));
      const orbitSpeed = 100 - (i * 20); // Outer rings move slower
      const direction = i % 2 === 0 ? 1 : -1; // Alternate directions
      
      // Use let instead of const for angle so we can modify it
      let currentAngle = Math.random() * Math.PI * 2; // Random starting angle
      
      // Create emitter for this orbit ring
      const emitter = this.add.particles(portalPos.x, portalPos.y, 'toxin', {
        lifespan: 3000,
        scale: { start: 0.1, end: 0.3 },
        quantity: 1,
        frequency: 100 + (i * 50),
        tint: [0x3366FF, 0x00CCFF, 0x66FFFF, 0xFFFFFF],
        alpha: { start: 0.4, end: 0 },
        blendMode: 'ADD',
        // Custom position function for circular movement
        x: () => {
          return portalPos.x + Math.cos(currentAngle) * radius;
        },
        y: () => {
          return portalPos.y + Math.sin(currentAngle) * radius;
        },
        // Set velocities tangent to the circle
        speedX: {
          onEmit: () => {
            // Update angle for next particle (produces orbital movement)
            currentAngle += 0.1 * direction;
            // Return tangential velocity X
            return Math.cos(currentAngle + (Math.PI / 2 * direction)) * orbitSpeed;
          }
        },
        speedY: {
          onEmit: () => {
            // Return tangential velocity Y
            return Math.sin(currentAngle + (Math.PI / 2 * direction)) * orbitSpeed;
          }
        }
      });
      
      emitter.particleBringToTop = true;
      
      // Store for cleanup
      orbitEmitters.push(emitter);
      
      // Ensure UI camera ignores the emitter
      this.ensureIgnoredByUICamera(emitter);
    }
    
    // Add some additional particles that flow toward the ref portal - fix type issues
    const inflowEmitter = this.add.particles(portalPos.x, portalPos.y, 'toxin', {
      x: { min: -500, max: 500 },
      y: { min: -500, max: 500 },
      lifespan: 2000,
      scale: { start: 0.05, end: 0.2 },
      quantity: 1,
      frequency: 150,
      tint: [0x3366FF, 0x00CCFF, 0x66FFFF],
      alpha: { start: 0.2, end: 0.6 },
      blendMode: 'ADD',
      // Use simpler fixed value instead of callback to avoid type issues
      speed: { min: 50, max: 80 }
    });
    
    // Create a timer to periodically create directed particles flowing toward the ref portal
    const createDirectedParticles = () => {
      // Safety check
      if (!this.refPortal || !this.refPortal.active) return;
      
      // Create multiple particles around the ref portal area that will flow inward
      for (let i = 0; i < (this.refPortal.particlesPerBurst || 5); i++) {
        // Random position in a wide area around the ref portal
        const angle = Math.random() * Math.PI * 2;
        const distance = 300 + Math.random() * 200;
        const x = portalPos.x + Math.cos(angle) * distance;
        const y = portalPos.y + Math.sin(angle) * distance;
        
        // Create a particle at this position
        const particle = inflowEmitter.emitParticle(1, x, y);
        
        // If particle was created successfully
        if (particle) {
          // Calculate angle to ref portal
          const angleToRefPortal = Math.atan2(portalPos.y - y, portalPos.x - x);
          
          // Set velocity toward the ref portal with increased variation based on proximity
          const distanceToRefPortal = Phaser.Math.Distance.Between(x, y, portalPos.x, portalPos.y);
          const speedFactor = Math.max(0.8, 1 - (distanceToRefPortal / 600));
          const particleSpeed = (50 + Math.random() * 30) * speedFactor;
          const angleVariation = (Math.random() - 0.5) * 0.2;
          
          particle.velocityX = Math.cos(angleToRefPortal + angleVariation) * particleSpeed;
          particle.velocityY = Math.sin(angleToRefPortal + angleVariation) * particleSpeed;
        }
      }
    };
    
    // Set up a timer to create particles
    const inflowParticleTimer = this.time.addEvent({
      delay: 200,
      callback: createDirectedParticles,
      callbackScope: this,
      loop: true
    });
    
    // Store the timer event for dynamic adjustment
    this.refPortal.inflowTimerEvent = inflowParticleTimer;
    
    // Set initial particles per burst value
    this.refPortal.particlesPerBurst = 5;
    
    // Ensure UI camera ignores the emitter
    this.ensureIgnoredByUICamera(inflowEmitter);
    
    // Store emitters for cleanup
    this.refPortal.approachEmitter = orbitEmitters[0]; // Store the first one as the main one
    this.refPortal.inflowEmitter = inflowEmitter;
    this.refPortal.orbitEmitters = orbitEmitters;
    
    // Update cleanup function to handle the timer
    const originalCleanupFunction = this.refPortal.orbitCleanupFunction;
    this.refPortal.orbitCleanupFunction = () => {
      // Call original cleanup if it exists
      if (originalCleanupFunction) originalCleanupFunction();
      
      // Remove the timer
      if (inflowParticleTimer) inflowParticleTimer.remove();
    };
  }
  
  /**
   * Creates a particle effect for the ref portal transition
   */
  private createRefPortalTransitionEffect(): void {
    if (!this.refPortal || !this.playerCell) return;
    
    // Get ref portal position
    const portalPos = this.refPortal.position;
    
    // Create particles around the player and heading toward the ref portal
    const emitter = this.add.particles(0, 0, 'toxin', {
      x: this.playerCell.x,
      y: this.playerCell.y,
      lifespan: 2000,
      scale: { start: 0.4, end: 0.1 },
      speed: { min: 50, max: 150 },
      quantity: 3,
      frequency: 50, // Emit particles frequently
      emitZone: {
        type: 'edge',
        source: new Phaser.Geom.Circle(0, 0, 80),
        quantity: 30
      },
      tint: [0x3366FF, 0x00CCFF, 0x66FFFF, 0xFFFFFF],
      alpha: { start: 0.8, end: 0 },
      blendMode: 'ADD'
    });
    
    // Make particles move toward the ref portal
    emitter.setEmitterAngle(Phaser.Math.Angle.Between(
      this.playerCell.x, this.playerCell.y,
      portalPos.x, portalPos.y
    ));
    
    // Create a second emitter for particles converging on the player
    const convergeEmitter = this.add.particles(0, 0, 'toxin', {
      x: { min: -this.worldSize/2, max: this.worldSize/2 },
      y: { min: -this.worldSize/2, max: this.worldSize/2 },
      lifespan: 1500,
      scale: { start: 0.1, end: 0.3 },
      quantity: 1,
      frequency: 20,
      tint: [0x3366FF, 0x00CCFF, 0x66FFFF, 0xFFFFFF],
      alpha: { start: 0, end: 0.8 },
      blendMode: 'ADD',
      emitting: true
    });
    
    // Make these particles move toward the player
    convergeEmitter.forEachAlive((particle) => {
      // Calculate angle from particle to player
      const angle = Phaser.Math.Angle.Between(
        particle.x, particle.y,
        this.playerCell.x, this.playerCell.y
      );
      
      // Set velocity based on angle
      const speed = 300;
      particle.velocityX = Math.cos(angle) * speed;
      particle.velocityY = Math.sin(angle) * speed;
    }, this);
    
    // Create a camera flash effect
    this.cameras.main.flash(2000, 0, 51, 255, true);
    
    // Add a slight camera zoom effect
    this.tweens.add({
      targets: this.cameraControls,
      targetZoom: this.cameraControls.targetZoom * 1.2,
      duration: 2000,
      ease: 'Sine.InOut'
    });
    
    // Stop the emitters after 2 seconds
    this.time.delayedCall(2000, () => {
      emitter.stop();
      convergeEmitter.stop();
    });
  }
  
  /**
   * Updates particle effects based on player proximity to the portal
   */
  private updateRefParticleEffects(distance: number, approachThreshold: number): void {
    if (!this.refPortal || !this.playerCell) return;
    
    // Calculate a factor (0-1) based on how close the player is to the portal
    // A smaller distance means a higher factor
    const proximityFactor = 1 - (distance / approachThreshold);
    
    // Adjust particle burst quantity based on proximity - more particles as player gets closer
    if (this.refPortal.particlesPerBurst) {
      this.refPortal.particlesPerBurst = Math.floor(5 + (10 * proximityFactor));
    }
    
    // Adjust particle emission frequency - faster as player gets closer
    if (this.refPortal.inflowTimerEvent) {
      // Calculate new delay - shorter delay as player gets closer
      const newDelay = Math.max(50, 200 - (150 * proximityFactor));
      
      // Only update if delay has changed significantly
      if (Math.abs(this.refPortal.inflowTimerEvent.delay - newDelay) > 10) {
        // Remove the old timer
        this.refPortal.inflowTimerEvent.remove();
        
        // Create a new timer with the updated delay
        this.refPortal.inflowTimerEvent = this.time.addEvent({
          delay: newDelay,
          callback: this.refPortal.inflowTimerEvent.callback,
          callbackScope: this,
          loop: true
        });
      }
    }
    
    // Add occasional direct particle bridge between player and portal
    if (proximityFactor > 0.4 && Math.random() < 0.1) {
      this.createSimpleBridgeParticle();
    }
  }
  
  /**
   * Setup mobile joystick controls
   */
  private setupMobileControls(): void {
    // Check if we're on a mobile device based on screen size
    this.isMobileDevice = window.innerWidth <= 768;
    
    // Create joystick containers through the DOM UI manager
    const joystickContainers = this.domUI.createJoystickContainers();
    
    // Initialize left joystick (movement)
    if (joystickContainers.leftContainer) {
      const leftZone = joystickContainers.leftContainer.querySelector('#left-joystick-inner');
      if (leftZone) {
        this.joystickLeft = nipplejs.create({
          zone: leftZone as HTMLElement,
          mode: 'static',
          position: { left: '50%', top: '50%' },
          color: 'cornflowerblue',
          size: 80,
          threshold: 0.1 // More responsive to small movements
        });
        
        // Set up left joystick events for movement
        this.joystickLeft.on('start', (evt: Event, data: nipplejs.JoystickEventData) => {
          this.moveJoystickData.active = true;
        });
        
        this.joystickLeft.on('move', (evt: Event, data: nipplejs.JoystickEventData) => {
          this.moveJoystickData.angle = data.angle.radian;
          // Allow better control with a scaled force:
          // - Gentle movements still register (0.3 minimum when active)
          // - Full extension still gives maximum control
          this.moveJoystickData.force = 0.3 + (Math.min(1, data.force / 50) * 0.7);
        });
        
        this.joystickLeft.on('end', (evt: Event, data: nipplejs.JoystickEventData) => {
          this.moveJoystickData.active = false;
          this.moveJoystickData.force = 0;
        });
      }
    }
    
    // Initialize right joystick (shooting)
    if (joystickContainers.rightContainer) {
      const rightZone = joystickContainers.rightContainer.querySelector('#right-joystick-inner');
      if (rightZone) {
        this.joystickRight = nipplejs.create({
          zone: rightZone as HTMLElement,
          mode: 'static',
          position: { left: '50%', top: '50%' },
          color: 'crimson',
          size: 80,
          threshold: 0.05, // More sensitive for aiming
          fadeTime: 100    // Faster fade time for better responsive feel
        });
        
        // Set up right joystick events for toxin shooting
        this.joystickRight.on('start', (evt: Event, data: nipplejs.JoystickEventData) => {
          this.shootJoystickData.active = true;
          this.shootJoystickData.force = 0; // Start with no force
          console.log('Right joystick activated');
        });
        
        this.joystickRight.on('move', (evt: Event, data: nipplejs.JoystickEventData) => {
          // Store angle and force
          this.shootJoystickData.angle = data.angle.radian;
          // More sensitive force calculation for easier shooting
          // Using 20 instead of 30 to make it even more responsive on mobile
          this.shootJoystickData.force = Math.min(1, data.force / 20);
          
          // The actual shooting is handled in the update method to allow for rate limiting
          console.log(`Right joystick moved: force=${this.shootJoystickData.force.toFixed(2)}`);
        });
        
        this.joystickRight.on('end', (evt: Event, data: nipplejs.JoystickEventData) => {
          // When releasing the joystick, fire a toxin if force was significant
          // Lower threshold from 0.3 to 0.15 to make it easier to shoot with small movements
          if (this.shootJoystickData.force > 0.15) {
            console.log('Shooting toxin on joystick release');
            this.shootToxinWithJoystick();
          }
          
          this.shootJoystickData.active = false;
          this.shootJoystickData.force = 0;
          console.log('Right joystick released');
        });
      }
    }
    
    // Toggle visibility based on device
    this.domUI.toggleJoystickVisibility(this.isMobileDevice);
    
    // Update joystick positions on resize
    this.scale.on('resize', () => {
      this.domUI.updateJoystickPositions();
      this.domUI.toggleJoystickVisibility(window.innerWidth <= 768);
      
      // Update mobile device detection
      this.isMobileDevice = window.innerWidth <= 768;
    });
  }
  
  /**
   * Shoot toxin based on joystick direction
   */
  private shootToxinWithJoystick(): void {
    if (!this.playerCell) {
      console.log('Cannot shoot toxin: player cell not found');
      return;
    }
    
    // Check if player has enough ATP for shooting
    if (this.atp < TOXIN_ATP_COST) {
      console.log('Cannot shoot toxin: not enough ATP');
      return;
    }
    
    // Get the current player position
    const playerX = this.playerCell.x;
    const playerY = this.playerCell.y;
    
    // Calculate the target position based on joystick data
    // Increase sensitivity by using a smaller divisor
    const force = this.shootJoystickData.force * 20; // Make this more responsive
    const angle = this.shootJoystickData.angle;
    
    console.log('Shooting toxin with joystick', {
      angle: angle,
      force: force,
      playerPosition: { x: playerX, y: playerY }
    });
    
    // Fix inverted Y-axis for shooting
    const targetX = playerX + Math.cos(angle) * force;
    const targetY = playerY - Math.sin(angle) * force; // Invert Y by adding negative sign
    
    // Use the existing shootToxin method (the same one used by mouse)
    this.shootToxin(targetX, targetY);
    
    // Prevent shooting too rapidly by adding a small force reset delay
    this.time.delayedCall(200, () => {
      // Reset the force to allow shooting again
      if (this.shootJoystickData.active) {
        this.shootJoystickData.force = 0;
      }
    });
  }
  
  /**
   * Handle movement from the joystick
   */
  private handleJoystickMovement(delta: number): void {
    if (!this.moveJoystickData.active || !this.playerCell || !this.playerControlsEnabled) return;
    
    const angle = this.moveJoystickData.angle;
    const force = this.moveJoystickData.force;
    
    // Only move if force is above a minimum threshold
    if (force < 0.1) return;
    
    // Calculate a target position based on joystick direction and force
    // The further the joystick is pushed, the further away the target is set
    const distance = 100 + (force * 150); // Scale distance based on joystick force
    
    // Fix inverted joystick controls - flip the Y component
    // We need to invert the sin component which controls Y movement
    const targetX = this.playerCell.x + Math.cos(angle) * distance;
    const targetY = this.playerCell.y - Math.sin(angle) * distance; // Invert Y by adding negative sign
    
    // Use the existing target-based movement system but don't show marker for joystick movement
    this.playerCell.setTargetPosition(targetX, targetY, false);
  }

  // Add this method after the create() method

  /**
   * Step 1: Initialize everything needed locally
   */
  private async initializeClientEnvironment(): Promise<void> {
    try {
      console.log("Step 1: Initializing client environment...");
      
      // Set background color
      this.cameras.main.setBackgroundColor('#000033');
      
      // Create a world boundary centered at (0,0)
      this.physics.world.setBounds(-this.worldSize/2, -this.worldSize/2, this.worldSize, this.worldSize);
      
      // Create gradient background using shared resources
      this.createBackground();
      
      // Initialize group for game objects
      this.gameObjectsGroup = this.add.group();
      
      // Create textures for resources if they don't exist
      this.createResourceTextures();
      
      // Create textures for toxins if they don't exist
      this.createToxinTexture();
      
      // Create main player cell in the center
      this.playerCell = new Cell(this, 0, 0, this.networkService.getSocketId() || 'temp_id');
      
      // Explicitly set initial resource values on the cell
      this.playerCell.setATP(this.atp);
      this.playerCell.setGlucose(this.glucose);
      
      this.playerCellCreated = true;
      log.info(() => `Created player cell at (0, 0) with id ${this.networkService.getSocketId() || 'temp_id'}`);
      log.info(() => `Initial resources: ATP=${this.atp}, Glucose=${this.glucose}, Max ATP=${this.playerCell.getMaxATP()}`);
      
      // Add nucleus to player cell
      this.addNucleusToPlayerCell();
      
      // Configure camera to follow player
      this.cameras.main.centerOn(0, 0);
      this.cameras.main.startFollow(this.playerCell, true, 0.1, 0.1);
      this.cameras.main.zoom = 1;
      
      // Initialize the DOM UI Manager (before GameUI to ensure layering)
      this.domUI = new DOMUIManager(this);
      console.log('🎨 DOM UI Manager initialized');
      
      // Create DOM-based UI elements
      this.createDOMUI();
      
      // Create Phaser-based UI (legacy - will be gradually replaced)
      this.createUI();
      
      // Setup camera controls
      this.setupCameraControls();
      
      // Handle resize initially
      this.handleResize();
      
      // Handle window resize
      this.scale.on('resize', this.handleResize, this);
      
      // Update UI camera ignore list initially
      this.updateUICameraIgnoreList();
      
      // Periodically update the UI camera ignore list to ensure all objects are properly ignored
      this.time.addEvent({
        delay: 5000,
        callback: this.updateUICameraIgnoreList,
        callbackScope: this,
        loop: true
      });
      
      // Create the portal with a delay to ensure it doesn't interfere with game loading
      this.time.delayedCall(500, () => {
        try {
          // Only create the portal if showPortal is true
          if (this.showPortal) {
            // Create the portal away from the player spawn to avoid initial collision
            this.createPortal(-1000, -1000);
            console.log('Portal created based on URL parameter');
            
            // Create the ref portal if needed (3 portal diameters away)
            if (this.showRefPortal && this.refUrl) {
              // Position the ref portal 3 portal diameters away from the first one
              const offsetX = this.portalRadius * 6; // 3 diameters = 6 radii
              this.createRefPortal(-1000 + offsetX, -1000, this.refUrl);
              console.log(`Ref portal created pointing to ${this.refUrl}`);
            }
          } else {
            console.log('Portal creation skipped based on URL parameter');
          }
        } catch (error) {
          console.error('Error creating portal:', error);
        }
      });
      
      // Add debug visuals to help with troubleshooting if enabled
      if (this.settingsManager.getSettings().showFps) {
        this.createDebugVisuals();
      }
      
      // Add debug info to help track cells
      this.time.addEvent({
        delay: 5000,
        callback: () => {
          if (this.serverUpdatesEnabled) {
            console.log(`Current player cell position: (${this.playerCell.x}, ${this.playerCell.y})`);
            console.log(`Number of remote cells: ${this.remoteCells.size}`);
            this.remoteCells.forEach((cell, id) => {
              console.log(`Remote cell ${id} at (${cell.x}, ${cell.y})`);
            });
          }
        },
        loop: true
      });
      
      // Set up debug commands
      this.addDebugCommands();
      
      // Mark client initialization as complete
      this.clientInitialized = true;
      console.log("Client environment initialized successfully");
      
      return Promise.resolve();
    } catch (error) {
      console.error("Failed to initialize client environment:", error);
      return Promise.reject(error);
    }
  }

  /**
   * Step 2: Connect to the network
   */
  private async initializeNetworkConnection(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      // Ensure we're not already connected
      if (this.networkService.isConnected()) {
        this.networkConnected = true;
        console.log("Already connected to network");
        resolve();
        return;
      }
      
      // Register one-time connection handler
      const onConnectHandler = () => {
        // Clean up the event listener to prevent recursion
        this.networkService.offConnect(onConnectHandler);
        
        this.networkConnected = true;
        console.log("Network connection established");
        resolve();
      };
      
      // Connect to the server
      this.networkService.onConnect(onConnectHandler);
      this.networkService.connect();
      
      // Set a timeout for connection failure
      const timeoutId = setTimeout(() => {
        // Clean up the event listener if connection timed out
        this.networkService.offConnect(onConnectHandler);
        
        if (!this.networkConnected) {
          console.error("Network connection timed out after 10 seconds");
          reject(new Error("Network connection timed out"));
        }
      }, 10000);
    });
  }

  /**
   * Step 3: Register all network event handlers with proper queueing
   */
  private registerNetworkEventHandlers(): void {
    console.log("Step 3: Registering network event handlers...");
    
    // Room state
    this.networkService.onRoomState((data: RoomStateEvent) => {
      console.log(`Received room state with ${data.players.length} players and ${data.resources.length} resources`);
      
      if (!this.clientInitialized) {
        console.log("Client not initialized, queueing room state event");
        // Queue for later processing
        this.pendingEvents.roomState = data;
        return;
      }
      
      this.processRoomState(data);
      this.stateReceived = true;
      
      // If this is the first state received, process any pending events
      if (!this.readyForInput) {
        this.processPendingEvents();
        this.readyForInput = true;
      }
    });

    this.networkService.onFullSnapshot((data: FullSnapshotEvent) => {
      if (!this.clientInitialized) {
        return;
      }
      this.processFullSnapshot(data);
      this.stateReceived = true;
      if (!this.readyForInput) {
        this.processPendingEvents();
        this.readyForInput = true;
      }
    });

    this.networkService.onServerMode((data: ServerModeEvent) => {
      this.strictTickMode = data.strictTickMode;
    });
    
    // Player joined - with queueing if client not ready
    this.networkService.onPlayerJoined((data: PlayerJoinedEvent) => {
      if (!this.readyForInput) {
        console.log(`Queueing player joined event for player ${data.id}`);
        this.pendingEvents.playerJoins.push(data);
        return;
      }
      
      this.handlePlayerJoined(data);
    });
    
    // Player left
    this.networkService.onPlayerLeft((playerId: string) => {
      if (!this.readyForInput) {
        console.log(`Queueing player left event for player ${playerId}`);
        this.pendingEvents.playerLeaves.push(playerId);
        return;
      }
      
      // Get the remote cell
      const remoteCell = this.remoteCells.get(playerId);
      
      if (remoteCell) {
        // Destroy the cell
        remoteCell.destroy();
        
        // Remove from our map
        this.remoteCells.delete(playerId);
        this.remoteInterpolationBuffer.clear(playerId);
        
        // Clean up name and health bar
        const nameText = this.remoteNameTexts.get(playerId);
        const healthBar = this.remoteHealthBars.get(playerId);
        
        if (nameText) {
          nameText.destroy();
          this.remoteNameTexts.delete(playerId);
        }
        
        if (healthBar) {
          healthBar.destroy();
          this.remoteHealthBars.delete(playerId);
        }
        
        log.debug(() => `Removed remote cell for player ${playerId}, remaining cells: ${this.remoteCells.size}`);
      } else {
        log.warn(() => `Received player left event for player ${playerId} but no remote cell found`);

        // Even if we can't find the cell, try to clean up any UI elements that might exist
        const nameText = this.remoteNameTexts.get(playerId);
        const healthBar = this.remoteHealthBars.get(playerId);
        
        if (nameText) {
          nameText.destroy();
          this.remoteNameTexts.delete(playerId);
        }
        
        if (healthBar) {
          healthBar.destroy();
          this.remoteHealthBars.delete(playerId);
        }
      }
    });
    
    // Player movement - with queueing if client not ready
    this.networkService.onPlayerMoved((data: PlayerMovedEvent) => {
      if (!this.readyForInput) {
        this.pendingEvents.playerMoves.push(data);
        return;
      }

      if (this.hasTickMovementEnabled()) {
        return;
      }

      this.queueRemoteMovement(data);
    });

    this.networkService.onTickUpdate((data: TickUpdateEvent) => {
      if (!this.readyForInput) {
        this.pendingEvents.playerMoves.push(...data.playerMoves);
        return;
      }

      this.handleTickUpdate(data);
    });

    this.networkService.onMembraneKeyframe((data: MembraneKeyframeEvent) => {
      if (!this.readyForInput) {
        return;
      }
      this.applyRemoteMembraneKeyframe(data);
    });
    
    // Resource spawned - with queueing if client not ready
    this.networkService.onResourceSpawned((data: ResourceState) => {
      if (!this.readyForInput) {
        console.log(`Queueing resource spawned event for resource ${data.id}`);
        this.pendingEvents.resources.push(data);
        return;
      }
      
      this.addResource(data);
    });
    
    // Resource collected
    this.networkService.onResourceCollected((data: ResourceCollectedEvent) => {
      if (!this.readyForInput) {
        console.log(`Queueing resource collected event for resource ${data.id}`);
        this.pendingEvents.resourceCollected.push(data);
        return;
      }
      
      const resource = this.resources.get(data.id);
      if (resource) {
        // Clean up any attached effects
        const effects = resource.getData('effects') as Phaser.GameObjects.GameObject[];
        if (effects && Array.isArray(effects)) {
          effects.forEach(effect => {
            if (effect && effect.active) {
              // Fade out all effects quickly
              this.tweens.add({
                targets: effect,
                alpha: 0,
                duration: 150,
                onComplete: () => {
                  if (effect.active) {
                    effect.destroy();
                  }
                }
              });
            }
          });
        }
        
        // Play collection animation
        this.tweens.add({
          targets: resource,
          alpha: 0,
          scale: 0.1,
          duration: 200,
          onComplete: () => {
            resource.destroy();
            this.resources.delete(data.id);
          }
        });
        
        // If we collected it, update our resource counts
        if (data.playerId === this.networkService.getSocketId()) {
          const resourceType = resource.getData('type') as 'lipid' | 'glucose';
          const value = resource.getData('value') as number;
          
          // Get capacity based on membrane stage
          const membraneStage = this.playerCell ? this.playerCell.getMembraneStage() : 1;
          const capacity = getResourceCapacityForStage(membraneStage);

          // One-time update of resources based on collected type
          if (resourceType === 'lipid') {
            this.lipids = Math.min(this.lipids + value, capacity.maxLipids);
          } else {
            // Just add glucose to the fixed amount - mitochondria will handle ATP production
            this.glucose = Math.min(this.glucose + value, capacity.maxGlucose);
            // Ensure we have a clean float value
            this.glucose = Math.round(this.glucose * 100) / 100;
            // Directly sync with the player cell to ensure consistent values
            this.playerCell.setGlucose(this.glucose);
          }
          
          // Get the correct maximum ATP capacity from the player cell
          const maxATP = this.playerCell ? this.playerCell.getMaxATP() : capacity.maxATP;

          // Update all UI components with new values
          this.updateResourceCounts({
            lipids: this.lipids,
            glucose: this.glucose,
            atp: this.atp,
            maxATP: maxATP
          }, true);
        }
      }
    });
    
    // Toxin shot
    this.networkService.onToxinShot((data: ToxinShotEvent) => {
      if (!this.readyForInput) {
        console.log(`Queueing toxin shot event from player ${data.playerId}`);
        this.pendingEvents.toxins.push(data);
        return;
      }
      
      this.createToxin(data);
    });
    
    // Cell damaged
    this.networkService.onCellDamaged((data: CellDamagedEvent) => {
      if (!this.readyForInput) {
        console.log(`Queueing cell damaged event for player ${data.playerId}`);
        this.pendingEvents.cellDamaged.push(data);
        return;
      }
      
      if (data.playerId === this.networkService.getSocketId()) {
        // Local player damaged
        this.playerCell.setHealth(data.health);
        this.showDamageEffect(this.playerCell, data.position.angle, data.amount);
        
        // Update health UI and DOM UI
        this.updateHealthBar(this.playerHealthBar, data.health);
        
        if (this.domUI) {
          // Get current resource values from _lastUIValues or use current values
          const resources = this._lastUIValues ? {
            lipids: this._lastUIValues.lipids,
            glucose: Math.round(this._lastUIValues.glucose),
            atp: Math.round(this._lastUIValues.atp),
            maxATP: this._lastUIValues.maxATP
          } : {
            lipids: this.lipids,
            glucose: Math.round(this.glucose),
            atp: Math.round(this.atp),
            maxATP: this.playerCell.getMaxATP()
          };
          
          // Update DOM UI with new health value
          this.domUI.updateStatsPanel(
            data.health,
            resources,
            this._lastUIValues?.membraneStage || 1
          );
          
          // Update stored health value for future comparisons
          if (this._lastUIValues) {
            this._lastUIValues.health = data.health;
          }
        }
      } else {
        // Remote player damaged
        const cell = this.remoteCells.get(data.playerId);
        if (cell) {
          (cell as any).health = data.health;
          this.showDamageEffect(cell, data.position.angle, data.amount);
          
          // Update remote player's health bar
          const healthBar = this.remoteHealthBars.get(data.playerId);
          if (healthBar) {
            this.updateHealthBar(healthBar, data.health);
          }
        }
      }
    });
    
    // Player died
    this.networkService.onPlayerDied((playerId: string) => {
      if (!this.readyForInput) {
        console.log(`Queueing player died event for player ${playerId}`);
        this.pendingEvents.playerDied.push(playerId);
        return;
      }
      
      console.log(`Player ${playerId} died`);
      
      if (playerId === this.networkService.getSocketId()) {
        // Local player died
        console.log('You died! Waiting for respawn...');
        
        // Create death effect
        this.createDeathEffect(this.playerCell);
        
        // Hide player cell (but don't destroy it)
        this.playerCell.setVisible(false);
        
        // Disable player controls
        this.playerControlsEnabled = false;
        
        // Show death message in UI
        this.gameUI.showNotification('You died! Respawning in 8 seconds...', 8000);
      } else {
        // Remote player died
        const remoteCell = this.remoteCells.get(playerId);
        if (remoteCell) {
          // Create death effect
          this.createDeathEffect(remoteCell);
          
          // Hide remote cell (but don't destroy it)
          remoteCell.setVisible(false);
          
          // Hide name and health bar
          const nameText = this.remoteNameTexts.get(playerId);
          const healthBar = this.remoteHealthBars.get(playerId);
          
          if (nameText) nameText.setVisible(false);
          if (healthBar) healthBar.setVisible(false);
        }
      }
    });
    
    // Player respawned
    this.networkService.onPlayerRespawned((data: PlayerRespawnedEvent) => {
      if (!this.readyForInput) {
        console.log(`Queueing player respawned event for player ${data.playerId}`);
        this.pendingEvents.playerRespawned.push(data);
        return;
      }
      
      console.log(`Player ${data.playerId} respawned at (${data.x}, ${data.y})`);
      
      if (data.playerId === this.networkService.getSocketId()) {
        // Local player respawned
        console.log('You have respawned!');
        
        // Update position
        this.playerCell.setPosition(data.x, data.y);
        this.playerCell.setVisible(true);
        this.playerCell.setHealth(data.health);
        
        // Re-enable player controls
        this.playerControlsEnabled = true;
        
        // Update camera
        this.cameras.main.centerOn(data.x, data.y);
        
        // Create spawn effect at new position
        this.createPlayerSpawnEffect(data.x, data.y);
        
        // Show respawn message
        this.gameUI.showNotification('You have respawned', 2000);
      } else {
        // Remote player respawned
        const remoteCell = this.remoteCells.get(data.playerId);
        if (remoteCell) {
          remoteCell.setPosition(data.x, data.y);
          remoteCell.setVisible(true);
          remoteCell.setHealth(data.health);
          
          // Create spawn effect at new position
          this.createPlayerSpawnEffect(data.x, data.y);
          
          // Show name and health bar
          const nameText = this.remoteNameTexts.get(data.playerId);
          const healthBar = this.remoteHealthBars.get(data.playerId);
          
          if (nameText) nameText.setVisible(true);
          if (healthBar) {
            this.updateHealthBar(healthBar, data.health);
            healthBar.setVisible(true);
          }
        }
      }
    });
    
    // Organelle built
    this.networkService.onOrganelleBuilt((data: OrganelleBuiltEvent) => {
      if (!this.readyForInput) {
        console.log(`Queueing organelle built event for player ${data.playerId}`);
        this.pendingEvents.organelleBuilt.push(data);
        return;
      }
      
      log.info(() => `Received organelleBuilt event: ${data}`);
      
      if (data.playerId === this.networkService.getSocketId()) {
        // This is for our player
        console.log('Organelle built for our player. Updating resources and organelles.');
        
        // Update resources with adjusted values
        this.updateResourceCounts(data.resources, true); // Force update
        
        // Update the player cell's organelles
        this.updateOrganelles(data.organelles);
        
        // Ensure the cell has the updated values
        this.playerCell.setATP(this.atp);
        this.playerCell.setGlucose(this.glucose);
      } else {
        log.info(() => `Organelle built for remote player: ${data.playerId}`);
        // Update remote cell's organelles
        const cell = this.remoteCells.get(data.playerId);
        if (cell) {
          this.updateRemoteCellOrganelles(cell, data.organelles);
        }
      }
    });
    
    // Membrane expanded event
    this.networkService.onMembraneExpanded((data: MembraneExpandedEvent) => {
      if (!this.readyForInput) {
        console.log(`Queueing membrane expanded event for player ${data.playerId}`);
        this.pendingEvents.membraneExpanded.push(data);
        return;
      }
      
      console.log('Membrane expanded event:', data);
      
      // Is this our own player?
      if (data.playerId === this.networkService.getSocketId()) {
        // Update our local cell membrane
        this.handleLocalMembraneExpansion(data.stage);
        
        // Update resources
        this.updateResourceCounts(data.resources);
      } else {
        // Find the remote cell
        const remoteCell = this.remoteCells.get(data.playerId);
        if (remoteCell) {
          // Get the stage configuration
          const stageConfig = MEMBRANE_EXPANSION_STAGES.find((s) => s.stage === data.stage);
          if (stageConfig) {
            // Apply expansion to remote cell
            remoteCell.setMembraneStage(
              data.stage, 
              stageConfig.sizeMultiplier, 
              stageConfig.speedMultiplier
            );
          }
        }
      }
    });
    
    // Other event handlers follow the same pattern...
    console.log("Network event handlers registered successfully");
  }

  /**
   * Step 4: Join the room
   */
  private joinRoom(): void {
    console.log("Step 4: Joining room...");
    
    // Only proceed if network is connected
    if (!this.networkConnected) {
      console.warn("Cannot join room: not connected to the network");
      console.log("Not connected yet, retrying in 1 second...");
      this.time.delayedCall(1000, () => this.joinRoom(), [], this);
      return;
    }
    
    // Handle the case where portal=true (join vibeverse)
    if (this.joinVibeverse) {
      console.log('Portal=true: Getting rooms list to find vibeverse room...');
      
      // Register one-time handler for rooms list
      const onRoomsListHandler = (rooms: RoomInfo[]) => {
        // Find the vibeverse room in the list
        const vibeVerseRoom = rooms.find(room => room.name.toLowerCase() === 'vibeverse');
        
        // Remove the handler after use
        this.networkService.offRoomsList(onRoomsListHandler);
        
        if (vibeVerseRoom) {
          console.log(`Found vibeverse room with ID: ${vibeVerseRoom.id}, joining...`);
          this.networkService.joinRoom(vibeVerseRoom.id, this.playerName);
        } else {
          console.warn('Vibeverse room not found in the rooms list!');
          // If we have a fallback roomId, use it
          if (this.roomId) {
            console.log(`Falling back to join room ${this.roomId} with name ${this.playerName}`);
            this.networkService.joinRoom(this.roomId, this.playerName);
          }
        }
      };
      
      // Register handler for rooms list and request the list
      this.networkService.onRoomsList(onRoomsListHandler);
      this.networkService.getRooms();
      return;
    }
    
    // Handle normal room joining case (when specific roomId is provided)
    if (this.roomId) {
      console.log(`Joining regular room ${this.roomId} with name ${this.playerName}`);
      this.networkService.joinRoom(this.roomId, this.playerName);
      return;
    }
    
    // If we get here, neither vibeverse nor a room ID was provided
    console.warn("Cannot join any room: no roomId provided and joinVibeverse is false");
  }

  /**
   * Step 5: Set up input handlers (these will only be effective after full initialization)
   */
  private setupInputHandlers(): void {
    console.log("Step 5: Setting up input handlers...");
    
    // Setup input handlers - ensure we don't override Cell's click-to-move
    this.setupInput();
    
    // Setup mobile controls if needed
    this.setupMobileControls();
    
    // Add global click handler for resource collection
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      // Only process if we're fully initialized and it's a left click
      if (!this.readyForInput || !pointer.leftButtonDown()) return;
      
      // Skip if over UI
      if (pointer.y < 150 || (pointer.x > this.scale.width - 200 && pointer.y < 300)) {
        return;
      }
      
      // Convert screen coordinates to world coordinates
      const worldPoint = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
      
      // Get the membrane radius from the membrane's baseRadius property
      const membraneRadius = this.playerCell.membrane ? 
        this.playerCell.membrane.baseRadius : 100;
      
      // Create a list of resources to collect
      const resourcesToCollect: string[] = [];
      let resourceClicked = false;  // Flag to track if resource is clicked
      // Check all resources to see if any are close to the click position
      this.resources.forEach((resource, id) => {
        // Calculate distance between click and resource
        const resourceDistance = Phaser.Math.Distance.Between(
          worldPoint.x, worldPoint.y,
          resource.x, resource.y
        );
        
        // Check if click is close to the resource (10 pixel radius for easier clicking)
        if (resourceDistance < 10) {
          // Calculate distance between resource and player cell
          const distanceToCell = Phaser.Math.Distance.Between(
            this.playerCell.x, this.playerCell.y,
            resource.x, resource.y
          );
          
          // Only collect if within collection range
          if (distanceToCell < membraneRadius + 15) {
            resourcesToCollect.push(id);
            resourceClicked = true;
            
            // Check for other resources that are very close to this one
            if (distanceToCell < membraneRadius) {
              this.resources.forEach((otherResource, otherId) => {
                // Skip if it's the one we already found
                if (otherId === id) return;
                
                // Calculate distance between resources
                const resourceToResourceDistance = Phaser.Math.Distance.Between(
                  resource.x, resource.y,
                  otherResource.x, otherResource.y
                );
                
                // If the other resource is very close and under the cell, add it too
                if (resourceToResourceDistance < 5) {
                  const otherDistanceToCell = Phaser.Math.Distance.Between(
                    this.playerCell.x, this.playerCell.y,
                    otherResource.x, otherResource.y
                  );
                  
                  if (otherDistanceToCell < membraneRadius && !resourcesToCollect.includes(otherId)) {
                    resourcesToCollect.push(otherId);
                  }
                }
              });
            }
          }
        }
      });
      
      // Collect the resources
      if (resourcesToCollect.length > 0) {
        log.info(() => `Collecting ${resourcesToCollect.length} resources at click position`);
        
        resourcesToCollect.forEach((id, index) => {
          // Small delay between each resource collection request
          setTimeout(() => {
            this.networkService.collectResource(id);
          }, index * 20);
        });
      }

      // If no resource was clicked, move the cell.
      // TODO: we have to investigate why the input listeners / handlers are not doing this
      // check later what's inside     this.setupInput(); and if we can make left click work the regular way
      // this is a temporary fix
      if (!resourceClicked) {
        // Check if click is on a UI element using hit testing
        let isClickOnUI = false;
        const hitObjects = this.input.hitTestPointer(pointer);
        if (hitObjects.length > 0) {
          isClickOnUI = hitObjects.some(obj => this.isUIObject(obj));
        }
        
        // Only move if not clicking on UI
        if (!isClickOnUI) {
          this.playerCell.setTargetPosition(worldPoint.x, worldPoint.y);
        }
      }

    });
    
    // Right-click handler for toxins
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      // Only process if we're fully initialized and it's a right click
      if (!this.readyForInput || !pointer.rightButtonDown()) return;
      
      // Convert to world coordinates
      const worldPoint = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
      
      // Check if click is on the player's cell (to prevent shooting when breaching)
      const cellBounds = this.playerCell.getBounds();
      const isClickOnCell = cellBounds.contains(worldPoint.x, worldPoint.y);
      
      // Check if click is on a UI element using hit testing
      // This is more precise than coordinate-based checks
      let isClickOnUI = false;
      const hitObjects = this.input.hitTestPointer(pointer);
      if (hitObjects.length > 0) {
        isClickOnUI = hitObjects.some(obj => this.isUIObject(obj));
      }
      
      // Only shoot toxin if NOT clicking on the player's cell or UI
      if (!isClickOnCell && !isClickOnUI) {
        // Debug log to help diagnose future issues
        log.info(() => `Shooting toxin at (${worldPoint.x}, ${worldPoint.y}) from cell at (${this.playerCell.x}, ${this.playerCell.y})`);
        this.shootToxin(worldPoint.x, worldPoint.y);
      }
    });
    
    console.log("Input handlers set up successfully");
  }

  /**
   * Process the room state received from the server
   */
  private processRoomState(data: RoomStateEvent): void {
    console.log(`Processing room state with ${data.players.length} players and ${data.resources.length} resources`);
    
    // Find our player in the state
    const ourPlayerID = this.networkService.getSocketId();
    const ourPlayer = data.players.find(p => p.id === ourPlayerID);
    
    // If we're part of the room state, update our position/state
    if (ourPlayer) {
      console.log(`Found our player in room state at position (${ourPlayer.x}, ${ourPlayer.y})`);
      
      // Only update position if it's significantly different
      const distanceToServer = Phaser.Math.Distance.Between(
        this.playerCell.x, this.playerCell.y,
        ourPlayer.x, ourPlayer.y
      );
      
      if (distanceToServer > 50) {
        const reconciled = this.localInputPredictor.reconcile(
          { x: this.playerCell.x, y: this.playerCell.y },
          { x: ourPlayer.x, y: ourPlayer.y }
        );
        this.playerCell.setPosition(reconciled.x, reconciled.y);
        console.log(`Reconciled local position toward server: (${ourPlayer.x}, ${ourPlayer.y})`);
      }
      
      // Update our resources
      this.updateResourceCounts(ourPlayer.resources);
      
      // Apply any organelles we have on the server
      this.updateOrganelles(ourPlayer.organelles);
      
      // Update membrane stage if provided
      if (ourPlayer.membraneStage) {
        // Find the stage configuration
        const stageConfig = MEMBRANE_EXPANSION_STAGES.find((s) => s.stage === ourPlayer.membraneStage);
        if (stageConfig) {
          // Apply correct membrane stage
          this.playerCell.setMembraneStage(
            ourPlayer.membraneStage,
            stageConfig.sizeMultiplier, 
            stageConfig.speedMultiplier
          );
        }
      }
    }
    
    // Create remote cells for other players
    data.players.forEach(playerState => {
      // Skip our own player
      if (playerState.id === ourPlayerID) return;
      
      // Check if we already have this player
      if (!this.remoteCells.has(playerState.id)) {
        // Create new remote cell
        this.addRemoteCell(playerState);
      } else {
        // Update existing remote cell
        const cell = this.remoteCells.get(playerState.id)!;
        cell.setPosition(playerState.x, playerState.y);
        cell.setRotation(playerState.rotation);
        
        // Update name text content without changing its position
        const nameText = this.remoteNameTexts.get(playerState.id);
        if (nameText) {
          nameText.setText(playerState.name);
          // Don't set position directly here - it will be updated in updateRemoteCells
        }
        
        // Update health bar content without changing its position
        const healthBar = this.remoteHealthBars.get(playerState.id);
        if (healthBar) {
          this.updateHealthBar(healthBar, playerState.health);
          // Don't set position directly here - it will be updated in updateRemoteCells
        }
        
        // Update organelles
        this.updateRemoteCellOrganelles(cell, playerState.organelles);
        
        // Update membrane stage if provided
        if (playerState.membraneStage) {
          // Find the stage configuration
          const stageConfig = MEMBRANE_EXPANSION_STAGES.find((s) => s.stage === playerState.membraneStage);
          if (stageConfig) {
            // Apply correct membrane stage
            cell.setMembraneStage(
              playerState.membraneStage,
              stageConfig.sizeMultiplier, 
              stageConfig.speedMultiplier
            );
          }
        }
      }
    });
    
    // Handle removed players
    const currentPlayerIDs = new Set(data.players.map(p => p.id));
    Array.from(this.remoteCells.keys()).forEach(id => {
      if (!currentPlayerIDs.has(id)) {
        // Player no longer in room - remove them
        console.log(`Player ${id} is no longer in room state, removing remote cell`);
        const remoteCell = this.remoteCells.get(id);
        if (remoteCell) {
          remoteCell.destroy();
          this.remoteCells.delete(id);
          
          // Also clean up name and health bar
          const nameText = this.remoteNameTexts.get(id);
          const healthBar = this.remoteHealthBars.get(id);
          
          if (nameText) {
            nameText.destroy();
            this.remoteNameTexts.delete(id);
          }
          
          if (healthBar) {
            healthBar.destroy();
            this.remoteHealthBars.delete(id);
          }
        }
      }
    });
    
    // Add all resources from room state
    data.resources.forEach(resourceState => {
      // Only add if we don't already have it
      if (!this.resources.has(resourceState.id)) {
        this.addResource(resourceState);
      }
    });
    
    // Make existing resources that aren't in the room state disappear
    const currentResourceIDs = new Set(data.resources.map(r => r.id));
    Array.from(this.resources.keys()).forEach(id => {
      if (!currentResourceIDs.has(id)) {
        // Resource no longer in room - remove it with animation
        const resource = this.resources.get(id);
        if (resource) {
          this.tweens.add({
            targets: resource,
            alpha: 0,
            scale: 0.1,
            duration: 200,
            onComplete: () => {
              resource.destroy();
              this.resources.delete(id);
            }
          });
        }
      }
    });
    
    // Show welcome modal after processing the initial room state
    this.time.delayedCall(1000, () => {
      if (this.domUI && !this.welcomeModalShown) {
        this.showWelcomeModal();
      }
    });
  }

  /**
   * Process any events that were received before we were ready
   */
  private processPendingEvents(): void {
    console.log("Processing pending events from server");
    
    // Process room state first if it exists
    if (this.pendingEvents.roomState) {
      console.log("Processing pending room state");
      this.processRoomState(this.pendingEvents.roomState);
      this.pendingEvents.roomState = null;
    }
    
    // Process player joins
    if (this.pendingEvents.playerJoins.length > 0) {
      console.log(`Processing ${this.pendingEvents.playerJoins.length} pending player join events`);
      this.pendingEvents.playerJoins.forEach(data => this.handlePlayerJoined(data));
      this.pendingEvents.playerJoins = [];
    }
    
    // Process player leaves
    if (this.pendingEvents.playerLeaves.length > 0) {
      console.log(`Processing ${this.pendingEvents.playerLeaves.length} pending player leave events`);
      this.pendingEvents.playerLeaves.forEach(playerId => {
        const remoteCell = this.remoteCells.get(playerId);
        if (remoteCell) {
          remoteCell.destroy();
          this.remoteCells.delete(playerId);
          this.remoteInterpolationBuffer.clear(playerId);
          
          // Clean up name and health bar
          const nameText = this.remoteNameTexts.get(playerId);
          const healthBar = this.remoteHealthBars.get(playerId);
          
          if (nameText) {
            nameText.destroy();
            this.remoteNameTexts.delete(playerId);
          }
          
          if (healthBar) {
            healthBar.destroy();
            this.remoteHealthBars.delete(playerId);
          }
        }
      });
      this.pendingEvents.playerLeaves = [];
    }
    
    // Process player moves
    if (this.pendingEvents.playerMoves.length > 0) {
      console.log(`Processing ${this.pendingEvents.playerMoves.length} pending player move events`);
      // For player moves, only process the most recent position for each player
      const latestMoves = new Map<string, PlayerMovedEvent>();
      this.pendingEvents.playerMoves.forEach(data => {
        latestMoves.set(data.id, data);
      });
      
      // Queue latest snapshots for interpolation
      latestMoves.forEach((data) => {
        this.queueRemoteMovement(data);
      });
      
      this.pendingEvents.playerMoves = [];
    }
    
    // Process resource spawns
    if (this.pendingEvents.resources.length > 0) {
      console.log(`Processing ${this.pendingEvents.resources.length} pending resource spawn events`);
      this.pendingEvents.resources.forEach(data => {
        if (!this.resources.has(data.id)) {
          this.addResource(data);
        }
      });
      this.pendingEvents.resources = [];
    }
    
    // Process toxin shots
    if (this.pendingEvents.toxins.length > 0) {
      console.log(`Processing ${this.pendingEvents.toxins.length} pending toxin shot events`);
      this.pendingEvents.toxins.forEach(data => {
        this.createToxin(data);
      });
      this.pendingEvents.toxins = [];
    }
    
    // Process resource collected events
    if (this.pendingEvents.resourceCollected.length > 0) {
      console.log(`Processing ${this.pendingEvents.resourceCollected.length} pending resource collected events`);
      this.pendingEvents.resourceCollected.forEach(data => {
        const resource = this.resources.get(data.id);
        if (resource) {
          // Play collection animation
          this.tweens.add({
            targets: resource,
            alpha: 0,
            scale: 0.1,
            duration: 200,
            onComplete: () => {
              resource.destroy();
              this.resources.delete(data.id);
            }
          });
        }
      });
      this.pendingEvents.resourceCollected = [];
    }
    
    // Process cell damaged events
    if (this.pendingEvents.cellDamaged.length > 0) {
      console.log(`Processing ${this.pendingEvents.cellDamaged.length} pending cell damaged events`);
      this.pendingEvents.cellDamaged.forEach(data => {
        if (data.playerId === this.networkService.getSocketId()) {
          // Local player damaged
          this.playerCell.setHealth(data.health);
          this.showDamageEffect(this.playerCell, data.position.angle, data.amount);
          
          // Update health UI
          if (this.playerHealthBar) {
            this.updateHealthBar(this.playerHealthBar, data.health);
          }
        } else {
          // Remote player damaged
          const cell = this.remoteCells.get(data.playerId);
          if (cell) {
            (cell as any).health = data.health;
            this.showDamageEffect(cell, data.position.angle, data.amount);
            
            // Update remote player's health bar
            const healthBar = this.remoteHealthBars.get(data.playerId);
            if (healthBar) {
              this.updateHealthBar(healthBar, data.health);
            }
          }
        }
      });
      this.pendingEvents.cellDamaged = [];
    }
    
    // Process player died events
    if (this.pendingEvents.playerDied.length > 0) {
      console.log(`Processing ${this.pendingEvents.playerDied.length} pending player died events`);
      this.pendingEvents.playerDied.forEach(playerId => {
        if (playerId === this.networkService.getSocketId()) {
          // Local player died
          console.log('You died! Waiting for respawn...');
          
          // Create death effect
          this.createDeathEffect(this.playerCell);
          
          // Hide player cell (but don't destroy it)
          this.playerCell.setVisible(false);
          
          // Disable player controls
          this.playerControlsEnabled = false;
          
          // Show death message in UI
          this.gameUI.showNotification('You died! Respawning in 8 seconds...', 8000);
        } else {
          // Remote player died
          const remoteCell = this.remoteCells.get(playerId);
          if (remoteCell) {
            // Create death effect
            this.createDeathEffect(remoteCell);
            
            // Hide remote cell (but don't destroy it)
            remoteCell.setVisible(false);
            
            // Hide name and health bar
            const nameText = this.remoteNameTexts.get(playerId);
            const healthBar = this.remoteHealthBars.get(playerId);
            
            if (nameText) nameText.setVisible(false);
            if (healthBar) healthBar.setVisible(false);
          }
        }
      });
      this.pendingEvents.playerDied = [];
    }
    
    // Process player respawned events
    if (this.pendingEvents.playerRespawned.length > 0) {
      console.log(`Processing ${this.pendingEvents.playerRespawned.length} pending player respawned events`);
      this.pendingEvents.playerRespawned.forEach(data => {
        if (data.playerId === this.networkService.getSocketId()) {
          // Local player respawned
          console.log('You have respawned!');
          
          // Update position
          this.playerCell.setPosition(data.x, data.y);
          this.playerCell.setVisible(true);
          this.playerCell.setHealth(data.health);
          
          // Re-enable player controls
          this.playerControlsEnabled = true;
          
          // Update camera
          this.cameras.main.centerOn(data.x, data.y);
          
          // Create spawn effect at new position
          this.createPlayerSpawnEffect(data.x, data.y);
          
          // Show respawn message
          this.gameUI.showNotification('You have respawned', 2000);
        } else {
          // Remote player respawned
          const remoteCell = this.remoteCells.get(data.playerId);
          if (remoteCell) {
            remoteCell.setPosition(data.x, data.y);
            remoteCell.setVisible(true);
            remoteCell.setHealth(data.health);
            
            // Create spawn effect at new position
            this.createPlayerSpawnEffect(data.x, data.y);
            
            // Show name and health bar
            const nameText = this.remoteNameTexts.get(data.playerId);
            const healthBar = this.remoteHealthBars.get(data.playerId);
            
            if (nameText) nameText.setVisible(true);
            if (healthBar) {
              this.updateHealthBar(healthBar, data.health);
              healthBar.setVisible(true);
            }
          }
        }
      });
      this.pendingEvents.playerRespawned = [];
    }
    
    // Process organelle built events
    if (this.pendingEvents.organelleBuilt.length > 0) {
      console.log(`Processing ${this.pendingEvents.organelleBuilt.length} pending organelle built events`);
      this.pendingEvents.organelleBuilt.forEach(data => {
        if (data.playerId === this.networkService.getSocketId()) {
          // Update resources
          this.updateResourceCounts(data.resources, true); // Force update
          
          // Update the player cell's organelles
          this.updateOrganelles(data.organelles);
          
          // Ensure the cell has the updated values
          this.playerCell.setATP(this.atp);
          this.playerCell.setGlucose(this.glucose);
        } else {
          // Update remote cell's organelles
          const cell = this.remoteCells.get(data.playerId);
          if (cell) {
            this.updateRemoteCellOrganelles(cell, data.organelles);
          }
        }
      });
      this.pendingEvents.organelleBuilt = [];
    }
    
    // Process membrane expanded events
    if (this.pendingEvents.membraneExpanded.length > 0) {
      console.log(`Processing ${this.pendingEvents.membraneExpanded.length} pending membrane expanded events`);
      this.pendingEvents.membraneExpanded.forEach(data => {
        if (data.playerId === this.networkService.getSocketId()) {
          // Update our local cell membrane
          this.handleLocalMembraneExpansion(data.stage);
          
          // Update resources
          this.updateResourceCounts(data.resources);
        } else {
          // Find the remote cell
          const remoteCell = this.remoteCells.get(data.playerId);
          if (remoteCell) {
            // Get the stage configuration
            const stageConfig = MEMBRANE_EXPANSION_STAGES.find((s) => s.stage === data.stage);
            if (stageConfig) {
              // Apply expansion to remote cell
              remoteCell.setMembraneStage(
                data.stage, 
                stageConfig.sizeMultiplier, 
                stageConfig.speedMultiplier
              );
            }
          }
        }
      });
      this.pendingEvents.membraneExpanded = [];
    }
    
    console.log("Finished processing all pending events");
  }
} 
